import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTint,
  faSave,
  faPaperPlane,
  faArrowRight,
  faArrowLeft,
  faTimes,
  faArchive,
  faChevronLeft,
  faChevronDown,
  faExclamationTriangle,
  faSpinner,
  faClock,
  faUserShield,
  faCheck,
  faTrash,
  faPlus,
  faCircleXmark,
  faCircleCheck,
  faCircleInfo,
  faFileContract,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import { fetchHMSEntities as fetchEntityOptions } from '../../services/hmsEntityService';
import { fuelEnquiryService } from '../../services/fuelEnquiryService';
import { dashboardService } from '../../services/dashboardService';
import CustomerPicker from './CustomerPicker';
import StageAdvanceModal from './StageAdvanceModal';
import ContractCreatorModal from '../Legal/ContractCreatorModal';
import ContractsInContextPanel from '../Legal/ContractsInContextPanel';
import KYCBlockModal from './KYCBlockModal';
import legalContractService from '../../services/legalContractService';
import RemoveProductConfirmModal from './RemoveProductConfirmModal';
import { stockService } from '../../services/stockService';
import InlineProductCreator from '../Stock/InlineProductCreator';
import RecapProductSearch from '../RecapProductSearch';
import InlineCrmCreator from '../CRM/InlineCrmCreator';
import { autofillSpecFromVariant } from '../../data/mixedFuelsSpec';
import { categoryForProductKind } from '../../data/productTaxonomy';
import { CURRENCY_OPTIONS } from '../../utils/constants';
import { isRestrictedCustomerTrader } from '../../utils/customerTraderAccess';
import { productShorthand } from '../../utils/productShorthand';
import { fetchTraderList, traderBoxNote } from '../../utils/traderList';
import {
  isLubesLine, lineQuantityUnit, perUnitLabel, unitLabel,
} from '../../utils/unitLabels';
import TraderOptions from '../shared/TraderOptions';
import { useFlashMessage } from '../FlashMessage';
import Layout from '../Layout';
import Footer from '../ui/Footer';
import BackToTopButton from '../BackToTopButton';
import HamsterFloatingButton from '../HamsterFloatingButton';
import DeliveryBackModal from '../DeliveryBackModal';
import PdfPreviewModal from '../shared/PdfPreviewModal';
import DispatchReviewSendModal from './DispatchReviewSendModal';
import VesselSearchAutocomplete from '../VesselSearchAutocomplete';
import AmountInput from '../ui/AmountInput';
import {
  AMOUNT_DECIMALS,
  PRICE_DECIMALS,
  QUANTITY_DECIMALS,
  formatFigure,
} from '../../utils/precision';
import {
  categoryAllowsOnCharge,
  categoryAllowsRatePerMt,
  categoryCountsInTotals,
  perMtPreview,
} from './perMtCost';
import {
  FloatingFormulaLine,
  FloatingFormulaProvider,
  HedgingExposureBanner,
  useFloatingFormulas,
} from './FloatingFormula';
import { DatePicker, DateRangePicker } from '../ui';
import logo from '../../assets/images/IMT_logo.png';

import './FuelEnquiriesTab.css';
import './FuelEnquiryForm.css';

import PinToDeskButton from '../mydesk/PinToDeskButton';
// =====================================================================
// Sprint 1E — Fuel Enquiry detail / edit form
//
// Mounts at:
//   /dashboard/trades/fuels/fuel-enquiries/new   (create)
//   /dashboard/trades/fuels/fuel-enquiries/:id   (edit)
//
// The form mirrors the PM enquiry template (file 15 §"Form section
// structure"): five sections in a fixed order — IMT, Customer, Ship
// Details, Credit Balances, Prod 1. Computed display rows (Contract
// Shorthand, Credit Request, Total Revenue) recompute on every keystroke.
//
// The stage-transitions table mirrored below is the single source of
// truth for which "Advance to {x}" buttons render. It MUST stay in lock-
// step with ``stage_service._build_allowed_transitions`` in
// ``backend/fuel_enquiries/services/stage_service.py``. If the backend
// table changes and this one doesn't, the user will see a button whose
// click yields a 400; that's acceptable but undesirable — keep them in
// sync. The required-fields-per-stage table (REQUIRED_FIELDS_BY_TARGET)
// mirrors ``stage_service._missing_fields_for_target``.
// =====================================================================

// Bunkers 2-screen redesign (May 2026). The legacy 6-stage machine
// collapsed to 3 active stages + 1 terminal -- see
// ``feature_planning/bunkers_flow/02_enquiries_2screen_target_state.md``
// §4 and ``backend/fuel_enquiries/services/stage_service.py``. The
// ``awaiting_credit_approval`` value below is a CREDIT_STATUS, not a
// stage -- it represents a row paused on ``draft`` while a CIR is
// pending. Surfaced in the badge row + the next-step banner.
const STAGE_LABEL = {
  draft: 'Draft',
  with_supply_trader: 'With Supply Trader',
  recapped: 'Recapped',
  lost: 'Lost',
};

const STAGE_BADGE_CLASS = {
  draft: 'fe-stage-badge fe-stage-new',
  with_supply_trader: 'fe-stage-badge fe-stage-supply',
  recapped: 'fe-stage-badge fe-stage-sent',
  lost: 'fe-stage-badge fe-stage-lost',
};

// The moves this person may make come from the server, worked out by the
// rule it refuses with (``stage_service.allowed_next_stages``, ledger row 139).
// The page keeps no table of its own: the copy it had offered customer traders
// a recap the server refuses. A deal without the list offers no move, loudly.
export const movesFromServer = (enquiry) => {
  if (!enquiry) return [];
  if (!Array.isArray(enquiry.allowed_next_stages)) {
    console.error('The enquiry came without allowed_next_stages; no stage move is offered.');
    return [];
  }
  return enquiry.allowed_next_stages;
};

const ROLLBACK_MAP = {
  with_supply_trader: 'draft',
};

// Human-readable labels for ``FuelEnquiry.CREDIT_STATUS_CHOICES``
// (backend ``fuel_enquiries/models.py``). The raw enum values (``n_a``,
// ``awaiting_credit_approval``...) leaked into the badge before, which
// looked like a developer string ("Credit: n_a") to traders.
const CREDIT_STATUS_LABEL = {
  pending: 'Pending',
  awaiting_credit_approval: 'Awaiting Credit Approval',
  approved: 'Approved',
  denied: 'Denied',
  n_a: 'No Credit Request Needed',
};

const TERMINAL_STAGES = new Set(['recapped', 'lost']);

// Required fields per target stage -- mirrors
// ``stage_service._missing_fields_for_target``. Keys are field names on
// the form state; ``__customer_or_unlinked`` is a synthetic key that the
// validator interprets as "either ``customer`` or ``unlinked_customer_name``".
//
// Screen 1 (``draft -> with_supply_trader``) requires the full Section
// 1/2/3 set + Product. Ship Details (vessel + port + delivery window)
// are intentionally NOT gated -- traders need to be able to workflow
// with TBC vessel/port and fill them in later. Supplier-side fields
// are NOT gated here either -- they live on Screen 2.
// Screen 2 (``with_supply_trader -> recapped``) gates on the supplier
// block.
// Map raw field names from ``missingFieldsForTarget`` to the
// human-readable form section they live in. Used by the
// ``Save & Workflow`` / ``Advance`` tooltips so the trader sees
// "Missing in: IMT, Product" instead of a 17-item snake_case
// dump. Every field in this map is also rendered with a red ``*`` on
// its ``<Field>`` component, so the section name is enough to find it.
const FIELD_TO_SECTION = {
  'customer or unlinked_customer_name': 'Customer',
  imt_branch: 'IMT',
  customer_trader: 'IMT',
  supplier_trader: 'IMT',
  hms_sell_company: 'IMT',
  fuel_type: 'Product',
  // A lubes line reports its missing product the same way a bunkers line
  // reports a missing fuel type -- both belong to the Product section.
  stock_item_variant: 'Product',
  fuel_grade: 'Product',
  sulfur_limit: 'Product',
  iso_8217_year: 'Product',
  min_tonnage: 'Product',
  max_tonnage: 'Product',
  // A lubes line's one quantity and the unit it is counted in.
  quantity: 'Product',
  measuring_unit: 'Product',
  sell_price: 'Product',
  pricing_type: 'Product',
  supplier: 'Supplier Pricing',
  buy_price: 'Supplier Pricing',
  supplier_credit_days: 'Supplier Pricing',
  supplier_pricing_type: 'Supplier Pricing',
};

// What the trader reads on the banner for each raw gate field name. A
// greyed-out button whose only explanation is a hover tooltip reads as
// broken; the banner names the field where everyone already looks.
const FIELD_LABEL = {
  'customer or unlinked_customer_name': 'Customer',
  imt_branch: 'IMT Branch',
  customer_trader: 'Customer Trader',
  supplier_trader: 'Supplier Trader',
  hms_sell_company: 'IMT Sell Company',
  fuel_type: 'Fuel Type',
  stock_item_variant: 'Product',
  fuel_grade: 'Fuel Grade',
  sulfur_limit: 'Sulfur Limit',
  iso_8217_year: 'ISO 8217 Year',
  min_tonnage: 'MIN Tonnage',
  max_tonnage: 'MAX Tonnage',
  quantity: 'Quantity',
  measuring_unit: 'Measuring Unit',
  sell_price: 'Sell Price',
  pricing_type: 'Pricing Type',
  supplier: 'Supplier',
  buy_price: 'Buy Price',
  supplier_credit_days: 'Supplier Credit Days',
  supplier_pricing_type: 'Supplier Pricing Type',
};

// Section label -> stepper anchor id (``fef-anchor-<id>``).
const SECTION_ANCHOR_ID = {
  Customer: 'customer',
  // IMT's section is titled 'IMT' (FIELD_TO_SECTION); its anchor id stays 'hms'.
  IMT: 'hms',
  Product: 'product',
  'Supplier Pricing': 'supplier',
};

export const describeMissingFields = (missing) => missing.map((raw) => {
  const stripped = raw.replace(/^products\[\d+\]\./, '');
  const section = FIELD_TO_SECTION[stripped] || FIELD_TO_SECTION[raw] || null;
  return {
    key: raw,
    label: FIELD_LABEL[stripped] || stripped,
    anchorId: section ? SECTION_ANCHOR_ID[section] : null,
  };
});

// Collapse the field-level missing list into a deduped, ordered
// section list. ``products[0].fuel_type`` etc. (the multi-product
// path) are mapped onto the same Product bucket.
const summarizeMissingSections = (missing) => {
  const sectionOrder = [
    'Customer', 'IMT', 'Product',
    'Supplier Pricing',
  ];
  const seen = new Set();
  missing.forEach((raw) => {
    const stripped = raw.replace(/^products\[\d+\]\./, '');
    const section = FIELD_TO_SECTION[stripped] || FIELD_TO_SECTION[raw];
    if (section) seen.add(section);
  });
  return sectionOrder.filter((s) => seen.has(s));
};

// The product half of the required set depends on WHAT the line sells. A bunkers
// line needs its marine-fuel spec; a lubes line needs the catalogue product it
// points at, and has no sulfur limit or ISO year to give. Mirrors
// ``stage_service._required_for`` -- both gates must agree or the trader is
// blocked by one while the other says the form is complete.
const BUNKERS_SPEC_FIELDS = ['fuel_type', 'fuel_grade', 'sulfur_limit', 'iso_8217_year'];
const LUBES_SPEC_FIELDS = ['stock_item_variant'];

export const productSpecFieldsFor = (productKind) => (
  productKind === 'lubes' ? LUBES_SPEC_FIELDS : BUNKERS_SPEC_FIELDS
);

// How much, and counted in what -- mirrors ``stage_service._required_for``. A
// bunkers line gives a MIN-MAX range in metric tonnes. A lubes line gives ONE
// quantity (kept in ``max_tonnage``) counted in its own measuring unit, so the
// unit is required and there is no MIN to ask for. Pairs are
// [form field, the name the banner reports it under].
export const productQuantityFieldsFor = (productKind) => (
  productKind === 'lubes'
    ? [['measuring_unit', 'measuring_unit'], ['max_tonnage', 'quantity']]
    : [['min_tonnage', 'min_tonnage'], ['max_tonnage', 'max_tonnage']]
);

export const REQUIRED_FIELDS_BY_TARGET = {
  with_supply_trader: [
    '__customer_or_unlinked',
    'imt_branch',
    'customer_trader',
    'supplier_trader',
    'hms_sell_company',
    '__product_spec',
    '__product_quantity',
    'sell_price',
    // Required from 2026-09-09. The SELL side, so the customer trader owns it
    // and knows it from the start. Its buy-side twin waits for Screen 2.
    'pricing_type',
    // Ship Details (vessel_name, vessel_type, port,
    // expected_delivery_start, expected_delivery_end) intentionally
    // omitted -- the entire Ship Details section is optional for
    // Save & Workflow. Backend mirrors this in
    // ``stage_service._missing_fields_for_target``.
  ],
  recapped: [
    'supplier',
    'buy_price',
    'supplier_credit_days',
    // Asked for at the moment the supply trader actually knows it. Requiring
    // it on Screen 1 would block a customer trader on a question about a
    // supplier nobody has chosen yet.
    'supplier_pricing_type',
  ],
  lost: [],
};

// =====================================================================
// Helpers
// =====================================================================

const todayISO = () => new Date().toISOString().slice(0, 10);

// Amount fields (tonnage / prices / costs) render through <AmountInput>
// (components/ui/AmountInput.js): a guarded text input that owns its
// display formatting — thousands separators, no phantom decimals — and
// emits canonical dot-decimal strings straight into form state. This also
// retired the old preventNumberWheelChange guard: text inputs don't spin
// on scroll-wheel like type="number" did.

const blankFormState = () => ({
  display_name: '',
  enquiry_date: todayISO(),
  monday_item_id: '',
  customer: null,
  unlinked_customer_name: '',
  unlinked_customer_email: '',
  unlinked_customer_contact_name: '',
  imt_branch: null,
  customer_trader: null,
  supplier_trader: null,
  hms_sell_company: null,
  hms_buy_company: null,
  vessel_name: '',
  vessel_type: null,
  imo_number: '',
  port: null,
  country: '',
  expected_delivery_start: '',
  expected_delivery_end: '',
  // Appointed shipping/port agent (free text). Mirrors the "Agent" row on
  // the Customer Confirmation. Optional, like the rest of Ship Details.
  // Kept as the unlinked fallback for agents with no CRM profile.
  shipping_agent: '',
  // Optional CRM link for the agent (July 2026). ``shipping_agent_profile``
  // is the FK id; ``shipping_agent_profile_name`` caches the display name so
  // the picker chip renders on load without a CRM list fetch (mirrors the
  // supplier pattern).
  shipping_agent_profile: null,
  shipping_agent_profile_name: '',
  // Bunkers | Lubes (2026-09). Which questions the Product card asks: a marine
  // fuel spec, or a branded product off the Lubrication catalogue. Per product
  // line, so a stem can carry fuel and lube together. Defaults to bunkers, so
  // every existing enquiry opens and behaves exactly as before.
  product_kind: 'bunkers',
  fuel_type: null,
  fuel_grade: null,
  sulfur_limit: null,
  sulfur_limit_other: '',
  iso_8217_year: null,
  measuring_unit: null,
  // Bunkers Phase 1 (May 2026): Product 1 Fixed/Floating pricing toggle.
  pricing_type: '',
  min_tonnage: '',
  max_tonnage: '',
  sell_price: '',
  // Commissions rework (June 2026): Product 1 sell-side commission. The
  // Counter Party is the CRM partner the commission is owed to; the amount
  // is a USD-per-MT rate. On save the backend mirrors the pair into a
  // locked "Commission Sales" row under 3rd Party Costs (Screen 2).
  // Replaces the free-text ``commissions`` field on the form.
  counter_party: null,
  counter_party_display_name: '',
  commission_amount: '',
  supplier: null,
  // Mirror of the customer pattern: store the saved profile's display
  // name alongside the FK so the picker chip can render on enquiry
  // load without bulk-fetching the CRM list. Updated by the picker
  // when a fresh selection is made.
  supplier_display_name: '',
  buy_price: '',
  supplier_credit_days: '',
  // Bunkers Phase 1 (May 2026): Product 1 buy-side Fixed/Floating toggle.
  // Buy-side commissions (June 2026): recorded as manual "Commission
  // Purchase" rows under 3rd Party Costs — no inline field anymore.
  supplier_pricing_type: '',
  // Per-section comments (Products / Shipment / General). Each block has a
  // free-text field plus two independent show-on ticks (Customer
  // Confirmation / Supplier Nomination). ``comments`` +
  // ``show_comments_on_supplier_nomination`` are the legacy General fields.
  comments: '',
  show_comments_on_supplier_nomination: false,
  show_comments_on_customer_confirmation: false,
  products_comments: '',
  show_products_comments_on_supplier_nomination: false,
  show_products_comments_on_customer_confirmation: false,
  shipment_comments: '',
  show_shipment_comments_on_supplier_nomination: false,
  show_shipment_comments_on_customer_confirmation: false,
  // Sprint 7A: Stock catalogue link for Product 1. ``stock_item_variant``
  // is the FK PK; ``stock_item_variant_display`` is the cached display
  // string the picker renders on form re-open (avoids a second API
  // call to look up the variant's display_name).
  stock_item_variant: null,
  stock_item_variant_display: '',
  // Sprint 6: extra product cards beyond the first. The first product
  // continues to use the inline scalar fields above (back-compat with
  // every existing single-product enquiry); rows here are the 2nd, 3rd
  // ... product on a multi-product fuels enquiry. Each row mirrors the
  // FuelEnquiryProduct shape: fuel spec + tonnage + sell price +
  // supplier + buy price + supplier credit days.
  extra_products: [],
});

const blankExtraProduct = () => ({
  // ``id`` is the FuelEnquiryProduct PK; null on freshly added rows.
  id: null,
  // Bunkers | Lubes (2026-09). Which questions the Product card asks: a marine
  // fuel spec, or a branded product off the Lubrication catalogue. Per product
  // line, so a stem can carry fuel and lube together. Defaults to bunkers, so
  // every existing enquiry opens and behaves exactly as before.
  product_kind: 'bunkers',
  fuel_type: null,
  fuel_grade: null,
  sulfur_limit: null,
  sulfur_limit_other: '',
  iso_8217_year: null,
  measuring_unit: null,
  // Bunkers Phase 1 (May 2026): per-product Fixed/Floating pricing toggle.
  pricing_type: '',
  min_tonnage: '',
  max_tonnage: '',
  sell_price: '',
  // Commissions rework (June 2026): per-product sell-side commission
  // (Counter Party CRM partner + USD/MT rate). Same semantics as the
  // Product 1 fields on ``blankFormState``.
  counter_party: null,
  counter_party_display_name: '',
  commission_amount: '',
  supplier: null,
  supplier_display_name: '',
  buy_price: '',
  supplier_credit_days: '',
  // Bunkers Phase 1 (May 2026): per-product buy-side Fixed/Floating toggle.
  supplier_pricing_type: '',
  // Sprint 7A: Stock catalogue link, same shape as Product 1.
  stock_item_variant: null,
  stock_item_variant_display: '',
});

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return '';
  return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const computeCreditRequest = (formState) => {
  // Sprint 6 multi-product: total credit exposure is the sum across
  // Product 1 (inline scalars on ``formState``) plus every extra
  // product card in ``formState.extra_products``. Rows with missing
  // / invalid sell_price or max_tonnage contribute 0. Returns ``null``
  // only when **no** row has both values populated -- so the UI keeps
  // showing the em-dash placeholder until the user fills in at least
  // one product's pricing pair. Matches ``FuelEnquiry.credit_request``
  // on the backend (which now also aggregates across children).
  let total = 0;
  let anyRow = false;
  const addRow = (sellRaw, maxRaw) => {
    const sell = parseFloat(sellRaw);
    const max = parseFloat(maxRaw);
    if (Number.isFinite(sell) && Number.isFinite(max)) {
      total += sell * max;
      anyRow = true;
    }
  };
  addRow(formState.sell_price, formState.max_tonnage);
  const extras = Array.isArray(formState.extra_products) ? formState.extra_products : [];
  for (const row of extras) {
    addRow(row.sell_price, row.max_tonnage);
  }
  return anyRow ? total : null;
};

const formatDateTime = (raw) => {
  if (!raw) return '-';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '-';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hh = date.getHours().toString().padStart(2, '0');
  const mm = date.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year} ${hh}:${mm}`;
};

const errorDetail = (err) => {
  const data = err?.response?.data;
  if (!data) return err?.message || 'Unknown error';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  // DRF field-level errors come through as { field: [msg, ...] }
  const fields = Object.entries(data)
    .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
    .join('; ');
  return fields || JSON.stringify(data);
};

// =====================================================================
// Component
// =====================================================================

const FuelEnquiryForm = () => {
  const history = useHistory();
  const location = useLocation();
  const { id: routeId } = useParams();
  const isCreateMode = !routeId || routeId === 'new';

  // -------- Bootstrap state --------
  const [bootstrapping, setBootstrapping] = useState(true);
  const [bootError, setBootError] = useState(null);
  const [accessError, setAccessError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [lookups, setLookups] = useState({
    fuelTypes: [],
    fuelGrades: [],
    sulfurLimits: [],
    iso8217Years: [],
    vesselTypes: [],
    measuringUnits: [],
    thirdPartyCostCategories: [],
  });
  const [branches, setBranches] = useState([]);
  const [hmsEntities, setHmsEntities] = useState([]);
  const [traders, setTraders] = useState([]);
  const [bunkerPorts, setBunkerPorts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // -------- Form state --------
  const [enquiry, setEnquiry] = useState(null); // detail payload from server (edit mode)
  const [formState, setFormState] = useState(blankFormState());
  const [customerProfile, setCustomerProfile] = useState(null);
  // Item #8: track the last customer id we backfilled credit for so the
  // enrichment effect below never re-fetches (even when the partner has no
  // credit set and the figures come back null).
  const creditBackfilledForRef = useRef(null);
  const [showUnlinked, setShowUnlinked] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Per-card cache of the picked StockItemVariant's catalogue snapshot
  // (unit, on-hand quantity, sale price + currency) so the picker info
  // row can render anchor numbers below each product's picker chip.
  // Keyed by ``'product1'`` or ``'extra:<idx>'``; values are
  // ``{ unit_name, quantity, sale_price, purchase_price, price_currency }``
  // or ``null`` when the picker is cleared. Populated at pick time from
  // the variant blob ``RecapProductSearch`` passes, and lazily hydrated
  // on enquiry load via ``stockService.getVariantById``.
  const [productVariantMeta, setProductVariantMeta] = useState({});

  // -------- UI / submission state --------
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Use the app-wide toast system (top-right slide-in) so save / advance
  // results are visible without scrolling. Mirrors DeliveryForm.js.
  const { showSuccess, showError } = useFlashMessage();

  const [stageModalState, setStageModalState] = useState({
    isOpen: false,
    action: 'advance',
    target: null,
    requireNotes: false,
  });
  const [stageModalSubmitting, setStageModalSubmitting] = useState(false);
  const [stageModalError, setStageModalError] = useState(null);

  // Contract Creator Modal state (bunkers_contracts Phase 3). Owns its own
  // submitting/error pair so an open Contract modal does not collide with
  // the rest of the stage-modal machinery. Files live here too so the
  // modal can stay presentation-only and the parent owns the upload calls
  // post-advance.
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractModalSubmitting, setContractModalSubmitting] = useState(false);
  const [contractModalError, setContractModalError] = useState(null);
  const [contractSaleFiles, setContractSaleFiles] = useState([]);
  const [contractPurchaseFiles, setContractPurchaseFiles] = useState([]);

  // Bunkers KYC gate (July 2026): the recap precheck opens this block modal
  // when a CRM-linked partner isn't KYC-cleared, instead of the Contract
  // Creator modal. It lists the blockers and offers "Notify KYC approver".
  const [kycBlockState, setKycBlockState] = useState({
    open: false,
    blockedPartners: [],
    canRequest: false,
    notifying: false,
    error: null,
  });

  // Time-boxed KYC gate stand-down (Aug 2026, bunkers UAT). Read on mount so
  // the screen can carry a banner: a trader must never be inside the bypass
  // window without knowing the compliance gate is currently down.
  const [kycBypass, setKycBypass] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fuelEnquiryService.kycGateBypass
      .get()
      .then((data) => {
        if (!cancelled) setKycBypass(data.active ? data.bypass : null);
      })
      .catch(() => {
        // A failed read must never imply "gate is down" -- stay silent and
        // leave the banner off. The gate itself is enforced server-side.
        if (!cancelled) setKycBypass(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Customer Confirmation PDF preview. ``url`` is an object URL created from
  // the blob the backend streams; we revoke it on close to avoid leaks.
  const [confirmation, setConfirmation] = useState({
    open: false,
    loading: false,
    url: null,
    fileName: '',
  });

  // -------- Trade-document dispatches (Phase 7) --------
  // Once an enquiry is recapped the backend mints a Customer Confirmation
  // and/or Supplier Nomination dispatch row. We surface an outstanding-sends
  // indicator listing any ``pending`` rows (with a Review & Send button) and
  // a sent confirmation once a CT has fired the email. ``activeDispatch`` is
  // the row whose Review & Send modal is open. ``dispatchsLoading`` guards the
  // banner so it doesn't flash before the first fetch resolves.
  const [dispatches, setDispatches] = useState([]);
  const [dispatchesLoading, setDispatchesLoading] = useState(false);
  const [activeDispatch, setActiveDispatch] = useState(null);
  // Id of the dispatch currently having a revision raised — disables that
  // row's button so a double-click can't mint two versions.
  const [revisingDispatchId, setRevisingDispatchId] = useState(null);
  // Dispatch id handed in via location state when the user arrives from a
  // ``customer_confirmation_required`` / ``supplier_nomination_required``
  // notification. Consumed once the dispatches load, then cleared.
  const pendingDispatchOpenId = location.state?.openDispatchId || null;

  // -------- Unsaved-changes tracking + back-confirmation modal --------
  // Mirrors DeliveryScreen's pattern: only flag the form dirty on real
  // user keystrokes (not bootstrap fills), gate the back navigation
  // through a confirmation modal, and warn on browser-level unloads.
  const userHasInteracted = useRef(false);
  const isInitialLoad = useRef(true);
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);

  // Sprint 6 multi-product. Tracks the index of the extra product the
  // user is asking to remove from a saved enquiry. Null = modal closed.
  // We only open the modal for persisted rows (those with ``id``); a
  // freshly added card removes silently because nothing was ever
  // written to the database for it.
  const [removeConfirmIdx, setRemoveConfirmIdx] = useState(null);

  // Active section for the numbered stepper; updated by IntersectionObserver.
  const [activeStep, setActiveStep] = useState('hms');

  // -------- Bootstrap on mount / id change --------

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      setBootstrapping(true);
      setBootError(null);
      setAccessError(null);
      setNotFound(false);
      try {
        const [
          userInfo,
          fuelTypes,
          fuelGrades,
          sulfurLimits,
          iso8217Years,
          vesselTypes,
          measuringUnits,
          thirdPartyCostCategories,
          branchList,
          hmsEntityOptions,
          traderList,
          portsResponse,
        ] = await Promise.all([
          dashboardService.getUserInfo(),
          fuelEnquiryService.lookups.fuelTypes(),
          fuelEnquiryService.lookups.fuelGrades(),
          fuelEnquiryService.lookups.sulfurLimits(),
          fuelEnquiryService.lookups.iso8217Years(),
          fuelEnquiryService.lookups.vesselTypes(),
          fuelEnquiryService.lookups.measuringUnits(),
          fuelEnquiryService.lookups.thirdPartyCostCategories(),
          fuelEnquiryService.branches.list(),
          // Corporate-structure gated list ({value: hms_entity_id, label});
          // the raw /api/offer/hms-entities/ endpoint still returns legacy
          // HMS Bergbau rows.
          fetchEntityOptions(),
          // Every page: this used to ask for one page of 100, so a trader past
          // the 100th could never be picked in either box (ledger row 133).
          fetchTraderList(),
          axiosInstance.get('/api/offer/ports/', {
            params: { bunker_only: 'true' },
          }),
        ]);
        if (cancelled) return;

        setCurrentUser(userInfo);
        setLookups({
          fuelTypes: Array.isArray(fuelTypes) ? fuelTypes : [],
          fuelGrades: Array.isArray(fuelGrades) ? fuelGrades : [],
          sulfurLimits: Array.isArray(sulfurLimits) ? sulfurLimits : [],
          iso8217Years: Array.isArray(iso8217Years) ? iso8217Years : [],
          vesselTypes: Array.isArray(vesselTypes) ? vesselTypes : [],
          measuringUnits: Array.isArray(measuringUnits) ? measuringUnits : [],
          thirdPartyCostCategories: Array.isArray(thirdPartyCostCategories)
            ? thirdPartyCostCategories : [],
        });
        setBranches(Array.isArray(branchList) ? branchList : []);
        setHmsEntities(hmsEntityOptions.map((o) => ({
          id: o.value,
          hms_entity_name: o.label,
        })));
        setTraders(traderList.rows);
        const portsList = Array.isArray(portsResponse.data)
          ? portsResponse.data
          : (portsResponse.data?.results || []);
        setBunkerPorts(portsList);

        if (!isCreateMode) {
          const detail = await fuelEnquiryService.retrieve(routeId);
          if (cancelled) return;
          setEnquiry(detail);
          setFormState(detailToFormState(detail));
          setShowUnlinked(!detail.customer && !!detail.unlinked_customer_name);
          if (detail.customer) {
            // Fetch the linked CRMProfile so the mirrors render even on a
            // hard-refresh into edit mode (the FuelEnquiryDetailSerializer
            // already exposes the mirrors directly, so we use those when
            // available and only fall back to a CRM fetch if needed).
            setCustomerProfile({
              id: detail.customer,
              legal_entity_name: detail.customer_name,
              monday_account_number: detail.customer_account_number,
              partner_credit_days: detail.customer_credit_days,
              credit_limit: detail.customer_credit_limit,
              credit_balance: detail.customer_credit_balance,
            });
          }
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setNotFound(true);
        } else if (status === 403) {
          setAccessError(
            err?.response?.data?.detail
            || "You don't have access to this enquiry.",
          );
        } else {
          console.error('FuelEnquiryForm bootstrap failed:', err);
          setBootError(errorDetail(err));
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    };
    bootstrap();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Item #8: backfill the picked customer's Credit Limit / Balance / Days on
  // the create screen. A Bunkers-only trader (no HasCRMAccess) 403s on the
  // CRM detail fetch inside CustomerPicker and falls back to the quick-search
  // row, which omits credit fields — so the mirrors render "—" until save.
  // When those fields are missing we pull them from the enquiry-permission-
  // gated summary endpoint and merge them into ``customerProfile`` so both the
  // Account/Credit-Days mirror and the Credit Balance section fill in pre-save.
  // The full CRM blob (full-access users) already carries the fields, so this
  // adds no request for them.
  useEffect(() => {
    const id = customerProfile?.id;
    if (!id) return;
    const hasCredit = customerProfile.credit_limit != null
      || customerProfile.credit_balance != null
      || customerProfile.partner_credit_days != null;
    if (hasCredit) return;
    if (creditBackfilledForRef.current === id) return;
    creditBackfilledForRef.current = id;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(
          `/api/fuel-enquiries/partner-credit-summary/${id}/`,
        );
        if (cancelled) return;
        setCustomerProfile((prev) => {
          if (!prev || prev.id !== id) return prev;
          return {
            ...prev,
            credit_limit: data.credit_limit,
            credit_balance: data.credit_balance,
            partner_credit_days: data.credit_days,
          };
        });
      } catch (err) {
        console.warn('Partner credit summary backfill failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [customerProfile]);


  // -------- Derived computed display values --------

  const contractShorthand = useMemo(
    () => productShorthand(lookups, formState),
    [lookups, formState],
  );
  const creditRequest = useMemo(
    () => computeCreditRequest(formState),
    [formState],
  );

  // -------- Field handlers --------

  // Mark as dirty only when the value actually changes AND we're past
  // the bootstrap phase. Mirrors DeliveryScreen's interaction tracking.
  const markDirtyIfChanged = (prev, field, value) => {
    if (!isInitialLoad.current && prev[field] !== value) {
      userHasInteracted.current = true;
    }
  };

  const setField = (field, value) => {
    setFormState((prev) => {
      markDirtyIfChanged(prev, field, value);
      return { ...prev, [field]: value };
    });
  };

  // Sprint 6 multi-product. Patches one field on the ``idx``-th extra
  // product row inside ``formState.extra_products`` while keeping the
  // existing dirty-tracking / interaction semantics. Used by the
  // multi-product supplier-pricing sub-cards in ``SectionSupplierPricing``
  // so each product gets its own (supplier / buy_price / supplier_credit_days)
  // captured against the matching ``FuelEnquiryProduct`` child row.
  const setExtraField = (idx, field, value) => {
    setFormState((prev) => {
      const extras = Array.isArray(prev.extra_products) ? prev.extra_products : [];
      const next = [...extras];
      const before = next[idx] ? next[idx][field] : undefined;
      if (before !== value) {
        userHasInteracted.current = true;
      }
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, extra_products: next };
    });
  };

  // ---------------------------------------------------------------------
  // Sprint 7A: Stock-picker integration
  // ---------------------------------------------------------------------
  // The picker on a product card writes 6 fields when a variant is
  // picked: the FK + display cache (always), and four fuel-spec FKs +
  // measuring_unit auto-filled from the variant's ``spec_attributes``
  // and ``unit_id``. Auto-fill is best-effort (loose name match via
  // ``autofillSpecFromVariant``); fields the matcher couldn't resolve
  // stay at whatever the trader had typed in, so manual override
  // always wins.
  //
  // ``target`` selects which product card receives the writes:
  //   'product1'                -> Product 1 (parent inline scalars)
  //   { type: 'extra', idx: N } -> extra_products[N]
  // Distil the catalogue snapshot the picker-info row needs out of a
  // full variant blob. Keeps the cache shape narrow and stable even if
  // the underlying serializer adds new fields. Returns ``null`` for a
  // falsy variant so callers can ``setProductVariantMeta`` symmetrically
  // on pick / clear.
  const extractVariantMeta = (variant) => {
    if (!variant) return null;
    // Prefer the live ledger balance (``on_hand_balance``) over the
    // static spec ``quantity``; the trader cares about what is
    // actually available right now when sizing a deal.
    const liveQty = variant.on_hand_balance;
    const specQty = variant.quantity;
    return {
      unit_name: variant.unit_name || variant.unit_display || '',
      quantity: liveQty != null ? liveQty : (specQty ?? null),
      quantity_is_live: liveQty != null,
      sale_price: variant.sale_price ?? null,
      purchase_price: variant.purchase_price ?? null,
      price_currency: variant.price_currency || 'USD',
    };
  };

  const metaKeyFor = (target) => {
    if (target === 'product1') return 'product1';
    if (target && target.type === 'extra') return `extra:${target.idx}`;
    return null;
  };

  const applyVariantSelection = (target, variantId, displayName, fullVariant) => {
    const writeVariant = (id, name) => {
      if (target === 'product1') {
        setField('stock_item_variant', id);
        setField('stock_item_variant_display', name);
      } else if (target && target.type === 'extra') {
        setExtraField(target.idx, 'stock_item_variant', id);
        setExtraField(target.idx, 'stock_item_variant_display', name);
      }
    };
    const writeSpec = (key, value) => {
      if (target === 'product1') {
        setField(key, value);
      } else if (target && target.type === 'extra') {
        setExtraField(target.idx, key, value);
      }
    };

    writeVariant(variantId, displayName);

    // Cache (or clear) the catalogue snapshot for the picker-info row
    // below the chip. Cleared even on partial picks so the trader never
    // sees stale stock/price numbers attached to a different variant.
    const cacheKey = metaKeyFor(target);
    if (cacheKey) {
      setProductVariantMeta((prev) => ({
        ...prev,
        [cacheKey]: extractVariantMeta(fullVariant),
      }));
    }

    // Clearing the picker (variantId == null) intentionally does NOT
    // wipe the four fuel-spec dropdowns or measuring_unit -- the trader
    // may want to re-pick a different variant without losing the spec
    // they already configured. Manual edits override the picker.
    if (variantId == null || !fullVariant) return;

    const result = autofillSpecFromVariant(fullVariant.spec_attributes, lookups);
    ['fuel_type', 'fuel_grade', 'sulfur_limit', 'iso_8217_year'].forEach((key) => {
      if (result[key] != null) writeSpec(key, result[key]);
    });
    if (fullVariant.unit_id != null) writeSpec('measuring_unit', fullVariant.unit_id);
  };

  // On enquiry hydrate, the form arrives with a ``stock_item_variant``
  // id but no cached catalogue snapshot (the variant blob lives on the
  // /stock/ endpoint, not on the enquiry payload). Lazily fetch the
  // variant the first time we notice a missing snapshot so the picker
  // info row can render after a reload too. Safe to re-run because we
  // skip any key whose snapshot is already cached.
  useEffect(() => {
    const targets = [];
    if (formState.stock_item_variant && productVariantMeta.product1 === undefined) {
      targets.push({ key: 'product1', id: formState.stock_item_variant });
    }
    (formState.extra_products || []).forEach((row, idx) => {
      const key = `extra:${idx}`;
      if (row?.stock_item_variant && productVariantMeta[key] === undefined) {
        targets.push({ key, id: row.stock_item_variant });
      }
    });
    if (targets.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const t of targets) {
        try {
          const variant = await stockService.getVariantById(t.id);
          if (cancelled) return;
          setProductVariantMeta((prev) => ({
            ...prev,
            [t.key]: extractVariantMeta(variant),
          }));
        } catch (err) {
          if (cancelled) return;
          console.warn(
            `FuelEnquiryForm: variant snapshot hydrate failed for ${t.key}`,
            err,
          );
          // Mark as null so we don't retry-loop on a missing variant.
          setProductVariantMeta((prev) => ({ ...prev, [t.key]: null }));
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.stock_item_variant, formState.extra_products]);

  // Inline-create modal orchestration. ``target`` is captured at open
  // time so the new variant lands on the card that triggered the
  // create flow even if the trader scrolled around in the modal.
  const [productCreatorState, setProductCreatorState] = useState({
    isOpen: false,
    searchTerm: '',
    target: null,
    // The product card's Bunkers/Lubes toggle, captured alongside the target.
    // ONE shared modal serves every card, so without this a Lubes line's
    // "+ Create product" filed the new product as Oil/Gas -- wrong category,
    // and then the bulk-commodity naming rule instead of brand + product name.
    productKind: 'bunkers',
  });

  // Inline CRM partner creator state for the Customer picker. Bunkers
  // customers are debtors; the modal seeds partner_type='Buyer (Debtor)'
  // and the new profile is preselected on the Customer picker via the
  // existing handleCustomerChange path. Pending stubs are surfaced in
  // the picker via ``pendingCustomerProfiles`` so the trader can pick the
  // profile they just created without waiting for CRM approval.
  // NOTE: Supplier inline-create (Section 6 SupplierPricingProductBlock)
  // is intentionally NOT wired here — its picker derives the chip from
  // a parent-passed ``suppliers`` list, so a new profile would not
  // render its chip until refresh. Adding inline-create there is a
  // follow-up that requires lifting supplier profile state.
  const [crmCreatorState, setCrmCreatorState] = useState({
    isOpen: false,
    searchTerm: '',
  });
  const [pendingCustomerProfiles, setPendingCustomerProfiles] = useState([]);

  // Which toggle the card that opened the creator is set to. Read off the
  // same row the target points at, so the answer cannot drift from the card.
  const productKindForTarget = (target) => {
    if (target && target.type === 'extra') {
      const row = (formState.extra_products || [])[target.idx];
      return (row && row.product_kind) || 'bunkers';
    }
    return formState.product_kind || 'bunkers';
  };

  const openProductCreator = (target, searchTerm) => {
    setProductCreatorState({
      isOpen: true,
      searchTerm: searchTerm || '',
      target,
      productKind: productKindForTarget(target),
    });
  };

  const closeProductCreator = () => {
    setProductCreatorState({
      isOpen: false, searchTerm: '', target: null, productKind: 'bunkers',
    });
  };

  const handleProductCreated = (variant) => {
    if (!variant || !variant.id) {
      closeProductCreator();
      return;
    }
    // ``display_name`` from the create endpoint includes the trailing
    // pricing block ("(Sale: ...)"); strip it so the picker's input
    // matches the format ``handleSelectProduct`` produces on a regular
    // pick (RecapProductSearch.js ~line 181).
    const cleanName =
      variant.display_name?.split(' (Sale:')[0]?.split(' (Purchase:')[0]
      || variant.shortname
      || 'Product';
    applyVariantSelection(productCreatorState.target, variant.id, cleanName, variant);
    closeProductCreator();
  };

  // Adapter for components that emit ``{ target: { name, value } }`` events
  // (e.g. DateRangePicker). Routes through ``setField`` so dirty-tracking
  // and react-state updates stay in lock-step.
  const handleEventInput = (e) => {
    if (e?.target?.name) setField(e.target.name, e.target.value);
  };

  const handlePortChange = (portId) => {
    const newId = portId ? Number(portId) : null;
    const matched = bunkerPorts.find((p) => p.id === newId);
    setFormState((prev) => {
      markDirtyIfChanged(prev, 'port', newId);
      const next = { ...prev, port: newId };
      // Auto-fill country when (a) it's empty, or (b) it currently mirrors
      // the previously-selected port's country (so port changes propagate).
      const priorMatched = bunkerPorts.find((p) => p.id === prev.port);
      const priorCountry = priorMatched?.country_name || '';
      if (matched && (!prev.country || prev.country === priorCountry)) {
        next.country = matched.country_name || prev.country;
      }
      return next;
    });
  };

  const handleBranchChange = (branchId) => {
    setField('imt_branch', branchId ? Number(branchId) : null);
  };

  const handleCustomerChange = (profile) => {
    // Reset the Item #8 backfill guard so re-picking (even the same partner
    // after a clear) can re-fetch the credit figures for a quick-search row.
    creditBackfilledForRef.current = null;
    setCustomerProfile(profile);
    setFormState((prev) => {
      const newCustomerId = profile ? profile.id : null;
      markDirtyIfChanged(prev, 'customer', newCustomerId);
      if (profile) {
        // Returning to a linked customer clears the unlinked text fields
        // so we don't keep stale data around.
        return {
          ...prev,
          customer: profile.id,
          unlinked_customer_name: '',
          unlinked_customer_email: '',
          unlinked_customer_contact_name: '',
        };
      }
      return {
        ...prev,
        customer: newCustomerId,
      };
    });
  };

  // Inline CRM customer creator — opened from the Customer picker's
  // "+ Create CRM partner" CTA when no match is found.
  const openCustomerCrmCreator = (searchTerm) => {
    setCrmCreatorState({ isOpen: true, searchTerm: searchTerm || '' });
  };
  const closeCustomerCrmCreator = () => {
    setCrmCreatorState({ isOpen: false, searchTerm: '' });
  };
  const handleInlineCustomerCreated = (profile) => {
    if (!profile) {
      closeCustomerCrmCreator();
      return;
    }
    setPendingCustomerProfiles((prev) => {
      if (prev.some((p) => p.id === profile.id)) return prev;
      return [...prev, profile];
    });
    // Reuse the existing select-customer path so form dirty tracking and
    // unlinked-toggle reset behave exactly as if the trader picked an
    // approved profile from the dropdown.
    handleCustomerChange(profile);
    closeCustomerCrmCreator();
  };

  const handleUnlinkedToggle = (showUnlinkedFlag) => {
    setShowUnlinked(showUnlinkedFlag);
    if (showUnlinkedFlag) {
      setCustomerProfile(null);
      setField('customer', null);
    }
  };

  // -------- Browser unload + initial-load settle --------

  // Warn on close/refresh if the form is dirty.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (userHasInteracted.current) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Once bootstrap finishes, allow setField to flag dirty edits. The 500ms
  // delay matches DeliveryScreen and lets cascading state updates settle.
  useEffect(() => {
    if (bootstrapping) return undefined;
    const t = setTimeout(() => { isInitialLoad.current = false; }, 500);
    return () => clearTimeout(t);
  }, [bootstrapping]);

  // -------- Permissions --------

  // The supply-side gate. Derived ONCE here and shared by the Supplier
  // Pricing / 3rd Party Costs sections and the stepper that links to them,
  // so the three can never disagree about who is restricted.
  //
  // Reads ``isRestrictedCustomerTrader`` rather than ``is_customer_trader``
  // directly: the raw flag is the INPUT to the rule, not the rule. Every
  // backend overlay exempts superusers, and this file used to not — which
  // hid Supplier Pricing + 3rd Party Costs from a superuser whose account
  // also carried the trader flag, even though the API had already sent her
  // that data (prod, 2026-08-18). See utils/customerTraderAccess.js.
  const isSupplySideHidden = isRestrictedCustomerTrader(currentUser);

  // Row 132: the floating-price formulas of this enquiry's lines, read once
  // per load and again after every save (each save hands back a new
  // ``enquiry``). Feeds the formula lines, the exposure banner and the gate.
  const floatingFormulas = useFloatingFormulas({
    enquiryId: isCreateMode ? null : (enquiry?.id ?? null),
    reloadToken: enquiry,
  });

  const isRowOwner = useMemo(() => {
    if (!enquiry || !currentUser) return false;
    if (currentUser.is_superuser) return true;
    if (currentUser.is_head_of_department) return true;
    const ownerIds = new Set([
      enquiry.customer_trader,
      enquiry.supplier_trader,
      enquiry.branch_manager,
    ].filter(Boolean));
    return ownerIds.has(currentUser.user_id);
  }, [enquiry, currentUser]);

  // -------- Validation --------

  // Save Draft only requires Section 1 (IMT) + Section 2 (Customer) per
  // PM (May 2026) — every other section can be filled in later. The model
  // / serializer changes that landed alongside this loosening let the
  // backend accept null Section 3-6 fields, and the stage_service still
  // gates them at "Advance to Enquiry Review".
  //
  // Section 3-6 sanity checks (date order, MIN<=MAX, positive sell price)
  // remain so that *if* the user did fill them in we surface obvious
  // typos before the round-trip.
  const validateForSave = () => {
    const errs = {};
    if (!formState.enquiry_date) {
      errs.enquiry_date = 'Required.';
    }
    if (!formState.imt_branch) {
      errs.imt_branch = 'Required.';
    }
    if (!formState.customer_trader) {
      errs.customer_trader = 'Required.';
    }
    if (!formState.hms_sell_company) {
      errs.hms_sell_company = 'Required.';
    }
    if (!formState.customer && !showUnlinked) {
      errs.customer = 'Pick a customer or add an unlinked customer.';
    }
    if (showUnlinked && !formState.unlinked_customer_name.trim()) {
      errs.unlinked_customer_name = 'Required when adding an unlinked customer.';
    }
    if (
      formState.expected_delivery_start
      && formState.expected_delivery_end
      && formState.expected_delivery_end < formState.expected_delivery_start
    ) {
      errs.expected_delivery_end = 'End date must be on or after start date.';
    }
    const minT = parseFloat(formState.min_tonnage);
    const maxT = parseFloat(formState.max_tonnage);
    // A lubes line has one quantity and no MIN box to be wrong about.
    if (
      !isLubesLine(formState)
      && Number.isFinite(minT) && Number.isFinite(maxT) && minT > maxT
    ) {
      errs.min_tonnage = 'MIN tonnage cannot exceed MAX tonnage.';
    }
    const sell = parseFloat(formState.sell_price);
    if (formState.sell_price && (!Number.isFinite(sell) || sell <= 0)) {
      errs.sell_price = 'Must be a positive number.';
    }
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const missingFieldsForTarget = (targetStage) => {
    const required = REQUIRED_FIELDS_BY_TARGET[targetStage] || [];
    const missing = [];
    for (const field of required) {
      if (field === '__customer_or_unlinked') {
        if (!formState.customer && !formState.unlinked_customer_name.trim()) {
          missing.push('customer or unlinked_customer_name');
        }
        continue;
      }
      if (field === '__product_spec') {
        // Product 1's spec fields depend on whether it is a fuel or a lube.
        // Extra product rows carry their own kind and are checked by the
        // backend gate on save; this covers the inline Product 1 scalars.
        for (const specField of productSpecFieldsFor(formState.product_kind)) {
          const specVal = formState[specField];
          if (specVal === null || specVal === undefined || specVal === '') {
            missing.push(specField);
          }
        }
        continue;
      }
      if (field === '__product_quantity') {
        for (const [qtyField, reportedAs] of productQuantityFieldsFor(formState.product_kind)) {
          const qtyVal = formState[qtyField];
          if (qtyVal === null || qtyVal === undefined || qtyVal === '') {
            missing.push(reportedAs);
          }
        }
        continue;
      }
      const val = formState[field];
      if (val === null || val === undefined || val === '') {
        missing.push(field);
      }
    }
    // Row 138: no stage move waits for a floating formula; the hedging desk
    // sets it from its task.
    return missing;
  };

  // -------- Network: save --------

  const buildPayload = () => {
    // Strip read-only and computed fields. Submit only what
    // FuelEnquiryCreateSerializer / FuelEnquiryUpdateSerializer accept.
    const payload = {
      display_name: formState.display_name,
      enquiry_date: formState.enquiry_date,
      monday_item_id: formState.monday_item_id || null,
      customer: formState.customer,
      unlinked_customer_name: formState.unlinked_customer_name,
      unlinked_customer_email: formState.unlinked_customer_email,
      unlinked_customer_contact_name: formState.unlinked_customer_contact_name,
      imt_branch: formState.imt_branch,
      customer_trader: formState.customer_trader,
      supplier_trader: formState.supplier_trader,
      hms_sell_company: formState.hms_sell_company,
      hms_buy_company: formState.hms_buy_company || null,
      vessel_name: formState.vessel_name,
      vessel_type: formState.vessel_type,
      imo_number: formState.imo_number,
      port: formState.port ?? null,
      country: formState.country,
      expected_delivery_start: formState.expected_delivery_start || null,
      expected_delivery_end: formState.expected_delivery_end || null,
      shipping_agent: formState.shipping_agent || '',
      shipping_agent_profile: formState.shipping_agent_profile ?? null,
      product_kind: formState.product_kind || 'bunkers',
      stock_item_variant: formState.stock_item_variant ?? null,
      fuel_type: formState.fuel_type,
      fuel_grade: formState.fuel_grade,
      sulfur_limit: formState.sulfur_limit,
      sulfur_limit_other: formState.sulfur_limit_other,
      iso_8217_year: formState.iso_8217_year,
      measuring_unit: formState.measuring_unit,
      pricing_type: formState.pricing_type || '',
      min_tonnage: formState.min_tonnage,
      max_tonnage: formState.max_tonnage,
      sell_price: formState.sell_price,
      counter_party: formState.counter_party,
      commission_amount: formState.commission_amount || null,
      supplier: formState.supplier,
      buy_price: formState.buy_price || null,
      supplier_credit_days: formState.supplier_credit_days || null,
      supplier_pricing_type: formState.supplier_pricing_type || '',
      comments: formState.comments || '',
      show_comments_on_supplier_nomination:
        !!formState.show_comments_on_supplier_nomination,
      show_comments_on_customer_confirmation:
        !!formState.show_comments_on_customer_confirmation,
      products_comments: formState.products_comments || '',
      show_products_comments_on_supplier_nomination:
        !!formState.show_products_comments_on_supplier_nomination,
      show_products_comments_on_customer_confirmation:
        !!formState.show_products_comments_on_customer_confirmation,
      shipment_comments: formState.shipment_comments || '',
      show_shipment_comments_on_supplier_nomination:
        !!formState.show_shipment_comments_on_supplier_nomination,
      show_shipment_comments_on_customer_confirmation:
        !!formState.show_shipment_comments_on_customer_confirmation,
    };
    // Sprint 6: when the user has added extra product cards we send a
    // full ``products`` array (first row mirrors the inline scalars,
    // followed by every extra card). When the key is omitted the backend
    // treats this as a single-product enquiry whose Product 1 lives on the
    // enquiry's own fields, and leaves product rows alone.
    //
    // Sprint 7A once also sent it whenever Product 1 had a catalogue link,
    // because that link then lived on the product rows only. The enquiry
    // has carried its own ``stock_item_variant`` since 2026-09 (sent above),
    // and HMS files one onto every fuels enquiry -- so that rule sent a
    // one-row array on every save of a single-product enquiry, and the
    // server turned it into a product row nobody added (dev ENQ-0017).
    //
    // The array now goes only when there is a second product, or when the
    // enquiry already keeps its products as rows (so those rows follow the
    // Product 1 boxes, and a removed extra card is removed there too).
    const extras = Array.isArray(formState.extra_products) ? formState.extra_products : [];
    const hasProductRows = Array.isArray(enquiry?.products) && enquiry.products.length > 0;
    if (extras.length > 0 || hasProductRows) {
      const inlineRow = {
        product_kind: formState.product_kind || 'bunkers',
        fuel_type: formState.fuel_type,
        fuel_grade: formState.fuel_grade,
        sulfur_limit: formState.sulfur_limit,
        sulfur_limit_other: formState.sulfur_limit_other || '',
        iso_8217_year: formState.iso_8217_year,
        measuring_unit: formState.measuring_unit,
        pricing_type: formState.pricing_type || '',
        min_tonnage: formState.min_tonnage || null,
        max_tonnage: formState.max_tonnage || null,
        sell_price: formState.sell_price || null,
        counter_party: formState.counter_party,
        commission_amount: formState.commission_amount || null,
        supplier: formState.supplier,
        buy_price: formState.buy_price || null,
        supplier_credit_days: formState.supplier_credit_days || null,
        supplier_pricing_type: formState.supplier_pricing_type || '',
        // Product 1's catalogue link. Also written to the parent (see
        // ``payload.stock_item_variant`` above): the parent column was added
        // in 2026-09 so a single-product LUBES enquiry -- where the picked
        // product IS the whole spec -- has somewhere to record it without
        // being forced onto the child-row path.
        stock_item_variant: formState.stock_item_variant || null,
      };
      payload.products = [inlineRow, ...extras.map((row) => {
        // Sprint 7A: drop the UI-only display caches before sending --
        // the backend rejects ``stock_item_variant_display`` (unknown
        // field) and silently ignores ``supplier_display_name`` (it's
        // ``read_only`` on the serializer); strip both to keep the
        // payload clean.
        const {
          stock_item_variant_display,
          supplier_display_name,
          counter_party_display_name,
          ...copy
        } = row;
        // null-out empty strings so DRF accepts them
        Object.keys(copy).forEach((k) => { if (copy[k] === '') copy[k] = null; });
        if (copy.id == null) delete copy.id;
        return copy;
      })];
    }
    // Drop empty strings — DRF will accept null but rejects '' for FK / numeric fields.
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '') payload[k] = null;
    });
    // PATCH-safety: these FKs are non-nullable on the FuelEnquiry model
    // (PROTECT, no ``null=True``). Sending ``null`` for them on PATCH
    // makes DRF reject the whole save with "This field may not be null."
    // even when the existing row already has a valid value. On PATCH we
    // want "leave it alone" semantics for these -- so if the form's
    // value is null we omit the key entirely. The user can still pick a
    // different value to overwrite, just can't blank it out (which the
    // database wouldn't allow anyway).
    if (!isCreateMode) {
      ['imt_branch', 'customer_trader', 'hms_sell_company'].forEach((k) => {
        if (payload[k] == null) delete payload[k];
      });
    }
    return payload;
  };

  // Thin wrapper around the global flash provider so we keep the
  // ``flash(kind, message)`` call sites untouched. ``'info'`` maps to
  // ``showSuccess`` (positive non-error toast) -- the 202 paused-on-
  // credit branch on Save & Workflow uses it to communicate "Saved,
  // now waiting on CRM" without flashing red.
  const flash = (kind, message) => {
    if (kind === 'success' || kind === 'info') showSuccess(message);
    else showError(message);
  };

  // Re-pulls the enquiry detail (3rd party costs, latest credit
  // request, stage history, etc.) without touching ``formState``. Used
  // by side-panel widgets like ``SectionThirdPartyCosts`` after a
  // POST/PATCH so the user sees the new row appear without a full page
  // refresh. No-op in create mode (no id yet).
  const refreshEnquiry = async () => {
    if (isCreateMode || !routeId) return;
    try {
      const fresh = await fuelEnquiryService.retrieve(routeId);
      setEnquiry(fresh);
    } catch (err) {
      console.error('refreshEnquiry failed:', err);
    }
  };

  // Pulls the trade-document dispatches for this enquiry so the
  // outstanding-sends indicator can render pending / sent rows. No-op in
  // create mode (no id yet). Returns the rows so callers (the location-state
  // deep-link) can act on them immediately.
  const loadDispatches = async () => {
    if (isCreateMode || !routeId) return [];
    setDispatchesLoading(true);
    try {
      const rows = await fuelEnquiryService.listDispatches(routeId);
      setDispatches(rows);
      return rows;
    } catch (err) {
      console.error('loadDispatches failed:', err);
      return [];
    } finally {
      setDispatchesLoading(false);
    }
  };

  // Raise the next version of a sent document, then reload so the superseded
  // row drops to the trail and the new pending revision takes its place. The
  // new version opens straight into Review & Send: the trader raised it in
  // order to amend something, so landing them on the edit form is the point.
  const handleReviseDispatch = async (dispatch) => {
    if (!dispatch?.id || revisingDispatchId) return;
    setRevisingDispatchId(dispatch.id);
    try {
      const revision = await fuelEnquiryService.reviseDispatch(dispatch.id);
      const rows = await loadDispatches();
      const fresh = rows.find((r) => r.id === revision.id) || revision;
      setActiveDispatch(fresh);
    } catch (err) {
      setSaveError(
        err?.response?.data?.error
        || 'Could not create a revision of this document.',
      );
    } finally {
      setRevisingDispatchId(null);
    }
  };

  // Open the document a sent version actually put in front of the
  // counterparty. Deliberately NOT a re-render: the backend serves the emailed
  // bytes, so this still shows the old price after the deal has moved on.
  const handleViewSentDocument = async (dispatch) => {
    if (!dispatch?.id) return;
    try {
      const blob = await fuelEnquiryService.dispatchPreviewPdf(dispatch.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // The new tab needs the blob to still exist while it loads; revoking
      // immediately would hand it a dead URL, never revoking would leak it.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setSaveError(
        err?.response?.data?.error
        || 'Could not open the document that was sent.',
      );
    }
  };

  // Fetch dispatches once the enquiry has reached the ``recapped`` stage --
  // that's when the recap hook (Phase 4) mints the dispatch rows. If the user
  // arrived from a dispatch notification (``location.state.openDispatchId``),
  // auto-open that row's Review & Send modal and clear the routing state so a
  // refresh / back-nav doesn't re-trigger it.
  useEffect(() => {
    if (isCreateMode || !routeId) return;
    if (enquiry?.stage !== 'recapped') return;

    let cancelled = false;
    (async () => {
      const rows = await loadDispatches();
      if (cancelled) return;
      if (pendingDispatchOpenId) {
        const target = rows.find((d) => d.id === pendingDispatchOpenId);
        if (target) setActiveDispatch(target);
        // Clear the one-shot routing state regardless of whether the row was
        // found (e.g. already sent) so it never re-fires.
        history.replace(location.pathname, {
          ...(location.state || {}),
          openDispatchId: undefined,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, enquiry?.stage]);

  // ``silent: true`` suppresses the "Saved as ..." success toast so
  // composite flows (Save & Workflow) can own the final user-facing
  // message. Errors still flash regardless of ``silent`` so the user
  // never loses a failure signal.
  const handleSaveDraft = async ({ silent = false } = {}) => {
    if (!validateForSave()) {
      flash('error', 'Please fix the highlighted fields.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = buildPayload();
      let updated;
      if (isCreateMode) {
        updated = await fuelEnquiryService.create(payload);
        history.replace(
          `/dashboard/trades/fuels/fuel-enquiries/${updated.id}`,
          { commodityType: 'fuels' },
        );
      } else {
        updated = await fuelEnquiryService.update(routeId, payload);
      }
      // Backend ``FuelEnquiryViewSet.create`` and ``update`` both
      // return the full Detail payload (stage, credit_status, nested
      // tonnage / third_party_costs / stage_history etc.) so the form
      // can re-render the right sections + buttons after any save
      // without a second round-trip. See the bug note in
      // ``backend/fuel_enquiries/views.py::update``.
      setEnquiry(updated);
      // Treat the post-save reload as a fresh bootstrap so the cascading
      // state updates from ``detailToFormState`` don't trip the dirty
      // tracker. Re-enable after a settle window (matches DeliveryScreen).
      isInitialLoad.current = true;
      setFormState(detailToFormState(updated));
      userHasInteracted.current = false;
      setTimeout(() => { isInitialLoad.current = false; }, 500);
      // Saving a recapped deal can add or cancel Supplier Nominations (a
      // supplier moved onto or off a fuel line). The document list only loads
      // when the stage changes, so without this the cards kept showing the old
      // letters until the page was reloaded.
      if (updated?.stage === 'recapped') {
        loadDispatches();
      }
      if (!silent) {
        flash('success', `Saved as ${updated.display_id || 'draft'}.`);
      }
      return updated;
    } catch (err) {
      console.error('Save failed:', err);
      const detail = errorDetail(err);
      setSaveError(detail);
      flash('error', detail);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // -------- Network: stage actions --------

  const openStageModal = (action, target = null, requireNotes = false) => {
    // Bunkers_contracts Phase 3 hand-off: the ``with_supply_trader ->
    // recapped`` transition opens the new ContractCreatorModal instead
    // of the generic StageAdvanceModal. Every other stage action keeps
    // the existing flow untouched.
    if (action === 'advance' && target === 'recapped') {
      openRecapFlow();
      return;
    }
    setStageModalError(null);
    setStageModalState({ isOpen: true, action, target, requireNotes });
  };

  // Bunkers KYC gate (July 2026): before opening the Contract Creator modal,
  // precheck whether the recap can actually run. If it can't, open the KYC
  // block modal instead. The authoritative gate is the 409 on advance
  // (handled in ``runContractCreation``); this is the fast, save-nothing path.
  //
  // Gate on ``can_proceed``, NOT on ``blocked_partners``. The two answer
  // different questions: ``blocked_partners`` is the compliance truth and
  // ignores an open KYCGateBypass by design, while ``can_proceed`` is what the
  // recap will actually enforce. Prechecking on the truth list is what refused
  // 7 of 8 live enquiries during the Aug 2026 UAT stand-down -- the modal has
  // no way forward, so the trader never reached the advance that would have
  // succeeded.
  const openRecapFlow = async () => {
    try {
      const statusResp = await fuelEnquiryService.getKycClearanceStatus(routeId);
      // An older backend has neither field. Fall back to the truth list so we
      // fail CLOSED (a false block is recoverable; a false pass walks an
      // unscreened partner into a recap).
      const canProceed = typeof statusResp?.can_proceed === 'boolean'
        ? statusResp.can_proceed
        : (statusResp?.blocked_partners || []).length === 0;
      if (!canProceed) {
        // Show what actually blocks, not the waived entries.
        const blocking = statusResp?.enforced_blockers
          || statusResp?.blocked_partners
          || [];
        setKycBlockState({
          open: true,
          blockedPartners: blocking,
          canRequest: !!statusResp.can_request,
          notifying: false,
          error: null,
        });
        return;
      }
    } catch (err) {
      // A precheck failure must not block the flow -- fall through to the
      // Contract Creator modal; the advance itself surfaces any real gate
      // error (SECO 400 / KYC 409).
      console.error('KYC clearance precheck failed:', err);
    }
    openContractModal();
  };

  const closeKycBlockModal = () => {
    if (kycBlockState.notifying) return;
    setKycBlockState({
      open: false, blockedPartners: [], canRequest: false,
      notifying: false, error: null,
    });
  };

  const handleNotifyKycApprover = async () => {
    setKycBlockState((s) => ({ ...s, notifying: true, error: null }));
    try {
      const result = await fuelEnquiryService.requestKycClearance(routeId);
      setKycBlockState({
        open: false, blockedPartners: [], canRequest: false,
        notifying: false, error: null,
      });
      if (result && result.notified_approvers) {
        flash(
          'success',
          'KYC approver notified. You will be told when the enquiry is clear '
          + 'to recap.',
        );
      } else {
        // Requests were minted but no active KYC approver is configured, so
        // nobody was paged -- don't flash a false success.
        flash(
          'warning',
          'Clearance recorded, but no KYC approver is configured to notify. '
          + 'Ask an administrator to assign a KYC approver.',
        );
      }
    } catch (err) {
      setKycBlockState((s) => ({
        ...s, notifying: false, error: errorDetail(err),
      }));
    }
  };

  const closeStageModal = () => {
    if (stageModalSubmitting) return;
    setStageModalState({ isOpen: false, action: 'advance', target: null, requireNotes: false });
    setStageModalError(null);
  };

  const openContractModal = () => {
    setContractModalError(null);
    setContractSaleFiles([]);
    setContractPurchaseFiles([]);
    setContractModalOpen(true);
  };

  const closeContractModal = () => {
    if (contractModalSubmitting) return;
    setContractModalOpen(false);
    setContractModalError(null);
    setContractSaleFiles([]);
    setContractPurchaseFiles([]);
  };

  const runStageAction = async (notes) => {
    setStageModalSubmitting(true);
    setStageModalError(null);
    try {
      // Persist any unsaved form edits BEFORE running an advance / lost
      // transition. Without this, the user's frontend gate (which reads
      // ``formState``) may pass while the backend gate (which reads the
      // DB row) still sees missing fields -- exactly what happens when a
      // trader fills the product section in the form, doesn't click Save
      // Draft, and clicks Save & Workflow. The backend gate then
      // rejects with "missing: fuel_type_id, sell_price, ...".
      // Mirrors ``handleSubmitAndAdvance``'s save-first pattern. Skipped
      // for archive (terminal-only) and rollback (no form edits should
      // change a backwards transition's outcome).
      //
      // ``unarchive`` is skipped for a harder reason than either: the row is
      // archived, so the save-first PATCH is refused by the server (409) and
      // the restore never runs -- pressing Restore just raised "this enquiry
      // is archived and cannot be changed", which is both true and useless.
      // Caught by the browser walkthrough; the endpoint tests could not see
      // it because they never went through this screen.
      const isAdvanceLike = (
        stageModalState.action !== 'archive'
        && stageModalState.action !== 'unarchive'
        && stageModalState.action !== 'rollback'
      );
      if (isAdvanceLike) {
        const saved = await handleSaveDraft();
        if (!saved) {
          setStageModalSubmitting(false);
          return;
        }
        // Race guard: between the modal opening and the user clicking
        // Advance, the row's stage can drift (a concurrent edit, the
        // ``advance_stage`` side-effects landing late, or a parallel
        // tab). ``handleSaveDraft`` re-pulls the canonical row; if the
        // refreshed ``saved.stage`` no longer permits ``target``, close
        // the modal with a clear message rather than hitting the
        // backend just to surface its 400 ("Cannot transition from
        // 'recapped' to 'recapped'", which makes no sense to a user).
        const liveStage = saved.stage;
        const target = stageModalState.target;
        const legalNext = movesFromServer(saved);
        if (liveStage === target || !legalNext.includes(target)) {
          setStageModalState({
            isOpen: false, action: 'advance', target: null, requireNotes: false,
          });
          setStageModalError(null);
          flash(
            'info',
            `This enquiry is now at ${STAGE_LABEL[liveStage] || liveStage}. `
            + `No advance to ${STAGE_LABEL[target] || target} is available `
            + 'from here. The form has been refreshed.',
          );
          setStageModalSubmitting(false);
          return;
        }
      }

      if (stageModalState.action === 'archive') {
        const archived = await fuelEnquiryService.archive(routeId);
        setEnquiry(archived);
        setFormState(detailToFormState(archived));
        flash('success', stageActionFlash(stageModalState, archived));
      } else if (stageModalState.action === 'unarchive') {
        const restored = await fuelEnquiryService.unarchive(routeId);
        setEnquiry(restored);
        setFormState(detailToFormState(restored));
        flash('success', stageActionFlash(stageModalState, restored));
      } else if (stageModalState.action === 'rollback') {
        const rolled = await fuelEnquiryService.rollbackStage(routeId, { notes });
        setEnquiry(rolled);
        setFormState(detailToFormState(rolled));
        flash('success', stageActionFlash(stageModalState, rolled));
      } else {
        const result = await fuelEnquiryService.advanceStage(routeId, {
          target_stage: stageModalState.target,
          notes,
        });
        if (result.paused) {
          // Bunkers 2-screen: credit-paused on draft. The backend
          // already flipped credit_status to ``awaiting_credit_approval``;
          // refresh the enquiry detail so the badge + next-step banner
          // pick up the new status (the 202 payload doesn't include
          // the full enquiry shape).
          const refreshed = await fuelEnquiryService.retrieve(routeId);
          setEnquiry(refreshed);
          setFormState(detailToFormState(refreshed));
          flash(
            'info',
            'Credit increase request opened. The enquiry stays on '
            + 'Draft until a Credit Approver actions the request.',
          );
        } else {
          setEnquiry(result.enquiry);
          setFormState(detailToFormState(result.enquiry));
          flash('success', stageActionFlash(stageModalState, result.enquiry));
        }
      }
      setStageModalState({ isOpen: false, action: 'advance', target: null, requireNotes: false });
    } catch (err) {
      console.error('Stage action failed:', err);
      setStageModalError(errorDetail(err));
    } finally {
      setStageModalSubmitting(false);
    }
  };

  // Update a single file row inside either Sale or Purchase file list.
  // Used by ``uploadContractFiles`` to drive the per-file status pill in
  // the modal (queued -> uploading -> uploaded / failed).
  const updateContractFileEntry = (side, localId, patch) => {
    const setter = side === 'sale' ? setContractSaleFiles : setContractPurchaseFiles;
    setter((prev) => prev.map((entry) => (
      entry.localId === localId ? { ...entry, ...patch } : entry
    )));
  };

  // Sequential per-file upload. We do not parallelise because the
  // backend file endpoint isn't sharded for concurrency in Phase 4 yet
  // and sequential keeps the per-file progress UI honest. Returns the
  // number of failed uploads so the parent can decide what to surface.
  const uploadContractFiles = async (contractId, fileEntries, side) => {
    let failedCount = 0;
    // ``fileEntries`` is captured at submit-time; iterate it directly so
    // we don't depend on the latest React state during the await loop.
    for (const entry of fileEntries) {
      if (entry.status === 'invalid') {
        failedCount += 1;
        continue;
      }
      updateContractFileEntry(side, entry.localId, {
        status: 'uploading',
        progress: 0,
        error: null,
      });
      try {
        await legalContractService.uploadFile(
          contractId,
          entry.file,
          (progressEvent) => {
            if (progressEvent && progressEvent.total) {
              const pct = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              updateContractFileEntry(side, entry.localId, { progress: pct });
            }
          },
        );
        updateContractFileEntry(side, entry.localId, {
          status: 'uploaded',
          progress: 100,
        });
      } catch (err) {
        failedCount += 1;
        const msg = (
          err?.response?.data?.detail
          || err?.message
          || 'Upload failed'
        );
        updateContractFileEntry(side, entry.localId, {
          status: 'failed',
          error: msg,
        });
        console.error(
          `Contract file upload failed (contract ${contractId}):`, err,
        );
      }
    }
    return failedCount;
  };

  // Contract Creator Modal confirm handler. Mirrors ``runStageAction``'s
  // shape (save-draft-first, then advance) but with the Phase 3 contract
  // payload + post-advance file uploads. Keeps the modal open if any
  // file upload fails so the trader can see which files need re-trying
  // from the Legal module.
  const runContractCreation = async ({
    contract_payloads,
    notes,
    seco_confirmed,
    saleFiles,
    purchaseFiles,
  }) => {
    setContractModalSubmitting(true);
    setContractModalError(null);
    try {
      // Save any in-flight form edits before advancing. ``handleSaveDraft``
      // returns the canonical row or ``null`` on validation failure.
      const saved = await handleSaveDraft();
      if (!saved) {
        setContractModalSubmitting(false);
        return;
      }

      if (saved.stage !== 'with_supply_trader') {
        flash(
          'info',
          `Enquiry is on ${STAGE_LABEL[saved.stage] || saved.stage} — `
          + 'contract creation only runs from With Supply Trader.',
        );
        setContractModalOpen(false);
        setContractModalSubmitting(false);
        return;
      }

      const result = await fuelEnquiryService.advanceStage(routeId, {
        target_stage: 'recapped',
        notes,
        contract_payloads,
        seco_confirmed,
      });

      // Recapped is never paused-on-credit (credit was handled earlier
      // at draft -> with_supply_trader), so we ignore result.paused here.
      setEnquiry(result.enquiry);
      setFormState(detailToFormState(result.enquiry));

      const contracts = result.contracts || {};
      const saleContractId = contracts.sale?.id;
      const purchaseContractId = contracts.purchase?.id;

      // Fire-and-await file uploads now that the contracts exist. A
      // missing contract id (because the backend response shape was
      // somehow stripped) is logged but does not crash the modal — the
      // trader can re-upload from the Legal module per planning doc 02
      // edge-cases.
      let failedUploads = 0;
      if (saleContractId && saleFiles.length > 0) {
        failedUploads += await uploadContractFiles(
          saleContractId, saleFiles, 'sale',
        );
      }
      if (purchaseContractId && purchaseFiles.length > 0) {
        failedUploads += await uploadContractFiles(
          purchaseContractId, purchaseFiles, 'purchase',
        );
      }

      const saleC = contracts.sale?.contract_number;
      const purchaseC = contracts.purchase?.contract_number;
      const successMsg = (saleC && purchaseC)
        ? `Contracts created: ${saleC} (Sale) + ${purchaseC} (Purchase).`
        : 'Contracts created.';

      if (failedUploads > 0) {
        flash(
          'warning',
          `${successMsg} ${failedUploads} file upload(s) failed — `
          + 'retry from the Legal module.',
        );
        // Keep the modal open so the trader sees which files failed;
        // they can click Cancel to dismiss.
        setContractModalSubmitting(false);
        return;
      }

      flash('success', successMsg);
      setContractModalOpen(false);
      setContractSaleFiles([]);
      setContractPurchaseFiles([]);
    } catch (err) {
      // KYC gate (409) fallback: the partner state drifted between the
      // precheck and this authoritative advance. Close the Contract Creator
      // modal and surface the block modal instead of a raw error string.
      const resp = err?.response;
      if (
        resp && resp.status === 409
        && resp.data && resp.data.error === 'kyc_clearance_pending'
      ) {
        const blocked = resp.data.blocked_partners || [];
        setContractModalOpen(false);
        setContractSaleFiles([]);
        setContractPurchaseFiles([]);
        setKycBlockState({
          open: true,
          blockedPartners: blocked,
          canRequest: blocked.some((p) => p.reason === 'awaiting_kyc'),
          notifying: false,
          error: null,
        });
        return;
      }
      console.error('Contract creation failed:', err);
      setContractModalError(errorDetail(err));
    } finally {
      setContractModalSubmitting(false);
    }
  };

  // Bunkers 2-screen "Save & Workflow" button on Screen 1. Saves the
  // current form state first, then drives the row through the
  // ``draft -> with_supply_trader`` transition. Two success branches:
  //   * Normal advance         -> enquiry now sits on with_supply_trader,
  //                               flash the Screen 2 hand-off message.
  //   * Paused on credit (202) -> enquiry STAYS on draft with
  //                               credit_status='awaiting_credit_approval';
  //                               flash the "waiting on credit" message.
  const handleSubmitAndAdvance = async () => {
    // ``silent: true`` -- this composite action owns its own messaging.
    // Without it the trader sees a misleading "Saved as ENQ-XXX" toast
    // immediately followed by an "advance failed" toast on the same
    // click, which reads like two unrelated outcomes.
    const updated = await handleSaveDraft({ silent: true });
    if (!updated) return;
    const savedAs = updated.display_id || `#${updated.id}`;
    try {
      const result = await fuelEnquiryService.advanceStage(updated.id, {
        target_stage: 'with_supply_trader',
        notes: '',
      });
      if (result.paused) {
        const refreshed = await fuelEnquiryService.retrieve(updated.id);
        setEnquiry(refreshed);
        setFormState(detailToFormState(refreshed));
        flash(
          'info',
          `Saved as ${savedAs}. Credit increase request opened -- the `
          + 'enquiry stays on Draft until a Credit Approver actions '
          + 'the request.',
        );
      } else {
        setEnquiry(result.enquiry);
        setFormState(detailToFormState(result.enquiry));
        flash(
          'success',
          `Saved as ${savedAs} and handed off to the Supply Trader.`,
        );
      }
    } catch (err) {
      console.error('Save & Workflow advance failed:', err);
      flash(
        'error',
        `Saved as ${savedAs}, but workflow failed: ${errorDetail(err)}`,
      );
    }
  };

  // Direct navigation back to the Fuels Trade Ops tab. Used both as the
  // clean-state path and after the user confirms abandoning unsaved
  // changes. We deliberately ``push`` the explicit fuel-enquiries tab
  // state instead of ``goBack()`` so the user always lands on the
  // Enquiries tab — even if they originally arrived from the Overview
  // tab. ``TradeOperationsLayout`` reads ``location.state.activeTab`` to
  // restore the active tab on mount.
  const navigateBack = () => {
    history.push('/dashboard/trades/fuels', {
      commodityType: 'fuels',
      activeTab: 'fuel-enquiries',
    });
  };

  const handleCancel = () => {
    if (userHasInteracted.current) {
      setShowBackConfirmation(true);
    } else {
      navigateBack();
    }
  };

  const handleConfirmBack = () => {
    userHasInteracted.current = false;
    setShowBackConfirmation(false);
    navigateBack();
  };

  const handleCancelBack = () => setShowBackConfirmation(false);

  // Generate + preview the Customer Confirmation PDF for the saved enquiry.
  // Only reachable in edit mode (the button is hidden in create mode), so a
  // server id always exists. Streams the PDF as a blob, wraps it in an object
  // URL, and hands it to ``PdfPreviewModal`` (preview + Download).
  const handleCustomerConfirmation = async () => {
    if (isCreateMode || !routeId) return;
    setConfirmation((prev) => ({ ...prev, loading: true }));
    try {
      const blob = await fuelEnquiryService.customerConfirmationPdf(routeId);
      const url = URL.createObjectURL(blob);
      const fileName = `Customer Confirmation ${enquiry?.display_id || routeId}.pdf`;
      setConfirmation({ open: true, loading: false, url, fileName });
    } catch (err) {
      console.error('Customer Confirmation generation failed:', err);
      showError('Could not generate the Customer Confirmation PDF. Please try again.');
      setConfirmation((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleCloseConfirmation = () => {
    setConfirmation((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { open: false, loading: false, url: null, fileName: '' };
    });
  };

  const handleDownloadConfirmation = () => {
    if (!confirmation.url) return;
    const link = document.createElement('a');
    link.href = confirmation.url;
    link.download = confirmation.fileName || 'Customer Confirmation.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // "Save Draft + Leave" path from the back-confirmation modal. Saves
  // first; only navigates away if the save succeeded.
  const handleDraftAndLeave = async () => {
    const updated = await handleSaveDraft();
    if (updated) {
      setShowBackConfirmation(false);
      navigateBack();
    }
  };

  // =================================================================
  // Render
  // =================================================================

  if (bootstrapping) {
    return <FormScaffold><BootstrappingState /></FormScaffold>;
  }
  if (notFound) {
    return (
      <FormScaffold>
        <NotFoundState onBack={handleCancel} />
      </FormScaffold>
    );
  }
  if (accessError) {
    return (
      <FormScaffold>
        <AccessDeniedState message={accessError} onBack={handleCancel} />
      </FormScaffold>
    );
  }
  if (bootError) {
    return (
      <FormScaffold>
        <BootErrorState message={bootError} />
      </FormScaffold>
    );
  }

  const stage = enquiry?.stage || 'draft';
  const creditStatus = enquiry?.credit_status || null;
  const isAwaitingCreditApproval = (
    stage === 'draft' && creditStatus === 'awaiting_credit_approval'
  );
  const allowedNextStages = movesFromServer(enquiry);
  const rollbackTarget = ROLLBACK_MAP[stage];
  const isTerminal = TERMINAL_STAGES.has(stage);
  const ownerGate = enquiry ? isRowOwner : true; // create mode = no row yet → no gate

  // -------- Stepper sections --------
  // Order matches the visual section sequence below. ``completed`` is a
  // best-effort heuristic over the required-for-save fields; the real
  // gate is still ``validateForSave`` / ``missingFieldsForTarget``.
  //
  // Bunkers 2-screen redesign (May 2026): on Screen 1 (``draft``) the
  // Supplier step is hidden because Supplier Pricing is captured on
  // Screen 2. Ship Details moves up to slot 5 on Screen 1. On Screen 2
  // (``with_supply_trader`` and beyond) the Supplier step reappears.
  const stepperSections = [
    {
      id: 'hms',
      label: 'IMT',
      completed: Boolean(formState.imt_branch && formState.customer_trader && formState.hms_sell_company),
    },
    {
      id: 'customer',
      label: 'Customer',
      completed: Boolean(formState.customer) || (showUnlinked && Boolean(formState.unlinked_customer_name?.trim())),
    },
    {
      id: 'credit',
      label: 'Credit',
      completed: Boolean(formState.customer) || (showUnlinked && Boolean(formState.unlinked_customer_name?.trim())),
    },
    {
      id: 'product',
      label: 'Product',
      completed: Boolean(
        formState.fuel_type
        && formState.fuel_grade
        && formState.sulfur_limit
        && formState.iso_8217_year
        && formState.min_tonnage
        && formState.max_tonnage
        && formState.sell_price
        && formState.pricing_type,
      ),
    },
    {
      id: 'ship',
      label: 'Ship Details',
      completed: Boolean(
        formState.vessel_name
        && formState.vessel_type
        && formState.port
        && formState.expected_delivery_start
        && formState.expected_delivery_end,
      ),
    },
    // Supply-side steps. Gated on BOTH stage and ``isSupplySideHidden`` —
    // the same predicate as the sections themselves. Without the second
    // condition a restricted Customer Trader got two stepper entries linking
    // to anchors that never render (dead links); with the old flag-only gate
    // on the sections, an admin carrying the trader flag got the opposite —
    // the steps rendered while the sections did not.
    ...(stage === 'draft' || isSupplySideHidden ? [] : [
      {
        id: 'supplier',
        label: 'Supplier',
        completed: Boolean(
          formState.supplier
          && formState.buy_price
          && formState.supplier_credit_days,
        ),
      },
      {
        id: 'third-party',
        label: '3rd Party',
        completed: Array.isArray(enquiry?.third_party_costs)
          && enquiry.third_party_costs.length > 0,
      },
    ]),
  ];

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="fuel-enquiry-screen">
        {/* IMT Screen Header (matches DeliveryScreen) ------------------- */}
        <div className="hms-screen-header">
          <button
            className="fef-back-circle"
            onClick={handleCancel}
            title="Back to Bunker Enquiries"
            type="button"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <div className="fef-header-logo">
            <img src={logo} alt="IMT Bergbau AG" className="fef-header-logo-img" />
            <div className="fef-header-title-content">
              <div className="fef-header-name-row">
                <FontAwesomeIcon icon={faTint} style={{ color: 'white', marginRight: '6px' }} />
                <span className="fef-header-name-display">
                  {isCreateMode
                    ? 'New Bunker Enquiry'
                    : `Bunker Enquiry ${enquiry?.display_id || ''}`}
                </span>
              </div>
              <div className="fef-header-info-row">
                <span className={STAGE_BADGE_CLASS[stage] || 'fe-stage-badge fe-stage-default'}>
                  {STAGE_LABEL[stage] || stage}
                </span>
                {isAwaitingCreditApproval && (
                  <span
                    className="fef-credit-request-pill fef-credit-request-pill--pending"
                    title="The auto credit check opened a Credit Increase Request. The enquiry will advance to With Supply Trader on approval."
                  >
                    <FontAwesomeIcon icon={faClock} /> Awaiting credit approval
                  </span>
                )}
                {creditStatus && !isAwaitingCreditApproval && (
                  <span
                    className="fe-credit-badge fe-credit-default"
                    title={enquiry?.credit_condition?.text
                      ? 'Credit was approved on condition that the named invoices are paid first. Nothing is blocked; these words clear by themselves once they are paid.'
                      : undefined}
                  >
                    Credit: {CREDIT_STATUS_LABEL[creditStatus] || creditStatus}
                    {/* "Approved, subject to INV-2050 being paid" (ClickUp 1248h532ugm) */}
                    {enquiry?.credit_condition?.text ? `, ${enquiry.credit_condition.text}` : ''}
                  </span>
                )}
                {enquiry?.latest_credit_request?.status === 'rejected' && (
                  <span
                    className="fef-credit-request-pill fef-credit-request-pill--rejected"
                    title="The latest credit increase request for this enquiry was rejected. See the Credit Balances section for details."
                  >
                    <FontAwesomeIcon icon={faCircleXmark} /> Credit request rejected
                  </span>
                )}
                {enquiry?.latest_credit_request?.status === 'pending' && (
                  <span
                    className="fef-credit-request-pill fef-credit-request-pill--pending"
                    title="A credit increase request for this enquiry is awaiting approval."
                  >
                    <FontAwesomeIcon icon={faClock} /> Credit request pending
                  </span>
                )}
                {enquiry?.is_archived && (
                  <span className="fef-archived-badge">Archived</span>
                )}
              </div>
            </div>
          </div>

          {!isCreateMode && <PinToDeskButton entityType="fuel_enquiry" objectId={routeId} />}
          <HeaderActions
            isCreateMode={isCreateMode}
            stage={stage}
            isTerminal={isTerminal}
            ownerGate={ownerGate}
            allowedNextStages={allowedNextStages}
            rollbackTarget={rollbackTarget}
            saving={saving}
            missingFieldsForTarget={missingFieldsForTarget}
            onSaveDraft={handleSaveDraft}
            onSubmitAndAdvance={handleSubmitAndAdvance}
            onAdvance={(target) => openStageModal('advance', target, false)}
            onRollback={() => openStageModal('rollback', null, false)}
            onMarkAsLost={() => openStageModal('lost', 'lost', true)}
            onArchive={() => openStageModal('archive', null, false)}
            isArchived={!!enquiry?.is_archived}
            onCustomerConfirmation={handleCustomerConfirmation}
            confirmationLoading={confirmation.loading}
            canDownloadConfirmation={!isCreateMode && !!enquiry?.trade_recap}
          />
        </div>

        {/* Archived banner -----------------------------------------------
            An archived enquiry can now be opened from anywhere it is
            referenced (its recap's "View Enquiry" button, a saved link, the
            archived-only list). Opening one must SAY so, name who filed it
            away and when, and offer the way back -- the Archive button had no
            counterpart anywhere in the app, so the only route out of the
            archive was a direct database edit. */}
        {enquiry?.is_archived && (
          <div className="fef-archived-banner" role="status">
            <FontAwesomeIcon icon={faArchive} className="fef-archived-banner-icon" />
            <div className="fef-archived-banner-body">
              <div className="fef-archived-banner-title">This enquiry is archived</div>
              <div className="fef-archived-banner-desc">
                {enquiry.archived_by_employee_name
                  ? `Archived by ${enquiry.archived_by_employee_name}`
                  : 'Archived'}
                {enquiry.archived_at ? ` on ${formatDateTime(enquiry.archived_at)}` : ''}
                . It is read-only and hidden from the enquiry list until it is
                restored. Everything on it is intact.
              </div>
            </div>
            {ownerGate && (
              <button
                type="button"
                className="fef-btn fef-btn-secondary fef-archived-banner-action"
                onClick={() => openStageModal('unarchive', null, false)}
                disabled={saving}
              >
                <FontAwesomeIcon icon={faArchive} /> Restore to live list
              </button>
            )}
          </div>
        )}

        {/* Sticky numbered stepper -------------------------------------- */}
        <div className="fef-stepper-bar">
          <FuelEnquiryStepper
            sections={stepperSections}
            activeId={activeStep}
            onStepClick={(id) => {
              setActiveStep(id);
              const el = document.getElementById(`fef-anchor-${id}`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />
        </div>

        {/* Form content ------------------------------------------------- */}
        <div className="fef-content-wrapper">
          {/* KYC gate stood down for testing. Deliberately persistent and
              impossible to dismiss: recaps made in this window skip the
              partner-KYC check and are stamped for Compliance review, so the
              trader has to be able to see that they are inside it. */}
          {kycBypass && (
            <div className="fef-kyc-bypass-banner" role="status">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>
                <strong>KYC gate bypassed for testing</strong> — partners without
                portal KYC approval will not block a recap until{' '}
                <strong>{kycBypass.active_until}</strong>
                {typeof kycBypass.days_remaining === 'number' && (
                  <> ({kycBypass.days_remaining} day
                    {kycBypass.days_remaining === 1 ? '' : 's'} left)</>
                )}
                . Partners flagged by Compliance still block. Every recap made
                now is logged for Compliance review.
              </span>
            </div>
          )}

          <NextStepBanner
            isCreateMode={isCreateMode}
            stage={stage}
            enquiry={enquiry}
            missingForNext={
              stage === 'draft' && (isCreateMode || allowedNextStages.includes('with_supply_trader'))
                ? missingFieldsForTarget('with_supply_trader')
                : stage === 'with_supply_trader' && allowedNextStages.includes('recapped')
                  ? missingFieldsForTarget('recapped')
                  : []
            }
            onJumpTo={(id) => {
              setActiveStep(id);
              const el = document.getElementById(`fef-anchor-${id}`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            onViewRecap={(recapId) => history.push(
              `/trade-recap/${recapId}`,
              { commodityType: 'fuels' },
            )}
          />

          {/* Outstanding-sends indicator (Phase 7). Once recapped, list any
              dispatch rows so the assignee can review + send each document
              and see which have already gone out. */}
          {stage === 'recapped' && !dispatchesLoading && dispatches.length > 0 && (
            <DispatchSendsIndicator
              dispatches={dispatches}
              onReview={(dispatch) => setActiveDispatch(dispatch)}
              onRevise={handleReviseDispatch}
              onViewSent={handleViewSentDocument}
              revisingId={revisingDispatchId}
            />
          )}

          {saveError && (
            <div className="fef-error-banner">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{saveError}</span>
            </div>
          )}

          {/* Read-only when archived --------------------------------------
              The server refuses a save on an archived enquiry (409), but a
              refusal the trader only meets AFTER typing a paragraph is a bad
              way to learn the rule. A native disabled <fieldset> greys every
              control inside it in one move, with no prop threaded through
              seven sections. Deliberately wraps the INPUT sections only:
              Stage History, Contracts, the recap links and the Customer
              Confirmation download all sit outside it and stay usable, which
              is the whole point of being able to open an archived deal. */}
          {/* Row 132: one list of floating formulas for every section below. */}
          <FloatingFormulaProvider
            formulas={floatingFormulas}
            enquiry={enquiry}
            isCustomerTrader={isSupplySideHidden}
          >
          <fieldset
            className="fef-form-fieldset"
            disabled={!!enquiry?.is_archived}
          >
          <SectionIMT
            stepNumber={1}
            anchorId="fef-anchor-hms"
            formState={formState}
            setField={setField}
            branches={branches}
            hmsEntities={hmsEntities}
            traders={traders}
            savedTraders={{
              customer: enquiry?.customer_trader_detail,
              supplier: enquiry?.supplier_trader_detail,
            }}
            onBranchChange={handleBranchChange}
            validationErrors={validationErrors}
          />

          <SectionCustomer
            stepNumber={2}
            anchorId="fef-anchor-customer"
            formState={formState}
            setField={setField}
            customerProfile={customerProfile}
            onCustomerChange={handleCustomerChange}
            showUnlinked={showUnlinked}
            onUnlinkedToggle={handleUnlinkedToggle}
            validationErrors={validationErrors}
            enquiry={enquiry}
            onCreateCustomer={openCustomerCrmCreator}
            extraVisibleProfiles={pendingCustomerProfiles}
          />

          <SectionCreditBalances
            stepNumber={3}
            anchorId="fef-anchor-credit"
            customerProfile={customerProfile}
            enquiry={enquiry}
            creditRequest={creditRequest}
          />

          <SectionProd1
            stepNumber={4}
            anchorId="fef-anchor-product"
            formState={formState}
            setField={setField}
            lookups={lookups}
            contractShorthand={contractShorthand}
            validationErrors={validationErrors}
            variantMeta={productVariantMeta.product1 || null}
            onPickerChange={(variantId, displayName, fullVariant) =>
              applyVariantSelection('product1', variantId, displayName, fullVariant)
            }
            onCreateProduct={(searchTerm) =>
              openProductCreator('product1', searchTerm)
            }
          />

          {/* Sprint 6 multi-product: render one card per extra product
           * row in formState.extra_products plus a "+ Add another product"
           * button below. The first product stays in SectionProd1 above
           * (back-compat with single-product enquiries). */}
          {(formState.extra_products || []).map((row, idx) => (
            <ExtraProductCard
              key={`extra-product-${idx}`}
              index={idx}
              row={row}
              lookups={lookups}
              onChange={(key, value) => {
                setFormState((prev) => {
                  const next = [...(prev.extra_products || [])];
                  next[idx] = { ...next[idx], [key]: value };
                  return { ...prev, extra_products: next };
                });
                userHasInteracted.current = true;
              }}
              onPickerChange={(variantId, displayName, fullVariant) =>
                applyVariantSelection({ type: 'extra', idx }, variantId, displayName, fullVariant)
              }
              onCreateProduct={(searchTerm) =>
                openProductCreator({ type: 'extra', idx }, searchTerm)
              }
              variantMeta={productVariantMeta[`extra:${idx}`] || null}
              onRequestRemove={() => {
                // Persisted rows (those with a DB id) require an explicit
                // confirmation -- removing them deletes the
                // FuelEnquiryProduct from the database on next save.
                // Brand-new (unsaved) rows just disappear.
                if (row && row.id != null) {
                  setRemoveConfirmIdx(idx);
                } else {
                  setFormState((prev) => ({
                    ...prev,
                    extra_products: (prev.extra_products || []).filter((_, i) => i !== idx),
                  }));
                  userHasInteracted.current = true;
                }
              }}
            />
          ))}

          <div className="fef-add-product-row">
            <button
              type="button"
              className="fef-btn fef-btn-secondary"
              onClick={() => {
                setFormState((prev) => ({
                  ...prev,
                  extra_products: [...(prev.extra_products || []), blankExtraProduct()],
                }));
                userHasInteracted.current = true;
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Add another product
            </button>
            <span className="fef-add-product-hint">
              {stage === 'draft'
                ? 'Stack additional fuel products onto this enquiry. '
                  + 'Each product gets its own fuel spec, tonnage and sell '
                  + 'price -- supplier pricing is filled in on the Supply '
                  + 'Trader screen after Save & Workflow.'
                : 'Stack additional fuel products onto this enquiry. '
                  + 'Each product gets its own fuel spec, tonnage and sell '
                  + 'price -- supplier pricing for every product is '
                  + 'captured together in the Supplier Pricing card below.'}
            </span>
          </div>

          <CommentBlock
            formState={formState}
            setField={setField}
            title="Products Comments"
            anchorId="fef-anchor-products-comments"
            textKey="products_comments"
            showOnCcKey="show_products_comments_on_customer_confirmation"
            showOnSnKey="show_products_comments_on_supplier_nomination"
          />

          {/* Bunkers 2-screen redesign: Supplier Pricing is Screen-2
              only. It appears once the enquiry has crossed into
              ``with_supply_trader`` (or any later stage). Screen 1
              stops at Ship Details + Comments. */}
          <SectionShipDetails
            stepNumber={5}
            anchorId="fef-anchor-ship"
            formState={formState}
            setField={setField}
            handleEventInput={handleEventInput}
            vesselTypes={lookups.vesselTypes}
            bunkerPorts={bunkerPorts}
            onPortChange={handlePortChange}
            validationErrors={validationErrors}
          />

          <CommentBlock
            formState={formState}
            setField={setField}
            title="Shipment Comments"
            anchorId="fef-anchor-shipment-comments"
            textKey="shipment_comments"
            showOnCcKey="show_shipment_comments_on_customer_confirmation"
            showOnSnKey="show_shipment_comments_on_supplier_nomination"
          />

          {/* Restricted Customer Traders only see Screen 1 — the supply-side
              sections (Supplier Pricing, 3rd Party Costs) belong to the Supply
              Trader and stay hidden for them. Superusers are NOT restricted,
              matching every backend overlay (see isSupplySideHidden). */}
          {stage !== 'draft' && !isSupplySideHidden && (
            <SectionSupplierPricing
              stepNumber={6}
              anchorId="fef-anchor-supplier"
              formState={formState}
              setField={setField}
              setExtraField={setExtraField}
              hmsEntities={hmsEntities}
              lookups={lookups}
              validationErrors={validationErrors}
            />
          )}

          <CommentBlock
            formState={formState}
            setField={setField}
            title="General Comments"
            anchorId="fef-anchor-comments"
            textKey="comments"
            showOnCcKey="show_comments_on_customer_confirmation"
            showOnSnKey="show_comments_on_supplier_nomination"
          />

          {/* Bunkers 2-screen Section 7. Screen-2 only: rows are
              authored once the Supply Trader picks the enquiry up.
              ``suppliers`` is deliberately NOT passed -- 3rd-party
              suppliers (barging / survey / agency) are not the same
              population as fuels-suppliers, so the picker searches
              all CRM partners via the unfiltered search endpoint. */}
          {stage !== 'draft' && !isCreateMode && enquiry?.id
            && !isSupplySideHidden && (
            <SectionThirdPartyCosts
              stepNumber={7}
              anchorId="fef-anchor-third-party"
              enquiry={enquiry}
              categories={lookups.thirdPartyCostCategories}
              onChanged={refreshEnquiry}
              commissionPreviews={computeCommissionPreviews(formState)}
            />
          )}

          <SectionTotals creditRequest={creditRequest} />
          </fieldset>
          </FloatingFormulaProvider>

          {!isCreateMode && enquiry?.stage_history?.length > 0 && (
            <StageHistoryPanel history={enquiry.stage_history} />
          )}

          {/* Contracts minted from this enquiry. The Legal module is closed
              (Craig, 2026-08-26): both legs are read here instead. Read-only —
              minting still happens through the Contract Creator. A Customer
              Trader is narrowed to the SALE leg server-side.

              Mounted INSIDE the form content, next to Stage History. It first
              sat beside the modal mounts at the end of the component, which
              put it below the page footer — present in the DOM and passing
              every probe, but visually off the end of the page. */}
          {!isCreateMode && enquiry?.id && (
            <ContractsInContextPanel
              enquiryId={enquiry.id}
              title="Contracts"
              /* Spelled out rather than left to the shared panel's default.
                 The delivery screen wanted different empty wording and moved
                 that default, which rewrote this screen without anyone
                 looking at it. A host that cares about its own words says
                 them. */
              emptyLabel="No contracts yet."
            />
          )}

          {/* Repeat of the header action cluster so traders don't have to
              scroll back up after working through the form. Identical props
              to the header instance. The ``saving`` prop guards double
              submits across both. NOTE: the More menu opens downward here
              (extends the page near the bottom -- scrollable, not clipped). */}
          <div className="fef-footer-actions">
            <HeaderActions
              isCreateMode={isCreateMode}
              stage={stage}
              isTerminal={isTerminal}
              ownerGate={ownerGate}
              allowedNextStages={allowedNextStages}
              rollbackTarget={rollbackTarget}
              saving={saving}
              missingFieldsForTarget={missingFieldsForTarget}
              onSaveDraft={handleSaveDraft}
              onSubmitAndAdvance={handleSubmitAndAdvance}
              onAdvance={(target) => openStageModal('advance', target, false)}
              onRollback={() => openStageModal('rollback', null, false)}
              onMarkAsLost={() => openStageModal('lost', 'lost', true)}
              onArchive={() => openStageModal('archive', null, false)}
              isArchived={!!enquiry?.is_archived}
              onCustomerConfirmation={handleCustomerConfirmation}
              confirmationLoading={confirmation.loading}
              canDownloadConfirmation={!isCreateMode && !!enquiry?.trade_recap}
            />
          </div>
        </div>

        <BackToTopButton />
        <HamsterFloatingButton />
        <Footer />

        <StageAdvanceModal
          isOpen={stageModalState.isOpen}
          onClose={closeStageModal}
          onConfirm={runStageAction}
          action={stageModalState.action}
          fromStage={STAGE_LABEL[stage]}
          toStage={
            stageModalState.action === 'rollback'
              ? STAGE_LABEL[rollbackTarget]
              : (stageModalState.action === 'archive'
                || stageModalState.action === 'unarchive')
                ? null
                : STAGE_LABEL[stageModalState.target]
          }
          requireNotes={stageModalState.requireNotes}
          submitting={stageModalSubmitting}
          error={stageModalError}
        />

        <ContractCreatorModal
          isOpen={contractModalOpen}
          enquiry={enquiry}
          hmsEntities={hmsEntities}
          onClose={closeContractModal}
          onConfirm={runContractCreation}
          submitting={contractModalSubmitting}
          error={contractModalError}
          saleFiles={contractSaleFiles}
          purchaseFiles={contractPurchaseFiles}
          onSaleFilesChange={setContractSaleFiles}
          onPurchaseFilesChange={setContractPurchaseFiles}
        />

        <KYCBlockModal
          isOpen={kycBlockState.open}
          blockedPartners={kycBlockState.blockedPartners}
          canRequest={kycBlockState.canRequest}
          onClose={closeKycBlockModal}
          onNotify={handleNotifyKycApprover}
          notifying={kycBlockState.notifying}
          error={kycBlockState.error}
          bypassActive={Boolean(kycBypass)}
        />

        {showBackConfirmation && (
          <DeliveryBackModal
            message="Unsaved bunker enquiry data will be lost. Are you sure you want to go back?"
            onConfirm={handleConfirmBack}
            onCancel={handleCancelBack}
            onDraft={handleDraftAndLeave}
            draftLabel={isCreateMode ? 'Save Draft & Leave' : 'Save & Leave'}
          />
        )}

        {removeConfirmIdx !== null && (
          <RemoveProductConfirmModal
            productLabel={`Product ${removeConfirmIdx + 2}`}
            onCancel={() => setRemoveConfirmIdx(null)}
            onConfirm={() => {
              setFormState((prev) => ({
                ...prev,
                extra_products: (prev.extra_products || []).filter(
                  (_, i) => i !== removeConfirmIdx,
                ),
              }));
              userHasInteracted.current = true;
              setRemoveConfirmIdx(null);
            }}
          />
        )}

        {/* Customer Confirmation PDF preview (bunkers equivalent of the
          * Cargo Bid & Offer confirmation). */}
        <PdfPreviewModal
          isOpen={confirmation.open}
          pdfUrl={confirmation.url}
          fileName={confirmation.fileName}
          onClose={handleCloseConfirmation}
          onDownload={handleDownloadConfirmation}
        />

        {/* Dispatch Review & Send modal (Phase 7). Opened from the
          * outstanding-sends indicator or a dispatch notification deep-link.
          * On a successful send we re-pull the dispatch list so the row flips
          * to its sent state. */}
        <DispatchReviewSendModal
          isOpen={!!activeDispatch}
          dispatch={activeDispatch}
          onClose={() => setActiveDispatch(null)}
          onSent={() => {
            setActiveDispatch(null);
            loadDispatches();
          }}
        />

        {/* Sprint 7A: inline Stock product creation. Shared across
          * Product 1 + every extra product card via the
          * ``productCreatorState.target`` capture, which also carries the
          * card's Bunkers/Lubes toggle: a Lubes line creates a Lubrication
          * product (brand + product name), a Bunkers line an Oil/Gas one.
          * Hard-coding oil_gas here filed every lube the trader created as
          * a fuel, under the wrong naming rule. */}
        <InlineProductCreator
          isOpen={productCreatorState.isOpen}
          initialCategory={categoryForProductKind(productCreatorState.productKind)}
          initialSearchTerm={productCreatorState.searchTerm}
          initialPriceField="sale_price"
          onClose={closeProductCreator}
          onProductCreated={handleProductCreated}
        />

        {/* Inline CRM customer creator. Bunkers customers are debtors;
            seeds partner_type='Buyer (Debtor)'. The new profile lands
            as a Pending stub and is preselected on the Customer picker
            via handleCustomerChange. */}
        <InlineCrmCreator
          isOpen={crmCreatorState.isOpen}
          onClose={closeCustomerCrmCreator}
          onPartnerCreated={handleInlineCustomerCreated}
          initialLegalEntityName={crmCreatorState.searchTerm}
          initialPartnerType="Buyer (Debtor)"
          title="Create Bunker Customer (CRM)"
        />
      </div>
    </Layout>
  );
};

// =====================================================================
// Section + scaffold components
// =====================================================================

// Bootstrapping / error states share a slim scaffold: same Layout +
// gradient background as the main render, but no header / stepper /
// footer (since those depend on bootstrap data that may not exist yet).
const FormScaffold = ({ children }) => (
  <Layout showLeftNavSidebar={true} showRightSidebar={false}>
    <div className="fuel-enquiry-screen">
      <div className="fef-page-wrapper">{children}</div>
    </div>
  </Layout>
);

const Section = ({ title, children, stepNumber, anchorId, headerAction }) => (
  <section className="fef-section" id={anchorId}>
    <header className="fef-section-header">
      <h2 className="fef-section-title">
        {stepNumber !== undefined && (
          <span className="fef-section-number">{stepNumber}</span>
        )}
        {title}
      </h2>
      {headerAction}
    </header>
    <div className="fef-section-body">{children}</div>
  </section>
);

// Sticky numbered stepper (matches the Invoicing wizard's step pattern).
const FuelEnquiryStepper = ({ sections, activeId, onStepClick }) => (
  <div className="fef-stepper" role="navigation" aria-label="Form sections">
    {sections.map((s, i) => {
      const cls = [
        'fef-step',
        activeId === s.id ? 'active' : '',
        s.completed ? 'completed' : '',
      ].filter(Boolean).join(' ');
      return (
        <React.Fragment key={s.id}>
          <button
            type="button"
            className={cls}
            onClick={() => onStepClick(s.id)}
            aria-current={activeId === s.id ? 'step' : undefined}
          >
            <span className="fef-step-number" aria-hidden="true">
              {s.completed ? <FontAwesomeIcon icon={faCheck} /> : i + 1}
            </span>
            <span className="fef-step-label">{s.label}</span>
          </button>
          {i < sections.length - 1 && <div className="fef-step-line" aria-hidden="true" />}
        </React.Fragment>
      );
    })}
  </div>
);

// Happy-path mapping: which target stage is the most common forward
// transition from each non-terminal stage. The primary green button
// uses this. In the bunkers 2-screen model ``draft`` is handled by the
// "Save & Workflow" button (which always targets ``with_supply_trader``)
// instead of the generic Advance, so ``draft`` is intentionally NOT in
// this map -- ``HeaderActions`` checks ``showSaveAndWorkflow`` first.
const HAPPY_PATH = {
  with_supply_trader: 'recapped',
};

// Self-contained dropdown menu that holds alternative advance targets,
// rollback, mark-as-lost, and archive. Click-outside-to-close mirrors
// the dropdown pattern already used in TradeOperationsLayout.js.
const MoreActionsMenu = ({
  stage,
  isTerminal,
  isArchived,
  ownerGate,
  saving,
  alternateAdvanceTargets,
  rollbackTarget,
  lostAllowed,
  missingFieldsForTarget,
  onAdvance,
  onRollback,
  onMarkAsLost,
  onArchive,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const showArchive = isTerminal && !isArchived;
  const items = [];

  alternateAdvanceTargets.forEach((target) => {
    const missing = missingFieldsForTarget(target);
    const disabled = !ownerGate || missing.length > 0 || saving;
    const tooltip = !ownerGate
      ? 'Only the row owner / HOD / superuser can advance this stage.'
      : missing.length > 0
        ? `Missing required fields: ${missing.join(', ')}`
        : '';
    items.push({
      key: `advance-${target}`,
      kind: 'advance',
      icon: faArrowRight,
      label: `Advance to ${STAGE_LABEL[target]}`,
      disabled,
      tooltip,
      onClick: () => onAdvance(target),
    });
  });

  if (rollbackTarget) {
    items.push({ kind: 'divider', key: 'div-rollback' });
    items.push({
      key: 'rollback',
      kind: 'warning',
      icon: faArrowLeft,
      label: `Rollback to ${STAGE_LABEL[rollbackTarget]}`,
      disabled: !ownerGate || saving,
      tooltip: !ownerGate ? 'Only the row owner / HOD / superuser can roll back.' : '',
      onClick: onRollback,
    });
  }

  if (lostAllowed) {
    if (!rollbackTarget) items.push({ kind: 'divider', key: 'div-lost' });
    items.push({
      key: 'lost',
      kind: 'danger',
      icon: faTimes,
      label: 'Mark as Lost',
      disabled: !ownerGate || saving,
      tooltip: !ownerGate ? 'Only the row owner / HOD / superuser can mark as lost.' : '',
      onClick: onMarkAsLost,
    });
  }

  if (showArchive) {
    items.push({ kind: 'divider', key: 'div-archive' });
    items.push({
      key: 'archive',
      kind: 'danger',
      icon: faArchive,
      label: 'Archive',
      disabled: !ownerGate || saving,
      tooltip: !ownerGate ? 'Only the row owner / HOD / superuser can archive.' : '',
      onClick: onArchive,
    });
  }

  if (items.length === 0) return null;

  return (
    <div className="fef-more-menu" ref={containerRef}>
      <button
        type="button"
        className="fef-btn fef-btn-secondary fef-more-menu-toggle"
        onClick={() => setIsOpen((v) => !v)}
        disabled={saving}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        More <FontAwesomeIcon icon={faChevronDown} />
      </button>
      {isOpen && (
        <div className="fef-more-menu-dropdown" role="menu">
          {items.map((item) => (
            item.kind === 'divider'
              ? <div key={item.key} className="fef-more-menu-divider" role="separator" />
              : (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className={`fef-more-menu-item fef-more-menu-item--${item.kind}`}
                  disabled={item.disabled}
                  title={item.tooltip}
                  onClick={() => { setIsOpen(false); item.onClick(); }}
                >
                  <FontAwesomeIcon icon={item.icon} className="fef-more-menu-item-icon" />
                  {item.label}
                </button>
              )
          ))}
        </div>
      )}
    </div>
  );
};

// Header action button cluster — relocated from the bottom ActionFooter
// so it lives in the gradient header (mirrors DeliveryScreen).
//
// Layout (Option A — primary action + More dropdown):
//   [Save Draft]   [Submit & Advance | Advance to {happy path}]   [More ▾]
//
// The "More" menu collapses optional detour stages, rollback, mark-as-
// lost, and archive into a single dropdown so the footer always reads
// as one obvious next step.
const HeaderActions = ({
  isCreateMode,
  stage,
  isTerminal,
  ownerGate,
  allowedNextStages,
  rollbackTarget,
  saving,
  missingFieldsForTarget,
  onSaveDraft,
  onSubmitAndAdvance,
  onAdvance,
  onRollback,
  onMarkAsLost,
  onArchive,
  isArchived,
  onCustomerConfirmation,
  confirmationLoading,
  canDownloadConfirmation,
}) => {
  const advanceCandidates = allowedNextStages.filter((s) => s !== 'lost');
  const lostAllowed = allowedNextStages.includes('lost');
  // Bunkers 2-screen Screen 1 button. Visible on every draft, including
  // create mode -- ``handleSubmitAndAdvance`` calls ``handleSaveDraft``
  // first which materialises the row and returns the new id used for
  // the ``advance_stage`` call. Targets ``with_supply_trader`` and may
  // return a 202 if credit is short.
  const showSaveAndWorkflow = stage === 'draft'
    && (isCreateMode || allowedNextStages.includes('with_supply_trader'));

  // Pick the primary forward target. On ``draft`` the Save & Workflow
  // button covers the only legal forward (``with_supply_trader``); skip
  // the generic Advance button there to avoid two greens side by side.
  const happyTarget = !isCreateMode && !isTerminal && !showSaveAndWorkflow
    ? (HAPPY_PATH[stage] && advanceCandidates.includes(HAPPY_PATH[stage])
        ? HAPPY_PATH[stage]
        : advanceCandidates[0] || null)
    : null;

  // Anything not selected as the primary advance falls into the More
  // menu -- including the legal-but-non-primary detour stages.
  const alternateAdvanceTargets = !isCreateMode && !isTerminal
    ? advanceCandidates.filter((t) => {
        if (showSaveAndWorkflow && t === 'with_supply_trader') return false;
        if (t === happyTarget) return false;
        return true;
      })
    : [];

  const happyMissing = happyTarget ? missingFieldsForTarget(happyTarget) : [];
  const happyDisabled = !ownerGate || happyMissing.length > 0 || saving;
  const happyMissingSections = summarizeMissingSections(happyMissing);
  const happyTooltip = !ownerGate
    ? 'Only the row owner / HOD / superuser can advance this stage.'
    : happyMissing.length > 0
      ? `Missing required fields in: ${happyMissingSections.join(', ')}. `
        + 'They are marked with a red * on the form.'
      : '';

  const saveAndWorkflowMissing = showSaveAndWorkflow
    ? missingFieldsForTarget('with_supply_trader') : [];
  const saveAndWorkflowDisabled = (
    !ownerGate || saveAndWorkflowMissing.length > 0 || saving
  );
  const saveAndWorkflowMissingSections = summarizeMissingSections(
    saveAndWorkflowMissing,
  );
  const saveAndWorkflowTooltip = !ownerGate
    ? 'Only the row owner / HOD / superuser can hand this off.'
    : saveAndWorkflowMissing.length > 0
      ? `Missing required fields in: ${saveAndWorkflowMissingSections.join(', ')}. `
        + 'They are marked with a red * on the form.'
      : 'Save the enquiry and hand it off to the Supply Trader. '
        + 'If credit is short, a Credit Increase Request is opened '
        + 'and the enquiry stays on Draft until it is approved.';

  return (
    <div className="fef-header-actions">
      {!isArchived && (
        <button
          type="button"
          className="fef-btn fef-btn-secondary"
          onClick={onSaveDraft}
          disabled={saving}
        >
          {saving
            ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
            : <><FontAwesomeIcon icon={faSave} /> Save Draft</>
          }
        </button>
      )}

      {/* Customer Confirmation — mirrors the Cargo Bid & Offer confirmation.
          Per IMT, it's "linked to the Sales Recap": the button only enables
          once the enquiry is recapped (``trade_recap`` populated), which is
          also when the document is complete (supplier filled + the recap's
          real contract number available). Shown-but-disabled before then so
          traders can see what unlocks it, matching the Save & Workflow gate. */}
      {!isCreateMode && (
        <button
          type="button"
          className="fef-btn fef-btn-secondary"
          onClick={onCustomerConfirmation}
          disabled={confirmationLoading || !canDownloadConfirmation}
          title={canDownloadConfirmation
            ? 'Generate the Customer Confirmation PDF for this enquiry.'
            : 'Available once the enquiry is recapped (linked to a Sales Recap).'}
        >
          {confirmationLoading
            ? <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
            : <><FontAwesomeIcon icon={faFileContract} /> Customer Confirmation</>
          }
        </button>
      )}

      {showSaveAndWorkflow && (
        <button
          type="button"
          className="fef-btn fef-btn-success"
          onClick={onSubmitAndAdvance}
          disabled={saveAndWorkflowDisabled}
          title={saveAndWorkflowTooltip}
        >
          <FontAwesomeIcon icon={faPaperPlane} /> Save &amp; Workflow
        </button>
      )}

      {happyTarget && (
        <button
          type="button"
          className="fef-btn fef-btn-success"
          disabled={happyDisabled}
          title={happyTooltip}
          onClick={() => onAdvance(happyTarget)}
        >
          <FontAwesomeIcon icon={faArrowRight} /> Advance to {STAGE_LABEL[happyTarget]}
        </button>
      )}

      {!isCreateMode && (
        <MoreActionsMenu
          stage={stage}
          isTerminal={isTerminal}
          isArchived={isArchived}
          ownerGate={ownerGate}
          saving={saving}
          alternateAdvanceTargets={alternateAdvanceTargets}
          rollbackTarget={rollbackTarget}
          lostAllowed={lostAllowed}
          missingFieldsForTarget={missingFieldsForTarget}
          onAdvance={onAdvance}
          onRollback={onRollback}
          onMarkAsLost={onMarkAsLost}
          onArchive={onArchive}
        />
      )}
    </div>
  );
};

const Field = ({ label, error, children, hint, required = false }) => (
  <div className={`fef-field ${error ? 'fef-field-error' : ''}`}>
    <label className="fef-label">
      {label}
      {required && <span className="fef-label-required"> *</span>}
    </label>
    {children}
    {hint && !error && <div className="fef-field-hint">{hint}</div>}
    {error && <div className="fef-field-error-text">{error}</div>}
  </div>
);

// Bunkers Phase 1 (May 2026): Fixed | Floating pricing-basis toggle. Renders
// as its own centered, full-width row below the product spec grid.
//
// NO LONGER DEFAULTS TO FIXED (2026-09-09, Chris). It used to open with Fixed
// already selected, which meant a deliberate fixed price and a toggle nobody
// touched looked identical for ever after -- on the live book, 83 of 90
// enquiries read fixed/fixed with no way to tell how many were real. It now
// opens with NEITHER chosen, and the subtext says why the answer is wanted
// rather than merely insisting on it. The stage gate is what enforces the
// requirement; this is the half that makes it understandable.
export const PricingTypeToggle = ({ value, onChange }) => {
  const chosen = value === 'fixed' || value === 'floating';
  return (
    <div className="fef-pricing-toggle-wrap">
      <span className="fef-pricing-label">
        Pricing Type <span className="fef-required-mark" aria-hidden="true">*</span>
      </span>
      <div className="fef-pricing-toggle" role="group" aria-label="Pricing type">
        {['fixed', 'floating'].map((opt) => (
          <button
            key={opt}
            type="button"
            className={`fef-pricing-toggle-btn${value === opt ? ' is-active' : ''}`}
            aria-pressed={value === opt}
            onClick={() => onChange(opt)}
          >
            {opt === 'fixed' ? 'Fixed' : 'Floating'}
          </button>
        ))}
      </div>
      {!chosen && (
        <span className="fef-pricing-hint">
          Required so the hedging desk is told when the buy and sell sides do not match.
        </span>
      )}
    </div>
  );
};

// The commission is a per-unit rate, so it carries a price's decimals: a 7th
// is refused at the keystroke rather than refused by the server on save.
const COMMISSION_RATE_GUARD = new RegExp(`^\\d*\\.?\\d{0,${PRICE_DECIMALS}}$`);

// Commissions rework (June 2026): structured sell-side commission box.
// Replaces the free-text Commissions input as the last block on each
// Product card. The Counter Party is the CRM partner the commission is
// owed to; the amount is a USD-per-MT rate. On save the backend mirrors
// the pair into a locked "Commission Sales" row under 3rd Party Costs on
// Screen 2 (cost = amount × MAX tonnage, USD, no charge). Buy-side
// commissions are entered manually there under "Commission Purchase".
const CounterPartyCommissionBox = ({
  counterPartyId,
  counterPartyDisplayName,
  amount,
  maxTonnage,
  onCounterPartyProfileChange,
  onAmountChange,
  // What the line's quantity is counted in (``lineQuantityUnit``): 'mt' on a
  // fuel line, the pack ("DRUM - 209 LTR") on a lubes line. The rate is per
  // that unit, because the cost is rate x the line's quantity.
  unitText = 'mt',
  isLubes = false,
}) => {
  let rateHint = 'Rate per metric tonne, USD.';
  if (isLubes) {
    rateHint = unitText ? `Rate per ${unitText}, USD.` : 'Rate per measuring unit, USD.';
  }
  const quantityWord = isLubes ? 'quantity' : 'MAX tonnage';
  // Stub blob for the picker chip; mirrors the supplier-picker pattern
  // ({id, legal_entity_name}) so a saved selection renders on re-open.
  const selectedProfile = counterPartyId
    ? { id: counterPartyId, legal_entity_name: counterPartyDisplayName || '' }
    : null;
  // Live preview of the auto line so the trader sees the consequence of
  // the rate while typing, not after saving.
  const rate = parseFloat(amount);
  const max = parseFloat(maxTonnage);
  const previewCost = Number.isFinite(rate) && Number.isFinite(max)
    ? rate * max
    : null;
  return (
    <>
      <div className="fef-grid fef-grid-2">
        <Field
          label="Counter Party"
          hint="CRM partner the sales commission is owed to."
        >
          <CustomerPicker
            customer={selectedProfile}
            onChange={onCounterPartyProfileChange}
            allowUnlinked={false}
            searchParams={{}}
            placeholder="Search CRM partners..."
            noResultsCopy="No matching CRM partners."
          />
        </Field>
        <Field
          label={perUnitLabel('Commission', 'USD', unitText)}
          hint={rateHint}
        >
          {/* type="text" + inputMode + regex guard instead of type="number":
              Safari lets arbitrary text through number inputs, and React
              controlled inputs won't snap it back — so non-numeric keystrokes
              are rejected here before they reach form state. */}
          <input
            type="text"
            inputMode="decimal"
            value={amount ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || COMMISSION_RATE_GUARD.test(v)) onAmountChange(v);
            }}
            placeholder="e.g. 2.50"
          />
        </Field>
      </div>

      {previewCost != null && counterPartyId && (
        <>
          <div className="fef-display-row">
            <div className="fef-display-row-label">Commission Sales line</div>
            <div className="fef-display-row-value">
              {previewCost.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} USD
            </div>
          </div>
          <div className="fef-credit-inline-hint" role="note">
            <FontAwesomeIcon icon={faCircleInfo} />
            <span>
              On save, this amount (commission × {quantityWord}) is added as a
              locked &quot;Commission Sales&quot; line under 3rd Party Costs.
              It is paid to the counter party and not charged to the
              customer.
            </span>
          </div>
        </>
      )}

      {previewCost != null && !counterPartyId && (
        <div className="fef-credit-inline-hint" role="note">
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>
            Pick a Counter Party to add this commission ({previewCost.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} USD) as a Commission Sales line under 3rd Party Costs on save.
          </span>
        </div>
      )}
    </>
  );
};

export const SectionIMT = ({
  formState, setField, branches, hmsEntities, traders, savedTraders = {}, validationErrors, stepNumber, anchorId,
}) => {
  const branchManagerName = useMemo(() => {
    if (!formState.imt_branch) return '';
    const branch = branches.find((b) => b.id === Number(formState.imt_branch));
    return branch?.branch_manager_name || '';
  }, [formState.imt_branch, branches]);

  return (
    <Section title="IMT" stepNumber={stepNumber} anchorId={anchorId}>
      <div className="fef-grid fef-grid-3">
        <Field
          label="Customer Trader"
          error={validationErrors.customer_trader}
          required
          hint={traderBoxNote(traders, 'customer')}
        >
          <select
            value={formState.customer_trader || ''}
            onChange={(e) => setField('customer_trader', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select a trader...</option>
            <TraderOptions traders={traders} box="customer" saved={savedTraders.customer} />
          </select>
        </Field>

        <Field
          label="Supplier Trader"
          error={validationErrors.supplier_trader}
          required
          hint={traderBoxNote(traders, 'supplier')}
        >
          <select
            value={formState.supplier_trader || ''}
            onChange={(e) => setField('supplier_trader', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select a trader...</option>
            <TraderOptions traders={traders} box="supplier" saved={savedTraders.supplier} />
          </select>
        </Field>

        <Field label="Enquiry Date" error={validationErrors.enquiry_date} required>
          <DatePicker
            id="enquiry_date"
            name="enquiry_date"
            value={formState.enquiry_date || ''}
            onChange={(e) => setField('enquiry_date', e.target.value)}
            required
            format="YYYY-MM-DD"
            placeholder="Select date"
          />
        </Field>
      </div>

      <div className="fef-grid fef-grid-3">
        <Field label="IMT Branch" error={validationErrors.imt_branch} required>
          <select
            value={formState.imt_branch || ''}
            onChange={(e) => setField('imt_branch', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select a branch...</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Branch Manager (auto)" hint="Auto-fills from IMT Branch">
          <input
            type="text"
            className="fef-input fef-input-readonly"
            value={branchManagerName || '—'}
            readOnly
          />
        </Field>

        <Field label="IMT Sell Company" error={validationErrors.hms_sell_company} required>
          <select
            value={formState.hms_sell_company || ''}
            onChange={(e) => setField('hms_sell_company', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select an entity...</option>
            {hmsEntities.map((entity) => (
              <option key={entity.id} value={entity.id}>{entity.hms_entity_name}</option>
            ))}
          </select>
        </Field>
      </div>
    </Section>
  );
};

const SectionCustomer = ({
  formState,
  setField,
  customerProfile,
  onCustomerChange,
  showUnlinked,
  onUnlinkedToggle,
  validationErrors,
  stepNumber,
  anchorId,
  onCreateCustomer,
  extraVisibleProfiles = [],
}) => (
  <Section title="Customer" stepNumber={stepNumber} anchorId={anchorId}>
    <Field label="Customer" error={validationErrors.customer} required>
      <CustomerPicker
        customer={customerProfile}
        onChange={onCustomerChange}
        showUnlinked={showUnlinked}
        onUnlinkedToggle={onUnlinkedToggle}
        onCreatePartner={onCreateCustomer}
        extraVisibleProfiles={extraVisibleProfiles}
      />
    </Field>

    {customerProfile && !showUnlinked && (
      <div className="fef-grid fef-grid-2 fef-mirror-grid">
        <Field label="Account #">
          <div className="fef-mirror-value">
            {customerProfile.monday_account_number || '—'}
          </div>
        </Field>
        <Field label="Credit Days">
          <div className="fef-mirror-value">
            {customerProfile.partner_credit_days ?? '—'}
          </div>
        </Field>
      </div>
    )}

    {showUnlinked && (
      <div className="fef-grid fef-grid-3">
        <Field
          label="Unlinked Customer Name"
          error={validationErrors.unlinked_customer_name}
          required
          hint="The CRM team will link this to a CRMProfile after submission."
        >
          <input
            type="text"
            value={formState.unlinked_customer_name}
            onChange={(e) => setField('unlinked_customer_name', e.target.value)}
            maxLength={255}
          />
        </Field>
        <Field label="Contact Email">
          <input
            type="email"
            value={formState.unlinked_customer_email}
            onChange={(e) => setField('unlinked_customer_email', e.target.value)}
          />
        </Field>
        <Field label="Contact Person Name">
          <input
            type="text"
            value={formState.unlinked_customer_contact_name}
            onChange={(e) => setField('unlinked_customer_contact_name', e.target.value)}
            maxLength={255}
          />
        </Field>
      </div>
    )}
  </Section>
);

// Searchable picker for the bunker-port list. The vanilla <select> works
// fine for ~10 options but the bunker-ports table has hundreds of rows,
// so traders end up scroll-hunting. This component renders a text input
// with a filtered dropdown -- type any part of the port name, UN/LOCODE,
// or country and click to select. Mirrors the click-outside-to-close
// pattern from CustomerPicker / MoreActionsMenu so it stays consistent
// with the rest of the form.
const PortPicker = ({ value, ports, onChange }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selected = useMemo(
    () => (value ? (ports || []).find((p) => p.id === Number(value)) || null : null),
    [value, ports],
  );
  const displayValue = selected
    ? `${selected.port_name} (${selected.unlocode}) — ${selected.country_name}`
    : '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ports || [];
    return (ports || []).filter((p) => (
      (p.port_name || '').toLowerCase().includes(q)
      || (p.unlocode || '').toLowerCase().includes(q)
      || (p.country_name || '').toLowerCase().includes(q)
    ));
  }, [search, ports]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (port) => {
    onChange(port ? port.id : null);
    setSearch('');
    setOpen(false);
  };

  return (
    <div className="fef-port-picker" ref={wrapperRef}>
      <div className="fef-port-picker-input-row">
        <input
          type="text"
          value={open ? search : displayValue}
          placeholder="Search by port name, UN/LOCODE, or country..."
          onFocus={() => { setOpen(true); setSearch(''); }}
          onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
        />
        {selected && !open && (
          <button
            type="button"
            className="fef-port-picker-clear"
            onClick={() => handleSelect(null)}
            title="Clear port"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>
      {open && (
        <div className="fef-port-picker-dropdown" role="listbox">
          {filtered.length === 0 ? (
            <div className="fef-port-picker-empty">No matching ports.</div>
          ) : (
            filtered.slice(0, 100).map((p) => (
              <button
                key={p.id}
                type="button"
                role="option"
                className={`fef-port-picker-option${selected && selected.id === p.id ? ' fef-port-picker-option--selected' : ''}`}
                onClick={() => handleSelect(p)}
              >
                <span className="fef-port-picker-name">{p.port_name}</span>
                <span className="fef-port-picker-meta">({p.unlocode}) — {p.country_name}</span>
              </button>
            ))
          )}
          {filtered.length > 100 && (
            <div className="fef-port-picker-more">
              Showing first 100 of {filtered.length} matches — refine your search.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Map NavAPI's coarse AIS ship-type category onto the curated fuels
// VesselType lookup. NavAPI's ``ship_type`` is a fixed category set
// ("Tanker", "Cargo", "Fishing", "Passenger", ...); only the entries
// that map confidently 1:1 are listed here. Anything ambiguous
// (Passenger, Special Purpose, Other, Unknown) is left for the trader.
const NAVAPI_SHIP_TYPE_TO_VESSEL_TYPE = {
  tanker: 'Tanker',
  cargo: 'General Cargo',
  fishing: 'Fishing',
};

// Resolve a search result's ``ship_type`` to a curated VesselType id, or
// null when there's no confident match. Tries the explicit category map
// first, then falls back to a direct case-insensitive name match.
// Exported for the recap forms (TradeRecap ship details), which share the
// same VesselType lookup list.
export const matchVesselTypeId = (shipType, vesselTypes) => {
  if (!shipType) return null;
  const key = shipType.trim().toLowerCase();
  const target = NAVAPI_SHIP_TYPE_TO_VESSEL_TYPE[key] || shipType;
  const normalized = target.trim().toLowerCase();
  const hit = (vesselTypes || []).find(
    (t) => (t.name || '').trim().toLowerCase() === normalized,
  );
  return hit ? hit.id : null;
};

const SectionShipDetails = ({
  formState,
  setField,
  handleEventInput,
  vesselTypes,
  bunkerPorts,
  onPortChange,
  validationErrors,
  stepNumber,
  anchorId,
}) => {
  // Build an alphabetical set of port options. Bunker port lists can be
  // ~1,000 rows; we keep a single <select> for simplicity. Future polish
  // can swap this for a search-as-you-type combobox.
  const portOptions = useMemo(() => {
    const sorted = [...bunkerPorts].sort((a, b) => {
      const ca = (a.country_name || '').localeCompare(b.country_name || '');
      if (ca !== 0) return ca;
      return (a.port_name || '').localeCompare(b.port_name || '');
    });
    return sorted;
  }, [bunkerPorts]);

  // Derive a deduplicated, alphabetical country list from the bunker port
  // dataset. Using the same source as the Port picker guarantees the
  // auto-fill-from-port value is always selectable in the dropdown.
  const countryOptions = useMemo(() => {
    const unique = new Set();
    for (const p of bunkerPorts) {
      const name = (p.country_name || '').trim();
      if (name) unique.add(name);
    }
    return [...unique].sort((a, b) => a.localeCompare(b));
  }, [bunkerPorts]);

  const currentCountry = (formState.country || '').trim();
  const currentCountryIsKnown =
    !currentCountry || countryOptions.includes(currentCountry);

  // NavAPI vessel search — same component the Deliveries form uses
  // (``DeliveryForm.js`` -> ``handleVesselSearchSelect``). When a vessel
  // is picked, auto-populate vessel_name + imo_number so the trader
  // doesn't have to retype data the maritime DB already has, and
  // best-effort auto-select ``vessel_type`` from the search result's
  // coarse AIS category (e.g. "Tanker"). Only confident matches fill;
  // the dropdown stays editable so the trader can override.
  const handleVesselSelected = (vessel) => {
    if (!vessel) return;
    if (vessel.ship_name) setField('vessel_name', vessel.ship_name);
    if (vessel.imo) setField('imo_number', vessel.imo);
    const typeId = matchVesselTypeId(vessel.ship_type, vesselTypes);
    if (typeId != null) setField('vessel_type', typeId);
  };

  // Agent CRM link (Item #9): mirror the supplier flow — persist BOTH the FK
  // id and the display name so the picker chip renders on load without a CRM
  // list fetch. Clearing the picker leaves the free-text fallback untouched.
  const handleAgentProfileChange = (profile) => {
    setField('shipping_agent_profile', profile ? profile.id : null);
    setField(
      'shipping_agent_profile_name',
      profile
        ? (profile.legal_entity_name || profile.trading_short_name || '')
        : '',
    );
  };
  const selectedAgentProfile = formState.shipping_agent_profile
    ? {
        id: formState.shipping_agent_profile,
        legal_entity_name: formState.shipping_agent_profile_name || '',
      }
    : null;

  return (
    <Section title="Ship Details" stepNumber={stepNumber} anchorId={anchorId}>
      <div className="fef-grid fef-grid-2">
        <Field
          label="Vessel Name"
          error={validationErrors.vessel_name}
          hint="Search by name, IMO, MMSI or call sign — picks from live maritime data."
        >
          <VesselSearchAutocomplete
            initialValue={formState.vessel_name || ''}
            onSelect={handleVesselSelected}
            placeholder="Search vessel by Name, IMO, MMSI or Call Sign..."
          />
        </Field>
        <Field label="Vessel Type" error={validationErrors.vessel_type}>
          <select
            value={formState.vessel_type || ''}
            onChange={(e) => setField('vessel_type', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select vessel type...</option>
            {vesselTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Field>
      </div>

      <div className="fef-grid fef-grid-2">
        <Field label="IMO Number" hint="Auto-fills from the vessel search. Editable — TBC / PENDING is fine.">
          <input
            type="text"
            value={formState.imo_number}
            onChange={(e) => setField('imo_number', e.target.value)}
            maxLength={50}
            placeholder="e.g. 9876543 (or TBC / PENDING)"
          />
        </Field>
        <Field label="Port" error={validationErrors.port} hint="Bunker ports only. Search by name, UN/LOCODE, or country.">
          <PortPicker
            value={formState.port}
            ports={portOptions}
            onChange={(portId) => onPortChange(portId ?? '')}
          />
        </Field>
      </div>

      <div className="fef-grid fef-grid-2">
        <Field
          label="Country"
          hint={
            currentCountryIsKnown
              ? 'Auto-fills from Port — pick from the list to override.'
              : `Legacy value "${currentCountry}" isn't in the bunker-port list. Re-select to standardise.`
          }
        >
          <select
            value={formState.country || ''}
            onChange={(e) => setField('country', e.target.value)}
          >
            <option value="">Select a country...</option>
            {!currentCountryIsKnown && (
              <option value={currentCountry}>{currentCountry} (legacy)</option>
            )}
            {countryOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </Field>
        <Field
          label="Expected Delivery Window"
          error={validationErrors.expected_delivery_start || validationErrors.expected_delivery_end}
          hint="Pick a start and end date in one calendar."
        >
          <DateRangePicker
            startDateName="expected_delivery_start"
            endDateName="expected_delivery_end"
            startDateValue={formState.expected_delivery_start || ''}
            endDateValue={formState.expected_delivery_end || ''}
            onChange={handleEventInput}
            placeholder="Select delivery window..."
          />
        </Field>
      </div>

      <div className="fef-grid fef-grid-2">
        <Field
          label="Agent"
          hint="Appointed shipping / port agent. Link a CRM partner, or type a name below if they have no CRM profile. Shown on the Customer Confirmation."
        >
          <CustomerPicker
            customer={selectedAgentProfile}
            onChange={handleAgentProfileChange}
            allowUnlinked={false}
            searchParams={{}}
            placeholder="Search CRM partners..."
            noResultsCopy="No matching CRM partners. Type a name in the fallback field instead."
          />
        </Field>
        <Field
          label="Agent (unlinked / free text)"
          hint="Use when the agent has no CRM profile. Falls back onto the Customer Confirmation when no partner is linked above."
        >
          <input
            type="text"
            value={formState.shipping_agent || ''}
            onChange={(e) => setField('shipping_agent', e.target.value)}
            maxLength={255}
            placeholder="e.g. Wilhelmsen Ships Service (or TBC)"
          />
        </Field>
      </div>
    </Section>
  );
};

const SectionCreditBalances = ({ customerProfile, enquiry, creditRequest, stepNumber, anchorId }) => {
  // The detail serializer's mirrors are the most authoritative: they
  // reflect the live CRMProfile balance even if the local customerProfile
  // blob is stale. Fall back to customerProfile when we don't have a
  // saved enquiry yet (create mode).
  // Per-enquiry credit rework (July 2026): the balance indicator is computed
  // against the EFFECTIVE limit (base + active enquiry uplifts), so surface the
  // effective limit as "Credit Limit" (falling back to base / the stale
  // customerProfile blob in create mode). ``base``/``uplifts`` drive the
  // one-line breakdown shown only when uplifts are active.
  const creditLimit = enquiry?.customer_effective_credit_limit
    ?? enquiry?.customer_credit_limit
    ?? customerProfile?.credit_limit;
  const baseCreditLimit = enquiry?.customer_base_credit_limit
    ?? enquiry?.customer_credit_limit
    ?? customerProfile?.credit_limit;
  const activeUpliftsTotal = Number(enquiry?.customer_active_uplifts_total || 0);
  const creditBalance = enquiry?.customer_credit_balance ?? customerProfile?.credit_balance;

  // The indicator must reflect projected headroom AFTER this enquiry's
  // credit_request is taken into account, not the partner's raw
  // pre-enquiry headroom. Bunkers 2-screen model: allocation fires when
  // the enquiry enters ``with_supply_trader`` and stays through
  // ``recapped`` (per credit_service in-allocation list). ``draft``
  // hasn't been allocated yet, so we project by adding the request.
  const ALREADY_ALLOCATED_STAGES = new Set([
    'with_supply_trader',
    'recapped',
  ]);
  const balanceAlreadyIncludesEnquiry =
    enquiry?.stage && ALREADY_ALLOCATED_STAGES.has(enquiry.stage);

  const projectedBalance =
    creditBalance != null
      ? Number(creditBalance) -
        (balanceAlreadyIncludesEnquiry ? 0 : Number(creditRequest || 0))
      : null;
  const projectedIsNegative = projectedBalance != null && projectedBalance < 0;
  const shortfall = projectedIsNegative ? Math.abs(projectedBalance) : 0;

  // Latest CreditIncreaseRequest tied to this enquiry (deal_reference ==
  // enquiry.display_id). Surfaces approval feedback so the trader sees
  // pending/approved/rejected state without leaving the form.
  const latestRequest = enquiry?.latest_credit_request || null;
  const requestStatus = latestRequest?.status || null;

  return (
    <Section title="Credit Balances" stepNumber={stepNumber} anchorId={anchorId}>
      <div className="fef-grid fef-grid-3 fef-mirror-grid">
        <Field
          label="Credit Limit"
          hint="Effective limit (base + active enquiry uplifts)"
        >
          <div className="fef-mirror-value">
            {creditLimit != null ? formatCurrency(creditLimit) : '—'}
          </div>
          {activeUpliftsTotal > 0 && baseCreditLimit != null && (
            <div className="fef-credit-breakdown">
              Base {formatCurrency(baseCreditLimit)} + uplifts{' '}
              {formatCurrency(activeUpliftsTotal)}
            </div>
          )}
        </Field>
        <Field
          label="Credit Balance Indicator"
          hint="Limit minus current allocations and this enquiry's credit request."
        >
          <div
            className="fef-mirror-value"
            data-negative={projectedIsNegative ? 'true' : 'false'}
          >
            {projectedBalance == null
              ? '—'
              : projectedBalance === 0
                ? '$0'
                : formatCurrency(projectedBalance)}
          </div>
        </Field>
        <Field label="Enquiry Credit Request">
          <div className="fef-mirror-value fef-credit-request">
            {creditRequest != null ? formatCurrency(creditRequest) : '—'}
          </div>
        </Field>
      </div>

      {/* Inline hints explaining what (if anything) will be requested.
          PM (May 2026): credit is only requested when needed — not on every
          deal. The trigger fires on stage advance to Enquiry Review, NOT on
          draft save. Two variants below:
            - amber  (.fef-credit-inline-hint)    -> deal exceeds limit
            - green  (.fef-credit-inline-hint-ok) -> deal fits, no request
          Both suppress while a CIR is already pending review (avoid noise). */}
      {projectedIsNegative && requestStatus !== 'pending' && (
        <div className="fef-credit-inline-hint" role="status">
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>
            This enquiry exceeds available credit by{' '}
            <strong>{formatCurrency(shortfall)}</strong>. Advancing the enquiry
            will automatically submit a credit increase request for approval.
            Deals that fit within the customer's credit limit skip this step.
          </span>
        </div>
      )}

      {creditLimit != null
        && !projectedIsNegative
        && Number(creditRequest || 0) > 0
        && requestStatus !== 'pending' && (
        <div className="fef-credit-inline-hint fef-credit-inline-hint-ok" role="status">
          <FontAwesomeIcon icon={faCircleCheck} />
          <span>
            This enquiry fits within the customer's available credit
            ({formatCurrency(projectedBalance)} headroom remaining after this
            deal). No credit increase request will be raised — credit is only
            requested when an enquiry exceeds the customer's limit.
          </span>
        </div>
      )}

      {/* Approval feedback banner — pending / approved / rejected. */}
      {latestRequest && (
        <CreditRequestStatusBanner request={latestRequest} />
      )}
    </Section>
  );
};

const CreditRequestStatusBanner = ({ request }) => {
  const status = request?.status;
  if (!status) return null;
  const amount = request.requested_amount != null
    ? formatCurrency(Number(request.requested_amount))
    : null;
  const reviewer = request.reviewed_by_name || 'a credit approver';
  const reviewedAt = request.reviewed_at
    ? formatDateTime(request.reviewed_at)
    : null;
  const submittedAt = request.created_at
    ? formatDateTime(request.created_at)
    : null;

  if (status === 'pending') {
    return (
      <div className="fef-credit-banner fef-credit-banner--pending" role="status">
        <FontAwesomeIcon icon={faClock} />
        <div>
          <strong>Credit increase request pending.</strong>{' '}
          {amount ? <>Requested <strong>{amount}</strong>{' '}increase</> : 'Awaiting approval'}
          {submittedAt ? <> on {submittedAt}</> : null}. Awaiting review by a
          credit approver.
        </div>
      </div>
    );
  }
  if (status === 'approved') {
    return (
      <div className="fef-credit-banner fef-credit-banner--approved" role="status">
        <FontAwesomeIcon icon={faCircleCheck} />
        <div>
          <strong>Credit increase APPROVED.</strong>{' '}
          {amount ? <>Limit increased by <strong>{amount}</strong>{' '}</> : ''}
          by {reviewer}{reviewedAt ? <> on {reviewedAt}</> : null}.
          {request.review_notes ? (
            <p className="fef-credit-banner-notes">{request.review_notes}</p>
          ) : null}
        </div>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="fef-credit-banner fef-credit-banner--rejected" role="alert">
        <FontAwesomeIcon icon={faCircleXmark} />
        <div>
          <strong>Credit increase REJECTED</strong> by {reviewer}
          {reviewedAt ? <> on {reviewedAt}</> : null}. Adjust the deal (e.g.
          reduce tonnage) or save again to submit a new request.
          {request.review_notes ? (
            <p className="fef-credit-banner-notes">
              <em>Reviewer's note:</em> {request.review_notes}
            </p>
          ) : null}
        </div>
      </div>
    );
  }
  return null;
};

// Catalogue snapshot pill that sits directly under a stock-variant
// picker. Surfaces the variant's on-hand quantity + per-unit catalogue
// sale price so the trader can sanity-check the per-MT ``Sell Price``
// they're about to enter. Renders nothing when no variant is picked
// or the snapshot couldn't be hydrated.
const VariantCatalogueHint = ({ meta }) => {
  if (!meta) return null;
  const hasQty = meta.quantity != null && meta.unit_name;
  const hasPrice = meta.sale_price != null;
  if (!hasQty && !hasPrice) return null;
  // ``quantity`` and prices come back as strings from DRF; coerce so
  // toFixed works and we don't print "200.00.00".
  const qtyNum = hasQty ? Number(meta.quantity) : null;
  const priceNum = hasPrice ? Number(meta.sale_price) : null;
  const qtyLabel = meta.quantity_is_live ? 'on hand' : 'spec qty';
  const qtyText = hasQty && Number.isFinite(qtyNum)
    ? `${qtyNum % 1 === 0 ? qtyNum.toFixed(0) : formatFigure(meta.quantity, 2)} \u00d7 ${meta.unit_name} ${qtyLabel}`
    : null;
  const priceText = hasPrice && Number.isFinite(priceNum)
    ? `catalogue sale price ${formatFigure(meta.sale_price, 2)} ${meta.price_currency}`
      + (meta.unit_name ? ` / ${meta.unit_name}` : '')
    : null;
  const parts = [qtyText, priceText].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <div className="fef-prod-hint" role="note" style={{ marginTop: '8px' }}>
      <FontAwesomeIcon icon={faCircleInfo} className="fef-prod-hint-icon" />
      <div>
        <strong>Stock catalogue:</strong> {parts.join(' \u00b7 ')}.{' '}
        Reference only -- enter the per-MT figures below for this deal.
      </div>
    </div>
  );
};

// Only Lubrication-category variants are pickable on a lubes line. Passing a
// coal or fuel variant here would let a trader sell a parcel of steam coal as a
// drum of lube.
const LUBES_VARIANT_FILTER = (variant) => !!variant && variant.category === 'lubrication';

/**
 * Bunkers | Lubes switch at the top of a Product card.
 *
 * Swapping it changes which questions the card asks -- a marine fuel spec, or a
 * product picked off the Lubrication catalogue. It does NOT clear the other
 * side's values: a trader who flips by accident must be able to flip back and
 * find their work. The stage gate only reads the fields belonging to the kind
 * that is currently selected, so the dormant half is ignored, not enforced.
 */
const ProductKindToggle = ({ value, onChange, disabled }) => (
  <div className="fef-kind-toggle" role="group" aria-label="Product type">
    {[
      { key: 'bunkers', label: 'Bunkers' },
      { key: 'lubes', label: 'Lubes' },
    ].map((opt) => (
      <button
        key={opt.key}
        type="button"
        className={`fef-kind-toggle-btn ${value === opt.key ? 'active' : ''}`}
        aria-pressed={value === opt.key}
        onClick={() => onChange(opt.key)}
        disabled={disabled}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

/**
 * The catalogue picker a lubes line uses instead of the four fuel dropdowns.
 * The picked product IS the spec, so this is the one field a lubes line cannot
 * leave blank (see ``LUBES_SPEC_FIELDS``).
 */
const LubesProductPicker = ({ value, variantId, onPickerChange, onCreateProduct, error, variantMeta }) => (
  <Field label="Lube Product" error={error}>
    <RecapProductSearch
      label=""
      value={value || ''}
      variantId={variantId || null}
      onChange={onPickerChange}
      onCreateProduct={onCreateProduct}
      filterFunction={LUBES_VARIANT_FILTER}
      placeholder="Search brand, product name or code..."
      disabled={false}
    />
    {/* Catalogue quantity/price for the picked product, as a sanity check
      * against what the trader is about to type for this deal. */}
    <VariantCatalogueHint meta={variantMeta} />
  </Field>
);

export const SectionProd1 = ({
  formState,
  setField,
  lookups,
  contractShorthand,
  validationErrors,
  stepNumber,
  anchorId,
  variantMeta,
  onPickerChange,
  onCreateProduct,
}) => {
  // Per-card credit request for Product 1 only (sell_price * max_tonnage).
  // The cross-product total is shown separately on the Credit Balances
  // section ("Enquiry Credit Request") and at the bottom of the form
  // ("Total Revenue") so each product card stays scoped to its own row.
  const rowCreditRequest = (() => {
    const sell = parseFloat(formState.sell_price);
    const max = parseFloat(formState.max_tonnage);
    if (!Number.isFinite(sell) || !Number.isFinite(max)) return null;
    return sell * max;
  })();
  const isLubes = isLubesLine(formState);
  // 'mt' on a fuel line; the pack ("DRUM - 209 LTR") on a lubes line.
  const quantityUnit = lineQuantityUnit(formState, lookups.measuringUnits);

  return (
  <Section title="Product" stepNumber={stepNumber} anchorId={anchorId}>
    {/* Multi-product workflow (Sprint 6). The first product is captured
     * via the inline fields below. Use the "+ Add another product"
     * button beneath this card to add a 2nd, 3rd, ... product to the
     * same enquiry. */}
    <ProductKindToggle
      value={formState.product_kind || 'bunkers'}
      onChange={(kind) => setField('product_kind', kind)}
    />

    {/* A lube has no sulfur limit and no ISO 8217 year. Asking for them made
      * traders invent three values that then printed on the counterparty's
      * confirmation, so on a lubes line the four fuel dropdowns are replaced by
      * the catalogue product itself. */}
    {(formState.product_kind || 'bunkers') === 'lubes' ? (
      /* Kept inside the same 4-column grid as the fuel fields it replaces --
       * rendered bare it stretched the full width of the card and broke the
       * rhythm of every other row. Two columns, because a lube name plus its
       * code ("TOTAL LUBMARINE TOTAL AURELIA TI 3030 (TMTPE330)") does not
       * read in a quarter-width box. */
      <div className="fef-grid fef-grid-4">
        <div className="fef-span-2">
          <LubesProductPicker
            value={formState.stock_item_variant_display}
            variantId={formState.stock_item_variant}
            onPickerChange={onPickerChange}
            onCreateProduct={onCreateProduct}
            error={validationErrors.stock_item_variant}
            variantMeta={variantMeta}
          />
        </div>
      </div>
    ) : (
    <div className="fef-grid fef-grid-4">
      <Field label="Bunker Type" error={validationErrors.fuel_type}>
        <select
          value={formState.fuel_type || ''}
          onChange={(e) => setField('fuel_type', e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Select...</option>
          {lookups.fuelTypes.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </Field>
      <Field label="Sulfur Limit" error={validationErrors.sulfur_limit}>
        <select
          value={formState.sulfur_limit || ''}
          onChange={(e) => setField('sulfur_limit', e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Select...</option>
          {lookups.sulfurLimits.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </Field>
      <Field label="Bunker Grade" error={validationErrors.fuel_grade}>
        <select
          value={formState.fuel_grade || ''}
          onChange={(e) => setField('fuel_grade', e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Select...</option>
          {lookups.fuelGrades.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </Field>
      <Field label="ISO 8217 Year" error={validationErrors.iso_8217_year}>
        <select
          value={formState.iso_8217_year || ''}
          onChange={(e) => setField('iso_8217_year', e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Select...</option>
          {lookups.iso8217Years.map((t) => (
            <option key={t.id} value={t.id}>{t.year}</option>
          ))}
        </select>
      </Field>
    </div>

    )}

    <div className="fef-display-row">
      <div className="fef-display-row-label">Contract Shorthand</div>
      <div className="fef-display-row-value">
        {/* ``productShorthand`` names a lubes line by its catalogue product
          * and a fuel line by its spec -- the same name Supplier Pricing
          * shows for this product, and the server's ``contract_shorthand``. */}
        {contractShorthand || (
          <span className="fef-display-row-placeholder">
            {(formState.product_kind || 'bunkers') === 'lubes'
              ? 'Pick a lube product from the catalogue'
              : 'Pick bunker type, sulfur, grade, and ISO year'}
          </span>
        )}
      </div>
    </div>

    {/* Bunkers: a MIN-MAX range, always in metric tonnes whatever the unit
     * box says. Lubes: ONE quantity counted in the unit picked beside it,
     * kept in ``max_tonnage`` -- the number credit, commission, the recap and
     * both letters already read -- and priced per that unit. */}
    <div className={`fef-grid ${isLubes ? 'fef-grid-3' : 'fef-grid-4'}`}>
      <Field label="Measuring Unit" error={validationErrors.measuring_unit}>
        <select
          value={formState.measuring_unit || ''}
          onChange={(e) => setField('measuring_unit', e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Select...</option>
          {(lookups.measuringUnits || []).map((u) => (
            <option key={u.id} value={u.id}>{unitLabel(u)}</option>
          ))}
        </select>
      </Field>
      {isLubes ? (
        <Field
          label={quantityUnit ? `Quantity (${quantityUnit})` : 'Quantity'}
          error={validationErrors.max_tonnage}
          hint={quantityUnit ? null : 'Counted in the measuring unit: pick it first.'}
        >
          <AmountInput
            value={formState.max_tonnage}
            onChange={(v) => setField('max_tonnage', v)}
            maxDecimals={QUANTITY_DECIMALS}
            placeholder="e.g. 40"
          />
        </Field>
      ) : (
        <>
          <Field
            label="MIN Tonnage (mt)"
            error={validationErrors.min_tonnage}
            hint="Always in metric tonnes, regardless of the packaging unit above."
          >
            <AmountInput
              value={formState.min_tonnage}
              onChange={(v) => setField('min_tonnage', v)}
              maxDecimals={QUANTITY_DECIMALS}
              placeholder="e.g. 500"
            />
          </Field>
          <Field
            label="MAX Tonnage (mt)"
            error={validationErrors.max_tonnage}
            hint="Always in metric tonnes, regardless of the packaging unit above."
          >
            <AmountInput
              value={formState.max_tonnage}
              onChange={(v) => setField('max_tonnage', v)}
              maxDecimals={QUANTITY_DECIMALS}
              placeholder="e.g. 1000"
            />
          </Field>
        </>
      )}
      <Field label={perUnitLabel('Sell Price', '$', quantityUnit)} error={validationErrors.sell_price}>
        <AmountInput
          value={formState.sell_price}
          onChange={(v) => setField('sell_price', v)}
          maxDecimals={PRICE_DECIMALS}
          placeholder="e.g. 650.00"
        />
      </Field>
    </div>

    <PricingTypeToggle
      value={formState.pricing_type}
      onChange={(v) => setField('pricing_type', v)}
    />
    {/* Row 132: a floating sale shows its formula right under the toggle. */}
    <FloatingFormulaLine side="sell" row={formState} lineNumber={1} />

    <CounterPartyCommissionBox
      counterPartyId={formState.counter_party}
      counterPartyDisplayName={formState.counter_party_display_name}
      amount={formState.commission_amount}
      maxTonnage={formState.max_tonnage}
      unitText={quantityUnit}
      isLubes={isLubes}
      onCounterPartyProfileChange={(profile) => {
        setField('counter_party', profile ? profile.id : null);
        setField(
          'counter_party_display_name',
          profile
            ? (profile.legal_entity_name || profile.trading_short_name || '')
            : '',
        );
      }}
      onAmountChange={(v) => setField('commission_amount', v)}
    />

    <div className="fef-display-row">
      <div className="fef-display-row-label">Credit Request</div>
      <div className="fef-display-row-value fef-credit-request">
        {rowCreditRequest != null ? formatCurrency(rowCreditRequest) : '—'}
      </div>
    </div>
  </Section>
  );
};

// Sprint 6 multi-product: the 2nd / 3rd / N-th product card. Pixel-
// identical to SectionProd1 above (same 4-col grids + Contract
// Shorthand row + Credit Request row); the only differences are the
// header (Product N + Remove button) and the lack of a supplier
// pricing block -- supplier pricing is captured one-block-per-product
// in SectionSupplierPricing below so the layout stays consistent.
export const ExtraProductCard = ({
  index,
  row,
  lookups,
  onChange,
  onRequestRemove,
  onPickerChange,
  onCreateProduct,
  variantMeta,
}) => {
  const setField = (key, value) => onChange(key, value);

  const isLubes = (row.product_kind || 'bunkers') === 'lubes';
  const quantityUnit = lineQuantityUnit(row, lookups.measuringUnits);
  // Same name Product 1's card and this product's Supplier Pricing block show.
  const displayShorthand = productShorthand(lookups, row);

  const creditRequest = (() => {
    const sell = parseFloat(row.sell_price);
    const max = parseFloat(row.max_tonnage);
    if (!Number.isFinite(sell) || !Number.isFinite(max)) return null;
    return sell * max;
  })();

  const removeButton = (
    <button
      type="button"
      className="fef-btn fef-btn-secondary fef-extra-product-remove"
      onClick={onRequestRemove}
    >
      <FontAwesomeIcon icon={faTrash} /> Remove
    </button>
  );

  return (
    <Section title={`Product ${index + 2}`} headerAction={removeButton}>
      <ProductKindToggle
        value={row.product_kind || 'bunkers'}
        onChange={(kind) => onChange('product_kind', kind)}
      />

      {isLubes ? (
        <div className="fef-grid fef-grid-4">
          <div className="fef-span-2">
            <LubesProductPicker
              value={row.stock_item_variant_display}
              variantId={row.stock_item_variant}
              onPickerChange={onPickerChange}
              onCreateProduct={onCreateProduct}
              variantMeta={variantMeta}
            />
          </div>
        </div>
      ) : (
      <div className="fef-grid fef-grid-4">
        <Field label="Bunker Type">
          <select
            value={row.fuel_type || ''}
            onChange={(e) => setField('fuel_type', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select...</option>
            {lookups.fuelTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Sulfur Limit">
          <select
            value={row.sulfur_limit || ''}
            onChange={(e) => setField('sulfur_limit', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select...</option>
            {lookups.sulfurLimits.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Bunker Grade">
          <select
            value={row.fuel_grade || ''}
            onChange={(e) => setField('fuel_grade', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select...</option>
            {lookups.fuelGrades.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Field>
        <Field label="ISO 8217 Year">
          <select
            value={row.iso_8217_year || ''}
            onChange={(e) => setField('iso_8217_year', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select...</option>
            {lookups.iso8217Years.map((t) => (
              <option key={t.id} value={t.id}>{t.year}</option>
            ))}
          </select>
        </Field>
      </div>
      )}

      <div className="fef-display-row">
        <div className="fef-display-row-label">Contract Shorthand</div>
        <div className="fef-display-row-value">
          {displayShorthand || (
            <span className="fef-display-row-placeholder">
              {isLubes
                ? 'Pick a lube product from the catalogue'
                : 'Pick bunker type, sulfur, grade, and ISO year'}
            </span>
          )}
        </div>
      </div>

      {/* Same rule as Product 1: a lubes line has ONE quantity, counted and
        * priced in its own unit; a fuel line keeps MIN-MAX in metric tonnes. */}
      <div className={`fef-grid ${isLubes ? 'fef-grid-3' : 'fef-grid-4'}`}>
        <Field label="Measuring Unit">
          <select
            value={row.measuring_unit || ''}
            onChange={(e) => setField('measuring_unit', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select...</option>
            {(lookups.measuringUnits || []).map((u) => (
              <option key={u.id} value={u.id}>{unitLabel(u)}</option>
            ))}
          </select>
        </Field>
        {isLubes ? (
          <Field
            label={quantityUnit ? `Quantity (${quantityUnit})` : 'Quantity'}
            hint={quantityUnit ? null : 'Counted in the measuring unit: pick it first.'}
          >
            <AmountInput
              value={row.max_tonnage}
              onChange={(v) => setField('max_tonnage', v)}
              maxDecimals={QUANTITY_DECIMALS}
              placeholder="e.g. 40"
            />
          </Field>
        ) : (
          <>
            <Field label="MIN Tonnage (mt)">
              <AmountInput
                value={row.min_tonnage}
                onChange={(v) => setField('min_tonnage', v)}
                maxDecimals={QUANTITY_DECIMALS}
                placeholder="e.g. 500"
              />
            </Field>
            <Field label="MAX Tonnage (mt)">
              <AmountInput
                value={row.max_tonnage}
                onChange={(v) => setField('max_tonnage', v)}
                maxDecimals={QUANTITY_DECIMALS}
                placeholder="e.g. 1000"
              />
            </Field>
          </>
        )}
        <Field label={perUnitLabel('Sell Price', '$', quantityUnit)}>
          <AmountInput
            value={row.sell_price}
            onChange={(v) => setField('sell_price', v)}
            maxDecimals={PRICE_DECIMALS}
            placeholder="e.g. 650.00"
          />
        </Field>
      </div>

      <PricingTypeToggle
        value={row.pricing_type}
        onChange={(v) => setField('pricing_type', v)}
      />
      <FloatingFormulaLine side="sell" row={row} lineNumber={index + 2} />

      <CounterPartyCommissionBox
        counterPartyId={row.counter_party}
        counterPartyDisplayName={row.counter_party_display_name}
        amount={row.commission_amount}
        maxTonnage={row.max_tonnage}
        unitText={quantityUnit}
        isLubes={isLubes}
        onCounterPartyProfileChange={(profile) => {
          setField('counter_party', profile ? profile.id : null);
          setField(
            'counter_party_display_name',
            profile
              ? (profile.legal_entity_name || profile.trading_short_name || '')
              : '',
          );
        }}
        onAmountChange={(v) => setField('commission_amount', v)}
      />

      <div className="fef-display-row">
        <div className="fef-display-row-label">Credit Request</div>
        <div className="fef-display-row-value fef-credit-request">
          {creditRequest != null ? formatCurrency(creditRequest) : '—'}
        </div>
      </div>
    </Section>
  );
};


// Supplier-side pricing section. These three fields are gated by the
// backend before the enquiry can advance from ``with_supply_trader``
// to ``recapped`` (per ``stage_service._missing_fields_for_target``):
//   - supplier (CRMProfile FK, Fuels-creditor)
//   - supplier_trader (Employee FK — already on SectionIMT)
//   - buy_price (per-mt cost from the supplier)
//   - supplier_credit_days (days the supplier extends to us)
//
// Sprint 6 multi-product. Supplier pricing is captured **per product**
// (one set of these three fields per FuelEnquiryProduct row) -- so when
// the trader has stacked extra products on this enquiry we render one
// "Product N" sub-block inside this same section instead of repeating
// the supplier fields on every Product card. Product 1 reads/writes
// the inline scalars on FuelEnquiry (back-compat); Product 2..N read
// /write ``formState.extra_products[idx]`` via ``setExtraField``.
//
// Each block is named by the product's short name next to its number
// ("Product 2 · VLSFO ISO 8217:2017 RMG380 Max.0.50%"), read from the same
// ``productShorthand`` the Product cards above use, so a trader pricing three
// products doesn't have to scroll up to learn which one "Product 2" is.
export const SectionSupplierPricing = ({
  formState,
  setField,
  setExtraField,
  hmsEntities,
  lookups,
  validationErrors,
  stepNumber,
  anchorId,
}) => {
  const extras = Array.isArray(formState.extra_products) ? formState.extra_products : [];
  const hasMultiProduct = extras.length > 0;

  // Item #2: mirror the customer-side credit backfill on the supplier side.
  // A Bunkers-only trader (no HasCRMAccess) 403s on the CRM detail fetch in
  // the picker and falls back to the quick-search row, which omits
  // ``partner_credit_days`` -- so supplier_credit_days stays blank until save.
  // When the picked profile already carries the value we fill it directly (no
  // request); when it's missing we pull it from the same enquiry-permission-
  // gated summary endpoint the customer backfill uses and fill from
  // ``credit_days``. ``supplierCreditFetchedRef`` records the last supplier id
  // we fetched per slot ('head' or the extra-product index) so we never
  // refetch the same supplier (including when it comes back null) while still
  // refetching when the slot's supplier changes.
  const supplierCreditFetchedRef = useRef({});

  const fillSupplierCreditDays = (slotKey, profile, apply) => {
    if (!profile) return;
    const direct = profile.partner_credit_days;
    if (direct != null && direct !== '') {
      apply(String(direct));
      return;
    }
    const id = profile.id;
    if (id == null) return;
    if (supplierCreditFetchedRef.current[slotKey] === id) return;
    supplierCreditFetchedRef.current[slotKey] = id;
    (async () => {
      try {
        const { data } = await axiosInstance.get(
          `/api/fuel-enquiries/partner-credit-summary/${id}/`,
        );
        if (data && data.credit_days != null && data.credit_days !== '') {
          apply(String(data.credit_days));
        }
      } catch (err) {
        console.warn('Supplier credit summary backfill failed:', err);
      }
    })();
  };

  // Mirror of the customer-side flow: when the picker returns a profile
  // (or null on clear), persist BOTH the FK id AND the display name on
  // the same form-state row. Re-renders pull the chip from
  // ``supplier_display_name`` -- no CRM list bulk fetch involved.
  const handleHeadProfile = (profile) => {
    setField('supplier', profile ? profile.id : null);
    setField(
      'supplier_display_name',
      profile
        ? (profile.legal_entity_name || profile.trading_short_name || '')
        : '',
    );
    fillSupplierCreditDays('head', profile, (v) => setField('supplier_credit_days', v));
  };

  const handleExtraProfile = (idx, profile) => {
    setExtraField(idx, 'supplier', profile ? profile.id : null);
    setExtraField(
      idx,
      'supplier_display_name',
      profile
        ? (profile.legal_entity_name || profile.trading_short_name || '')
        : '',
    );
    fillSupplierCreditDays(idx, profile, (v) => setExtraField(idx, 'supplier_credit_days', v));
  };

  return (
    <Section title="Supplier Pricing" stepNumber={stepNumber} anchorId={anchorId}>
      {hasMultiProduct && (
        <p className="fef-supplier-multi-hint">
          One supplier set per product. Product 1 always shows the
          primary supplier; Product 2 onwards are the additional products
          you stacked on this enquiry above.
        </p>
      )}

      <SupplierPricingProductBlock
        productLabel={hasMultiProduct ? 'Product 1' : null}
        shorthand={productShorthand(lookups, formState)}
        priceUnit={lineQuantityUnit(formState, lookups.measuringUnits)}
        isLubes={isLubesLine(formState)}
        leadingExtras={(
          /* Enquiry-level buying entity (not per-product) -- mirrors the
             "IMT Sell Company" picker on the IMT section. Optional, no
             validation gate. Rendered inside the head block's gray card so
             it sits with the rest of the supplier pricing fields. */
          <Field label="IMT Buying Entity">
            <select
              value={formState.hms_buy_company || ''}
              onChange={(e) => setField('hms_buy_company', e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">None</option>
              {(hmsEntities || []).map((entity) => (
                <option key={entity.id} value={entity.id}>{entity.hms_entity_name}</option>
              ))}
            </select>
          </Field>
        )}
        supplierId={formState.supplier}
        supplierDisplayName={formState.supplier_display_name}
        buyPrice={formState.buy_price}
        sellPrice={formState.sell_price}
        creditDays={formState.supplier_credit_days}
        supplierPricingType={formState.supplier_pricing_type}
        onSupplierProfileChange={handleHeadProfile}
        onBuyPriceChange={(v) => setField('buy_price', v)}
        onCreditDaysChange={(v) => setField('supplier_credit_days', v)}
        onSupplierPricingTypeChange={(v) => setField('supplier_pricing_type', v)}
        validationErrors={{
          supplier: validationErrors.supplier,
          buy_price: validationErrors.buy_price,
          supplier_credit_days: validationErrors.supplier_credit_days,
        }}
        showHints
        formulaRow={formState}
        lineNumber={1}
      />

      {extras.map((row, idx) => (
        <SupplierPricingProductBlock
          key={`supplier-product-${idx}`}
          productLabel={`Product ${idx + 2}`}
          shorthand={productShorthand(lookups, row)}
          priceUnit={lineQuantityUnit(row, lookups.measuringUnits)}
          isLubes={isLubesLine(row)}
          supplierId={row.supplier}
          supplierDisplayName={row.supplier_display_name}
          buyPrice={row.buy_price}
          sellPrice={row.sell_price}
          creditDays={row.supplier_credit_days}
          supplierPricingType={row.supplier_pricing_type}
          onSupplierProfileChange={(profile) => handleExtraProfile(idx, profile)}
          onBuyPriceChange={(v) => setExtraField(idx, 'buy_price', v)}
          onCreditDaysChange={(v) => setExtraField(idx, 'supplier_credit_days', v)}
          onSupplierPricingTypeChange={(v) => setExtraField(idx, 'supplier_pricing_type', v)}
          showHints
          formulaRow={row}
          lineNumber={idx + 2}
        />
      ))}

      {/* Row 132: one side fixed, the other floating -> the hedging desk's
          task, said here while the purchase side is being priced. */}
      <HedgingExposureBanner formState={formState} lookups={lookups} />
    </Section>
  );
};

// One self-contained supplier-pricing block (Supplier picker + Buy
// Price + Supplier Credit Days). Used N times inside
// ``SectionSupplierPricing`` -- once for Product 1 and once for each
// extra product on a multi-product enquiry. Picking a supplier
// auto-fills the credit days from the CRM profile's
// ``partner_credit_days`` (same UX as Branch Manager auto-fill).
const SupplierPricingProductBlock = ({
  productLabel,
  shorthand = '',
  leadingExtras = null,
  supplierId,
  supplierDisplayName,
  buyPrice,
  creditDays,
  supplierPricingType,
  onSupplierProfileChange,
  onBuyPriceChange,
  onCreditDaysChange,
  onSupplierPricingTypeChange,
  // What this line's quantity is counted in (``lineQuantityUnit``): the buy
  // price is per that unit -- 'mt' for fuel, the pack for a lubes line.
  priceUnit = 'mt',
  isLubes = false,
  validationErrors = {},
  showHints = false,
  // Row 132: the form row this block prices, for its purchase-side formula.
  formulaRow = null,
  lineNumber = null,
}) => {
  // Stub blob for the picker chip; mirrors the customer flow's
  // ``customerProfile`` shape ({id, legal_entity_name, ...}).
  const selectedSupplierProfile = supplierId
    ? { id: supplierId, legal_entity_name: supplierDisplayName || '' }
    : null;

  const creditDaysHint = !showHints
    ? null
    : (selectedSupplierProfile
        ? 'Auto-fills from CRM when you pick a supplier. Override only for one-off deals.'
        : 'Days of credit the supplier extends to IMT. Auto-fills from CRM when you pick a supplier.');

  const supplierHint = showHints
    ? 'Search CRM partners by name. Required to advance to Final Enquiry Review.'
    : null;
  let buyPriceUnitWords = 'metric tonne';
  if (isLubes) buyPriceUnitWords = priceUnit || 'measuring unit';
  const buyPriceHint = showHints ? `Price per ${buyPriceUnitWords} quoted by the supplier.` : null;

  return (
    <div className="fef-supplier-product-block">
      {/* A line with no name yet keeps its number alone (or, on a
          single-product enquiry, no heading) -- never a dangling separator. */}
      {(productLabel || shorthand) && (
        <div className="fef-supplier-product-label">
          {productLabel}
          {productLabel && shorthand && (
            <span className="fef-supplier-product-sep"> · </span>
          )}
          {shorthand && (
            <span className="fef-supplier-product-shorthand">{shorthand}</span>
          )}
        </div>
      )}

      {leadingExtras}

      <Field
        label="Supplier"
        error={validationErrors.supplier}
        hint={supplierHint}
      >
        <CustomerPicker
          customer={selectedSupplierProfile}
          onChange={onSupplierProfileChange}
          allowUnlinked={false}
          searchParams={{}}
          placeholder="Search CRM partners..."
          noResultsCopy="No matching CRM partners. Try a different search term."
        />
      </Field>

      <div className="fef-grid fef-grid-2">
        <Field
          label={perUnitLabel('Buy Price', '$', priceUnit)}
          error={validationErrors.buy_price}
          hint={buyPriceHint}
        >
          <AmountInput
            value={buyPrice ?? ''}
            onChange={onBuyPriceChange}
            maxDecimals={PRICE_DECIMALS}
            placeholder="e.g. 600.00"
          />
        </Field>

        <Field
          label="Supplier Credit Days"
          error={validationErrors.supplier_credit_days}
          hint={creditDaysHint}
        >
          <AmountInput
            kind="integer"
            value={creditDays ?? ''}
            onChange={onCreditDaysChange}
            placeholder="e.g. 14"
          />
        </Field>
      </div>

      <PricingTypeToggle
        value={supplierPricingType}
        onChange={onSupplierPricingTypeChange}
      />
      {formulaRow && (
        <FloatingFormulaLine side="buy" row={formulaRow} lineNumber={lineNumber} />
      )}

      {/* Commissions rework (June 2026): the buy-side commissions box was
          removed. Purchase commissions are now recorded as manual
          "Commission Purchase" rows under 3rd Party Costs (Section 7). */}
    </div>
  );
};

// Per-section comment block (Products / Shipment / General). Each block is
// a stand-alone <Section> card (no stepNumber, like the old Comments card)
// with a free-text "Notes" textarea plus TWO independent show-on ticks:
// one for the Customer Confirmation, one for the Supplier Nomination. The
// text is always saved; each tick only decides whether that text prints on
// the matching PDF.
const CommentBlock = ({
  formState,
  setField,
  title,
  anchorId,
  textKey,
  showOnCcKey,
  showOnSnKey,
}) => (
  <Section title={title} anchorId={anchorId}>
    <Field label="Notes (optional)">
      <textarea
        rows={4}
        value={formState[textKey]}
        onChange={(e) => setField(textKey, e.target.value)}
        placeholder="Free-form notes for the operations / supply teams. Optional."
      />
    </Field>
    {/* Comments stay internal unless the trader opts in — each tick names
        the document (Customer Confirmation / Supplier Nomination) the notes
        should print on. */}
    <label className="fef-inline-checkbox">
      <input
        type="checkbox"
        checked={!!formState[showOnCcKey]}
        onChange={(e) => setField(showOnCcKey, e.target.checked)}
      />
      <span>Show on Customer Confirmation</span>
    </label>
    <label className="fef-inline-checkbox">
      <input
        type="checkbox"
        checked={!!formState[showOnSnKey]}
        onChange={(e) => setField(showOnSnKey, e.target.checked)}
      />
      <span>Show on Supplier Nomination</span>
    </label>
    <div className="fef-inline-checkbox-hint">
      Ticked: these notes print on the matching document. Unticked: they stay
      internal.
    </div>
  </Section>
);

// ---------------------------------------------------------------------
// Next-Step Banner — top-of-form "you are here / do this next" cue.
//
// Demo-driven UX add: traders kept asking "when does the green Advance
// button unlock?" and "what do I do now?". Rather than scatter hints
// across every section, we collapse the whole flow's state machine
// into a single, stage- and data-aware banner that always sits at the
// top of the form. One source of truth, one place to read.
//
// The banner is purely informational -- it does not gate any action;
// it just narrates what the form is already doing. Action buttons
// (Save Draft, Submit & Advance, Advance to ...) remain in the
// HeaderActions block exactly as before.
// ---------------------------------------------------------------------

const computeNextStep = (isCreateMode, stage, enquiry) => {
  if (isCreateMode) {
    return {
      tone: 'info',
      icon: faCircleInfo,
      title: 'New enquiry \u2014 fill in the basics',
      description:
        'Add the customer, product spec and sell price. Save Draft '
        + 'to come back later, or Save & Workflow once the deal shape '
        + 'is solid -- it hands the enquiry off to the Supply Trader '
        + '(an automatic credit check runs first).',
    };
  }

  const creditStatus = enquiry?.credit_status || null;
  const credit = enquiry?.latest_credit_request;

  switch (stage) {
    case 'draft': {
      // Bunkers 2-screen redesign: drafts paused on credit show a
      // dedicated waiting banner. credit_status is the source of truth
      // -- the CIR row may also exist, but the badge is keyed off the
      // status flag.
      if (creditStatus === 'awaiting_credit_approval') {
        return {
          tone: 'waiting',
          icon: faClock,
          title: 'Waiting on Credit Approval',
          description:
            'The auto credit check on Save & Workflow opened a '
            + 'Credit Increase Request. A Credit Approver has been '
            + 'notified. The enquiry will hand off to the Supply '
            + 'Trader the moment the request is approved.',
        };
      }
      if (credit?.status === 'rejected') {
        return {
          tone: 'blocked',
          icon: faCircleXmark,
          title: 'Credit increase was rejected',
          description:
            'Open the Credit Balances section for the reviewer\u2019s '
            + 'notes. Lower the deal value (or revise terms) and '
            + 'click Save & Workflow again to retry.',
        };
      }
      return {
        tone: 'info',
        icon: faPaperPlane,
        title: 'Step 1 \u2014 fill in the deal shape, then Save & Workflow',
        description:
          'Capture customer, product, sell price and ship details. '
          + 'Save Draft is always available; click Save & Workflow '
          + 'when the deal is ready to be picked up by the Supply '
          + 'Trader. The credit check runs automatically.',
      };
    }

    case 'with_supply_trader': {
      if (!movesFromServer(enquiry).includes('recapped')) {
        // Row 139: a customer trader never recaps, so the supply-side step is
        // not theirs to read about.
        return {
          tone: 'waiting',
          icon: faPaperPlane,
          title: 'With the supply trader',
          description:
            'The supply trader adds the supplier pricing and recaps this '
            + 'deal. The sales recap shows here once it is done.',
        };
      }
      return {
        tone: 'info',
        icon: faPaperPlane,
        title: 'Step 2 \u2014 add supplier pricing, then Advance to Recapped',
        description:
          'Fill in Supplier Pricing for every product, then click '
          + 'Advance to Recapped to convert this enquiry into a '
          + 'Trade Recap.',
      };
    }

    case 'recapped': {
      // Bunkers Phase 1: paired SALE+PURCHASE conversion. The SALE
      // leg is on ``enquiry.trade_recap`` (the existing FK); the
      // PURCHASE sibling is on ``enquiry.paired_purchase_recap``
      // (serializer summary returns ``{id, transaction_number,
      // recap_type}`` or ``null``). Pre-paired enquiries surface
      // only the SALE button -- ``paired_purchase_recap`` will be
      // null for any conversion that ran before the paired
      // conversion service landed.
      const actions = [];
      if (enquiry?.trade_recap) {
        actions.push({
          label: 'View Sale Recap',
          recapId: enquiry.trade_recap,
        });
      }
      if (enquiry?.paired_purchase_recap?.id) {
        actions.push({
          label: 'View Purchase Recap',
          recapId: enquiry.paired_purchase_recap.id,
        });
      }
      return {
        tone: 'done',
        icon: faCircleCheck,
        title: 'Done \u2014 converted to Trade Recap',
        description:
          'This enquiry has been handed off to the Deals Board.',
        actions: actions.length > 0 ? actions : undefined,
      };
    }

    case 'lost':
      return {
        tone: 'done',
        icon: faCircleXmark,
        title: 'Marked as Lost',
        description:
          'This deal didn\u2019t go through. The audit trail is '
          + 'preserved for reference.',
      };

    default:
      return null;
  }
};

const NEXT_STEP_TONE_CLASS = {
  info: 'fef-next-step--info',
  waiting: 'fef-next-step--waiting',
  ready: 'fef-next-step--ready',
  done: 'fef-next-step--done',
  blocked: 'fef-next-step--blocked',
};

export const NextStepBanner = ({
  isCreateMode,
  stage,
  enquiry,
  onViewRecap,
  missingForNext = [],
  onJumpTo,
}) => {
  const next = computeNextStep(isCreateMode, stage, enquiry);
  if (!next) return null;
  const toneClass = (
    NEXT_STEP_TONE_CLASS[next.tone] || NEXT_STEP_TONE_CLASS.info
  );
  // ``next.actions`` (plural) is the new paired-recap shape used by
  // the ``recapped`` stage to surface both SALE and PURCHASE legs.
  // Older stages still use ``next.action`` (singular) -- coerce both
  // into one array so the render block has a single code path.
  const actions = next.actions
    || (next.action?.recapId ? [next.action] : []);
  const missingItems = describeMissingFields(missingForNext);
  const gateButtonLabel = stage === 'draft'
    ? 'Save & Workflow'
    : 'Advance to Recapped';
  return (
    <div className={`fef-next-step ${toneClass}`} role="note">
      <FontAwesomeIcon icon={next.icon} className="fef-next-step-icon" />
      <div className="fef-next-step-body">
        <div className="fef-next-step-title">{next.title}</div>
        <div className="fef-next-step-desc">{next.description}</div>
        {missingItems.length > 0 && (
          <div className="fef-next-step-missing">
            <span className="fef-next-step-missing-label">
              Still needed before {gateButtonLabel}:
            </span>
            {missingItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className="fef-next-step-missing-item"
                onClick={() => item.anchorId && onJumpTo?.(item.anchorId)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {actions.length > 0 && (
        <div className="fef-next-step-actions">
          {actions.map((a) => (
            <button
              key={a.recapId}
              type="button"
              className="fef-next-step-action"
              onClick={() => onViewRecap?.(a.recapId)}
            >
              {a.label} <FontAwesomeIcon icon={faArrowRight} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Outstanding-sends indicator (Phase 7).
//
// Once an enquiry is recapped the backend mints one dispatch row per
// outbound document (Customer Confirmation / Supplier Nomination). Rather
// than stack one page-wide banner per document, we render a SINGLE compact
// card ("Trade documents to send") whose body is a two-column segmented row
// -- one section per dispatch. Each section shows the doc name, a status
// chip, and a contextual control: ``pending`` -> "not sent" tone + a compact
// Review & Send button; ``sent`` -> a check + recipient + timestamp;
// ``failed`` -> a blocked tone + Retry; ``cancelled`` -> a muted done state.
// The card chrome reuses the ``fef-next-step`` info tone; the inner grid +
// status chips are the only new ``fef-dispatch-*`` styles.
// ---------------------------------------------------------------------

const DISPATCH_STATUS_TONE = {
  pending: 'fef-dispatch-section--waiting',
  sent: 'fef-dispatch-section--done',
  failed: 'fef-dispatch-section--blocked',
  cancelled: 'fef-dispatch-section--done',
};

const DISPATCH_STATUS_ICON = {
  pending: faPaperPlane,
  sent: faCircleCheck,
  failed: faCircleXmark,
  cancelled: faCircleXmark,
};

const DispatchSection = ({
  dispatch, onReview, onRevise, onViewSent, revising,
}) => {
  const toneClass = DISPATCH_STATUS_TONE[dispatch.status]
    || 'fef-dispatch-section--info';
  const icon = DISPATCH_STATUS_ICON[dispatch.status] || faCircleInfo;
  const version = dispatch.version || 1;
  const isRevision = version > 1;
  const isSuperseded = dispatch.is_current === false;
  // A deal with several suppliers has one Supplier Nomination each, so the
  // card names the supplier -- otherwise two identical "Supplier Nomination"
  // cards and no way to tell which one went out.
  const label = (dispatch.doc_type_display || 'Document')
    + (dispatch.supplier_name ? ` — ${dispatch.supplier_name}` : '')
    + (isRevision ? ` — Revision ${version}` : '');
  const isCancelled = dispatch.status === 'cancelled';
  const isPending = dispatch.status === 'pending';
  const isSent = dispatch.status === 'sent';
  // 'original' = the emailed PDF is on file; 'recorded' = only the values it
  // was sent with survive; null/undefined = nothing honest to show.
  const sentDocumentView = dispatch.sent_document_view;
  const isFailed = dispatch.status === 'failed';

  let statusText;
  if (isPending) statusText = 'Not sent';
  else if (isSent) statusText = 'Sent';
  else if (isFailed) statusText = 'Send failed';
  else statusText = dispatch.status_display || dispatch.status;

  let description;
  if (isSent) {
    const who = dispatch.sent_by_name ? ` by ${dispatch.sent_by_name}` : '';
    description = `Sent${who} on ${formatDateTime(dispatch.sent_at)}`
      + (dispatch.recipient_email
        ? ` to ${dispatch.recipient_email}`
        : '');
  } else if (isFailed) {
    description = 'The last send attempt failed. Review and try again.';
  } else if (isPending) {
    description = dispatch.resolved_recipient_email
      ? `Ready to send to ${dispatch.resolved_recipient_email}.`
      : 'Add a recipient before sending.';
  } else if (isCancelled) {
    // The server says why (e.g. the supplier was taken off the deal before
    // this letter went out); fall back to the bare status only if it didn't.
    description = dispatch.last_error || dispatch.status_display || 'Cancelled';
  } else {
    description = dispatch.status_display || '';
  }

  return (
    <div className={`fef-dispatch-section ${toneClass}`}>
      <div className="fef-dispatch-section-head">
        <span className="fef-dispatch-doc">{label}</span>
        <span className="fef-dispatch-chip">
          <FontAwesomeIcon icon={icon} className="fef-dispatch-chip-icon" />
          {statusText}
        </span>
      </div>
      <div className="fef-dispatch-desc">{description}</div>
      {/* A superseded version is history: it stays visible for the trail but
          offers no actions, and says which version replaced it. */}
      {isSuperseded && (
        <div className="fef-dispatch-superseded">
          Superseded
          {dispatch.superseded_by_version
            ? ` by Revision ${dispatch.superseded_by_version}`
            : ''}
        </div>
      )}

      {/* What the counterparty actually received, for THIS version -- on the
          current sent row and on every superseded one, which is what makes the
          version trail readable without leaving the enquiry. ``recorded`` means
          the stored file has gone and we are rebuilding from the values the
          version was sent with, so the label must not promise the original. */}
      {sentDocumentView && (
        <button
          type="button"
          className="fef-dispatch-action fef-dispatch-action--secondary"
          onClick={() => onViewSent?.(dispatch)}
        >
          {sentDocumentView === 'original'
            ? 'View sent document'
            : 'View recorded values'}
        </button>
      )}

      {!isSuperseded && (isPending || isFailed) && (
        <button
          type="button"
          className="fef-dispatch-action"
          onClick={() => onReview?.(dispatch)}
        >
          {isFailed ? 'Retry' : 'Review & Send'}
          {' '}
          <FontAwesomeIcon icon={faArrowRight} />
        </button>
      )}

      {/* Issue revision — only on a SENT, current version. The deal moved
          after this went out, so the counterparty needs a corrected document.
          Deliberately trader-triggered: auto-minting
          a version off any enquiry edit would put a new document in front of
          the customer for a typo fix. */}
      {dispatch.can_revise && (
        <button
          type="button"
          className="fef-dispatch-action fef-dispatch-action--secondary"
          onClick={() => onRevise?.(dispatch)}
          disabled={revising}
        >
          {revising ? 'Creating revision…' : `Issue revision ${version + 1}`}
        </button>
      )}
    </div>
  );
};

const DispatchSendsIndicator = ({
  dispatches, onReview, onRevise, onViewSent, revisingId,
}) => (
  <div className="fef-next-step fef-next-step--info fef-dispatch-card" role="note">
    <FontAwesomeIcon icon={faPaperPlane} className="fef-next-step-icon" />
    <div className="fef-next-step-body">
      <div className="fef-next-step-title">Trade documents to send</div>
      <div className="fef-dispatch-grid">
        {/* Current versions first, then superseded ones as the trail — so the
            live document is always what the eye lands on. */}
        {[...dispatches]
          .sort((a, b) => {
            const currentDiff = (b.is_current === false ? 0 : 1)
              - (a.is_current === false ? 0 : 1);
            if (currentDiff !== 0) return currentDiff;
            // Confirmation first, then each supplier's nomination by name, so
            // one supplier's letter and its revisions sit together.
            const typeDiff = (a.doc_type === 'customer_confirmation' ? 0 : 1)
              - (b.doc_type === 'customer_confirmation' ? 0 : 1);
            if (typeDiff !== 0) return typeDiff;
            const nameDiff = (a.supplier_name || '')
              .localeCompare(b.supplier_name || '');
            if (nameDiff !== 0) return nameDiff;
            return (b.version || 1) - (a.version || 1);
          })
          .map((dispatch) => (
            <DispatchSection
              key={dispatch.id}
              dispatch={dispatch}
              onReview={onReview}
              onRevise={onRevise}
              onViewSent={onViewSent}
              revising={revisingId === dispatch.id}
            />
          ))}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------
// 3rd Party Costs section (Bunkers 2-screen, Section 7).
//
// Each row is one ``ThirdPartyCost`` (Barging / Survey / Agency / Other).
// Reads come off ``enquiry.third_party_costs`` (eager-loaded by the
// detail serializer); writes go through ``fuelEnquiryService.thirdPartyCosts``.
//
// The serializer enforces the visibility matrix
// (``not_required`` blanks supplier/cost/on_charge, ``no_charge`` forces
// on_charge=0) and the cross-row currency lock; we mirror the matrix
// here so the UI hides fields that the backend would coerce, but the
// server is the source of truth for validation.
//
// Layout reuses ``fef-supplier-product-block`` cards + ``fef-grid`` so
// no new CSS is introduced. ``charge_mode='not_required'`` rows fade
// the card via ``fef-mirror-value`` styling to make absent-by-design
// rows look distinct from dirty / incomplete ones.
// ---------------------------------------------------------------------

const CHARGE_MODE_ON_CHARGE = 'on_charge';
const CHARGE_MODE_NO_CHARGE = 'no_charge';
const CHARGE_MODE_NOT_REQUIRED = 'not_required';

const CHARGE_MODE_OPTIONS = [
  { value: CHARGE_MODE_ON_CHARGE, label: 'On Charge' },
  { value: CHARGE_MODE_NO_CHARGE, label: 'No Charge' },
];

const TPC_CONTENT_TYPE = 'fuel_enquiries.fuelenquiry';

const tpcCategoryById = (categories, id) => (
  (categories || []).find((c) => c.id === Number(id)) || null
);

// Normalize an enquiry's TPC row into the local edit shape. Decimal
// fields come back from DRF as strings already; we keep them as strings
// so a freshly-typed value and a freshly-loaded one round-trip the same
// through ``<input type=number>``. ``supplier_display`` is a
// client-side cache of the picked CRMProfile blob (legal_entity_name +
// id) so the CustomerPicker chip can render regardless of whether the
// supplier is in any pre-loaded list -- 3rd-party suppliers are not
// fuels-suppliers and won't appear in the bootstrap ``suppliers`` prop.
const tpcRowToDraft = (row) => ({
  id: row.id ?? null,
  category: row.category ?? null,
  other_label: row.other_label || '',
  charge_mode: row.charge_mode || CHARGE_MODE_ON_CHARGE,
  supplier: row.supplier ?? null,
  // ``supplier_display_name`` ships from the serializer; rehydrate it
  // into the cached blob so the CustomerPicker chip renders for
  // suppliers that aren't in any pre-loaded list.
  supplier_display: row.supplier
    ? {
        id: row.supplier,
        legal_entity_name: row.supplier_display_name || '',
        trading_short_name: '',
      }
    : null,
  // Commissions as line items (Sept 2026): when a rate is set, the total is
  // the server's to work out -- see ``perMtCost.js``.
  rate_per_mt: row.rate_per_mt != null ? String(row.rate_per_mt) : '',
  cost_amount: row.cost_amount != null ? String(row.cost_amount) : '',
  on_charge_amount: row.on_charge_amount != null ? String(row.on_charge_amount) : '',
  currency: row.currency || 'USD',
  notes: row.notes || '',
  // Which product lines of the deal this cost belongs to. An empty list is a
  // real answer meaning "the whole deal" -- it is what every row created before
  // this control existed carries, and it is what a row keeps if the trader
  // ticks nothing.
  products: Array.isArray(row.products) ? row.products.map(Number) : [],
});

const blankTpcDraft = (categoryId, currency) => ({
  id: null,
  category: categoryId ?? null,
  other_label: '',
  charge_mode: CHARGE_MODE_ON_CHARGE,
  supplier: null,
  supplier_display: null,
  rate_per_mt: '',
  cost_amount: '',
  on_charge_amount: '',
  currency: currency || 'USD',
  notes: '',
  products: [],
});

// Stable, order-independent comparison of two product-tag lists. The server
// returns them sorted by id and the trader ticks them in whatever order they
// like, so a plain array compare would flag a row dirty forever.
const sameProductTags = (a, b) => {
  const left = [...(a || [])].map(Number).sort((x, y) => x - y);
  const right = [...(b || [])].map(Number).sort((x, y) => x - y);
  return left.length === right.length
    && left.every((value, index) => value === right[index]);
};

// Mirror the backend visibility matrix on the draft so dirty-tracking
// stays truthful (otherwise the user sees "unsaved changes" forever
// because the server has NULL but the local draft holds the prior
// typed value). Same matrix as ``ThirdPartyCostSerializer.validate``:
//   not_required -> supplier/cost/on_charge/other_label blanked
//   no_charge    -> on_charge forced to '0'
//   on_charge    -> all three required, no coercion
const coerceDraftForChargeMode = (draft) => {
  if (draft.charge_mode === CHARGE_MODE_NOT_REQUIRED) {
    return {
      ...draft,
      supplier: null,
      supplier_display: null,
      rate_per_mt: '',
      cost_amount: '',
      on_charge_amount: '',
      other_label: '',
    };
  }
  if (draft.charge_mode === CHARGE_MODE_NO_CHARGE) {
    return { ...draft, on_charge_amount: '0' };
  }
  return draft;
};

// Decide whether a draft has unsaved changes vs the persisted row.
// Compares the coerced draft (mirrors what the server stored) against
// the persisted snapshot, so legitimate no-op edits don't flag dirty.
const draftDiffersFromRow = (draft, row) => {
  if (!row) return true;
  const reference = tpcRowToDraft(row);
  const normalized = coerceDraftForChargeMode(draft);
  // Normalize decimal-string format so '1250' vs '1250.00' don't
  // produce false-positive dirty flags.
  const normDecimal = (v, places = AMOUNT_DECIMALS) => {
    if (v === '' || v == null) return '';
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(places) : String(v);
  };
  // With a rate typed, the total on screen is a preview of the server's
  // figure, not the trader's entry -- comparing it would flag a row "unsaved"
  // whenever the saved tonnage moved underneath it.
  const totalIsWorkedOut = normalized.rate_per_mt !== '' && normalized.rate_per_mt != null;
  return (
    normalized.category !== reference.category
    || (normalized.other_label || '') !== (reference.other_label || '')
    || normalized.charge_mode !== reference.charge_mode
    || (normalized.supplier ?? null) !== (reference.supplier ?? null)
    || normDecimal(normalized.rate_per_mt, PRICE_DECIMALS) !== normDecimal(reference.rate_per_mt, PRICE_DECIMALS)
    || (!totalIsWorkedOut
      && normDecimal(normalized.cost_amount) !== normDecimal(reference.cost_amount))
    || normDecimal(normalized.on_charge_amount) !== normDecimal(reference.on_charge_amount)
    || normalized.currency !== reference.currency
    || (normalized.notes || '') !== (reference.notes || '')
    || !sameProductTags(normalized.products, reference.products)
  );
};

// Commissions rework (June 2026): locked "Commission Sales" card. Rendered
// LIVE from the form's Counter Party + commission fields (not from the
// persisted row) so the figure updates as the trader types on the Product
// card; the backend writes the matching real row on every enquiry save and
// rejects API edits/deletes on it. Read-only — no Save / Delete actions.
const SystemCommissionRow = ({ supplierName, cost, productLabel }) => (
  <div className="fef-supplier-product-block">
    <div className="fef-supplier-product-label">
      Commission Sales — No Charge (auto-generated)
    </div>

    <div className="fef-grid fef-grid-3">
      <Field label="Counter Party">
        <input
          type="text"
          value={supplierName || ''}
          disabled
          readOnly
        />
      </Field>
      <Field label="Total cost (USD)">
        <input
          type="text"
          value={cost.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          disabled
          readOnly
        />
      </Field>
      <Field label="Currency">
        <input type="text" value="USD" disabled readOnly />
      </Field>
    </div>

    {/* Which product this commission belongs to. Shown, never editable: the
        row is generated one-per-product from the Counter Party box, so the
        answer is already settled and hand-editing it could only make the card
        disagree with the Product section it was built from. */}
    {productLabel && (
      <Field label="Applies to">
        {/* ``Field``'s label is not wired to its control, so the readout gets
            its own accessible name — otherwise a screen reader announces an
            unlabelled box, and the value is the whole point of the row. */}
        <input
          type="text"
          aria-label="Applies to"
          value={productLabel}
          disabled
          readOnly
        />
      </Field>
    )}

    <div className="fef-credit-inline-hint" role="note">
      <FontAwesomeIcon icon={faCircleInfo} />
      <span>
        Auto-calculated from the Product card: commission (USD/mt) × MAX
        tonnage. Saved with the enquiry. To change or remove it, edit the
        Counter Party box on the enquiry&apos;s Product section.
      </span>
    </div>
  </div>
);

// Which product lines a cost row on THIS screen may be ticked against.
//
// The screen is always looking at an enquiry, so the list is the enquiry's own
// product children. That is the same list the server validates against, and the
// server also ships an ``available_products`` list on every saved cost row --
// so a cost card built for a delivery or a recap gets the right products from
// the API without repeating this assumption.
//
// An empty list is not an empty dropdown. A single-product enquiry (and a
// Solids deal, which has no product lines at all) gets NO control: an empty
// dropdown reads as broken, a missing one reads as not applicable.
const tpcProductOptions = (enquiry) => {
  const products = Array.isArray(enquiry?.products) ? enquiry.products : [];
  return products.map((product, index) => ({
    id: product.id,
    label: (product.contract_shorthand || '').trim() || `Product ${index + 1}`,
  }));
};

// Build the live Commission Sales previews off the current form state —
// one entry per product (Product 1 inline scalars + every extra card)
// that has a counter party, a rate and a MAX tonnage. Mirrors the
// backend's ``commission_service.sync_commission_sales_rows`` so what the
// card shows pre-save is exactly what the save will write.
const computeCommissionPreviews = (formState) => {
  // ``position`` is the product's place on the screen (1-based), carried so the
  // card can say WHICH product the commission belongs to. It has to be set
  // here, before the incomplete entries are filtered out -- reading it off the
  // surviving array's index would relabel Product 2's commission as Product 1's
  // the moment Product 1 has no counter party.
  const mk = (key, position, counterParty, name, rate, maxTonnage) => {
    const r = parseFloat(rate);
    const m = parseFloat(maxTonnage);
    if (!counterParty || !Number.isFinite(r) || !Number.isFinite(m)) return null;
    return { key, position, supplierName: name || '', cost: r * m };
  };
  const extras = Array.isArray(formState.extra_products)
    ? formState.extra_products : [];
  return [
    mk(
      'commission-preview-p1',
      1,
      formState.counter_party,
      formState.counter_party_display_name,
      formState.commission_amount,
      formState.max_tonnage,
    ),
    ...extras.map((row, idx) => mk(
      `commission-preview-p${idx + 2}`,
      idx + 2,
      row.counter_party,
      row.counter_party_display_name,
      row.commission_amount,
      row.max_tonnage,
    )),
  ].filter(Boolean);
};

// Exported for the seam test (``FuelEnquiryForm.thirdPartyCostProducts.test.js``).
// The section is driven directly there rather than through the whole enquiry
// page: the page needs a dozen mocked services before it will paint, and the
// thing worth pinning is what this section sends when a trader ticks a box and
// what it draws when the server sends the ticks back.
export const SectionThirdPartyCosts = ({
  stepNumber,
  anchorId,
  enquiry,
  categories,
  onChanged,
  // Live Commission Sales previews from ``computeCommissionPreviews`` —
  // rendered in place of the persisted system rows so the cards track the
  // Product card inputs before the save round-trip.
  commissionPreviews = [],
}) => {
  const rows = useMemo(
    () => (Array.isArray(enquiry?.third_party_costs)
      ? enquiry.third_party_costs : []),
    [enquiry],
  );

  // Set by the server once a customer invoice on this deal is approved (Chris,
  // 2026-09-16: changes allowed "until invoices are APPROVED"). Until then it
  // is empty and the section works as normal, recapped or not. When set, the
  // section says why and stops offering edits -- the server refuses them
  // anyway, and a trader should not type a correction only to be told no.
  const lockedReason = enquiry?.third_party_costs_locked_reason || null;

  // Section-level currency: drives the per-row currency for new drafts
  // so we don't trigger the backend's cross-row currency lock. Defaults
  // to the existing row currency, otherwise USD. System-generated
  // commission rows are skipped — they are always USD and exempt from
  // the lock, so they must not drag manual rows onto USD.
  const initialCurrency = rows.find((r) => !r.is_system_generated)?.currency || 'USD';
  const [currency, setCurrency] = useState(initialCurrency);

  useEffect(() => {
    // Re-seed when the enquiry payload changes (e.g. after a save round-trip).
    setCurrency(initialCurrency);
  }, [initialCurrency]);

  // ``drafts`` is keyed by stable row id ('row-<pk>' for persisted,
  // 'new-<n>' for unsaved). We hydrate persisted entries from
  // ``rows`` and append any local-only drafts the user is filling in.
  const [drafts, setDrafts] = useState({});
  const [newCounter, setNewCounter] = useState(0);
  const [busyKey, setBusyKey] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  // The server row each draft was last hydrated from. Without it there is no
  // way to tell "the trader typed something" apart from "the server changed
  // underneath us", and the two need opposite treatment.
  const lastServerRowById = useRef(new Map());

  // Re-hydrate persisted rows whenever the enquiry payload changes.
  //
  // A draft the trader has NOT touched is nothing but a copy of the server
  // row, so it is re-taken every time. The previous version kept it forever
  // once hydrated, which meant a value the server had since changed -- the
  // product ticks it clears when a product line is removed -- stayed on the
  // screen naming a product that no longer exists, showed "unsaved changes"
  // on a row nobody had edited, and made Save fail with the backend refusing
  // a product that isn't on the deal.
  //
  // An edit in progress does survive, so nobody loses typing to a background
  // refresh -- but even then it may not keep a tick for a product line the
  // enquiry no longer has. That is not the trader's edit, it is a leftover.
  useEffect(() => {
    const liveProductIds = new Set(
      (Array.isArray(enquiry?.products) ? enquiry.products : []).map((p) => p.id),
    );
    const onlyLiveProducts = (draft) => {
      const tagged = Array.isArray(draft.products) ? draft.products : [];
      const kept = tagged.filter((id) => liveProductIds.has(id));
      return kept.length === tagged.length ? draft : { ...draft, products: kept };
    };
    setDrafts((prev) => {
      const next = {};
      rows.forEach((row) => {
        // System-generated commission rows are render-only (locked card)
        // and never get an editable draft slot.
        if (row.is_system_generated) return;
        const key = `row-${row.id}`;
        const draft = prev[key];
        const lastSeen = lastServerRowById.current.get(row.id);
        const editedByTrader = Boolean(draft)
          && draft.id === row.id
          && Boolean(lastSeen)
          && draftDiffersFromRow(draft, lastSeen);
        next[key] = editedByTrader
          ? onlyLiveProducts(draft)
          : tpcRowToDraft(row);
      });
      Object.keys(prev).forEach((k) => {
        if (k.startsWith('new-')) next[k] = onlyLiveProducts(prev[k]);
      });
      return next;
    });
    const seen = new Map();
    rows.forEach((row) => seen.set(row.id, row));
    lastServerRowById.current = seen;
  }, [rows, enquiry]);

  const persistedRowById = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => map.set(r.id, r));
    return map;
  }, [rows]);

  // Empty on a single-product enquiry and on any deal with no product lines,
  // which is the signal to render no picker at all rather than an empty one.
  const productOptions = useMemo(() => tpcProductOptions(enquiry), [enquiry]);
  const productLabelById = useMemo(() => {
    const map = new Map();
    productOptions.forEach((option) => map.set(option.id, option.label));
    return map;
  }, [productOptions]);

  const orderedKeys = useMemo(() => {
    // System-generated commission rows are rendered separately as live
    // preview cards (``commissionPreviews``), so only manual rows get a
    // key here.
    const persisted = rows
      .filter((r) => !r.is_system_generated)
      .map((r) => `row-${r.id}`);
    const news = Object.keys(drafts)
      .filter((k) => k.startsWith('new-'))
      .sort();
    return [...persisted, ...news];
  }, [rows, drafts]);

  const isOther = (categoryId) => {
    const cat = (categories || []).find((c) => c.id === Number(categoryId));
    return (cat?.name || '').toLowerCase() === 'other';
  };

  // "Commission Sales" is listed but disabled in the category dropdown —
  // those rows are system-generated from the Product card's Counter Party
  // box, so hand-picking the category would allow forged / duplicate
  // commission lines. The backend serializer rejects it as defence in
  // depth. ("Commission Purchase" stays freely selectable for manual
  // buy-side rows.)
  const isSystemOnlyCategory = (c) => (
    (c?.name || '').toLowerCase() === 'commission sales'
  );

  const setDraftField = (key, field, value) => {
    setDrafts((prev) => {
      const current = prev[key] || {};
      let next = { ...current, [field]: value };
      // Apply the backend visibility matrix immediately so the UI
      // doesn't show stale supplier/cost fields after switching to
      // ``not_required`` (which the server would coerce to NULL on
      // save anyway).
      if (field === 'charge_mode') {
        next = coerceDraftForChargeMode(next);
      }
      // Commissions as line items (Sept 2026). Moving a row onto a category
      // HMS always pays itself switches it to No Charge on screen -- the
      // trader watches the dropdown change -- instead of leaving a greyed-out
      // option selected that the save would refuse. A category with no rate
      // box takes the rate away with it.
      if (field === 'category') {
        const category = tpcCategoryById(categories, value);
        if (category && !categoryAllowsOnCharge(category)
            && next.charge_mode === CHARGE_MODE_ON_CHARGE) {
          next = coerceDraftForChargeMode({ ...next, charge_mode: CHARGE_MODE_NO_CHARGE });
        }
        if (category && !categoryAllowsRatePerMt(category)) {
          next = { ...next, rate_per_mt: '' };
        }
      }
      // A rate on a deal with exactly one product line ticks that line, in
      // plain sight: the rate must be multiplied by some product's tonnage and
      // there is only one it can be.
      if (field === 'rate_per_mt' && value !== ''
          && productOptions.length === 1
          && (next.products || []).length === 0) {
        next = { ...next, products: [productOptions[0].id] };
      }
      // On "On Charge" rows, mirror Total cost into On-charge so the
      // common case (bill the customer exactly what it cost) needs no
      // extra typing. We only mirror while on-charge still tracks cost
      // (blank, or still equal to the prior cost value); once the user
      // diverges on-charge manually, their value is never clobbered.
      if (field === 'cost_amount' && current.charge_mode === CHARGE_MODE_ON_CHARGE) {
        const prevOnCharge = current.on_charge_amount;
        const onChargeTracksCost = prevOnCharge == null
          || prevOnCharge === ''
          || prevOnCharge === current.cost_amount;
        if (onChargeTracksCost) {
          next = { ...next, on_charge_amount: value };
        }
      }
      return { ...prev, [key]: next };
    });
  };

  const toggleDraftProduct = (key, productId) => {
    setDrafts((prev) => {
      const current = prev[key] || {};
      const tagged = Array.isArray(current.products) ? current.products : [];
      const next = tagged.includes(productId)
        ? tagged.filter((id) => id !== productId)
        : [...tagged, productId];
      return { ...prev, [key]: { ...current, products: next } };
    });
  };

  const setDraftSupplier = (key, profile) => {
    setDrafts((prev) => {
      const current = prev[key] || {};
      return {
        ...prev,
        [key]: {
          ...current,
          supplier: profile ? profile.id : null,
          supplier_display: profile
            ? {
                id: profile.id,
                legal_entity_name: profile.legal_entity_name || '',
                trading_short_name: profile.trading_short_name || '',
              }
            : null,
        },
      };
    });
  };

  const addRow = () => {
    const key = `new-${newCounter}`;
    setNewCounter((n) => n + 1);
    setDrafts((prev) => ({
      ...prev,
      [key]: blankTpcDraft(null, currency),
    }));
  };

  const cancelDraft = (key) => {
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (key.startsWith('new-')) {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      // Reset persisted row's draft back to the server snapshot.
      const id = Number(key.slice(4));
      const row = persistedRowById.get(id);
      if (row) {
        setDrafts((prev) => ({ ...prev, [key]: tpcRowToDraft(row) }));
      }
    }
  };

  const saveDraft = async (key) => {
    const draft = drafts[key];
    if (!draft) return;
    if (!draft.category) {
      setRowErrors((prev) => ({ ...prev, [key]: 'Pick a category first.' }));
      return;
    }
    setBusyKey(key);
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    const payload = {
      content_type: TPC_CONTENT_TYPE,
      object_id: enquiry.id,
      category: Number(draft.category),
      other_label: draft.other_label || '',
      charge_mode: draft.charge_mode,
      supplier: draft.supplier ? Number(draft.supplier) : null,
      rate_per_mt: draft.rate_per_mt === '' || draft.rate_per_mt == null
        ? null : draft.rate_per_mt,
      // With a rate, the server works the total out and ignores this; sending
      // nothing makes that plain in the request as well.
      cost_amount: (draft.rate_per_mt || draft.cost_amount === '')
        ? null : draft.cost_amount,
      on_charge_amount: draft.on_charge_amount === '' ? null : draft.on_charge_amount,
      currency: draft.currency || currency,
      notes: draft.notes || '',
      // Always sent, including as an empty list: on a save that follows an
      // untick, omitting the key would leave the old tags in place and the
      // trader would watch their change reappear on the next reload.
      products: (draft.products || []).map(Number),
    };
    try {
      if (draft.id) {
        await fuelEnquiryService.thirdPartyCosts.update(draft.id, payload);
      } else {
        await fuelEnquiryService.thirdPartyCosts.create(payload);
      }
      // Drop the saved draft slot so the next ``rows``-driven hydrate
      // becomes the source of truth. Without this, the server's
      // normalised values ('1250.00' vs the user-typed '1250', or
      // NULL after ``not_required`` coercion) would leave the row
      // flagged "unsaved changes" forever.
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('ThirdPartyCost save failed:', err);
      const data = err?.response?.data;
      const detail = data?.detail
        || data?.non_field_errors?.[0]
        || (typeof data === 'object'
            ? Object.values(data).flat().filter(Boolean).join(' ')
            : null)
        || err?.message
        || 'Failed to save row.';
      setRowErrors((prev) => ({ ...prev, [key]: String(detail) }));
    } finally {
      setBusyKey(null);
    }
  };

  const removeRow = async (key) => {
    const draft = drafts[key];
    if (!draft) return;
    if (!draft.id) {
      cancelDraft(key);
      return;
    }
    if (!window.confirm(
      'Remove this third-party cost row? This cannot be undone.',
    )) return;
    setBusyKey(key);
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    try {
      await fuelEnquiryService.thirdPartyCosts.remove(draft.id);
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('ThirdPartyCost delete failed:', err);
      setRowErrors((prev) => ({
        ...prev,
        [key]: err?.response?.data?.detail || err?.message || 'Delete failed.',
      }));
    } finally {
      setBusyKey(null);
    }
  };

  // Section totals roll up only persisted, active rows -- mirrors the
  // backend visibility matrix (NULL on ``not_required``). Drafts that
  // haven't been saved don't contribute to the displayed totals.
  // Commission rows contribute their LIVE preview value (not the stale
  // persisted amount) so the totals agree with the cards above.
  const totals = useMemo(() => {
    let cost = 0;
    let onCharge = 0;
    rows.forEach((r) => {
      if (r.is_system_generated) return;
      if (r.charge_mode === CHARGE_MODE_NOT_REQUIRED) return;
      // A captured-only category (Supply Desk) is saved but counts nowhere.
      // Read off the row the server sent, not the category list, so a list
      // that failed to load can't quietly start counting it.
      if (r.counts_in_totals === false) return;
      const c = Number(r.cost_amount);
      const oc = Number(r.on_charge_amount);
      if (Number.isFinite(c)) cost += c;
      if (Number.isFinite(oc)) onCharge += oc;
    });
    commissionPreviews.forEach((p) => {
      if (Number.isFinite(p.cost)) cost += p.cost;
    });
    return { cost, onCharge };
  }, [rows, commissionPreviews]);

  const headerAction = (
    <div className="fef-tpc-currency-control">
      <label htmlFor="fef-tpc-currency" className="fef-tpc-currency-label">
        Currency:
      </label>
      <select
        id="fef-tpc-currency"
        value={currency}
        onChange={(e) => setCurrency(e.target.value)}
        disabled={Boolean(lockedReason)}
      >
        {CURRENCY_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.value}</option>
        ))}
      </select>
    </div>
  );

  return (
    <Section
      title="3rd Party Costs"
      stepNumber={stepNumber}
      anchorId={anchorId}
      headerAction={headerAction}
    >
      <p className="fef-supplier-multi-hint">
        Capture supplier-side third-party costs for this enquiry (Barging,
        Survey, Agency, Commission Purchase, Supply Desk, Other). Commission
        Purchase and Supply Desk can be entered as a rate per MT against the
        product they are for; Supply Desk is captured only and not counted in
        any total yet. Each row carries its own charge mode --
        <strong> Yes on Charge</strong> bills the customer,
        <strong> Yes no Charge</strong> absorbs the cost on IMT' side, and
        <strong> Not Required</strong> records that the line was considered
        but skipped. All manual rows must share the same currency.
        Sell-side commissions appear automatically as locked
        <strong> Commission Sales</strong> rows, mirrored from the Counter
        Party box on the Product card (always USD).
      </p>

      {lockedReason && (
        <div
          className="fef-credit-banner fef-credit-banner--rejected"
          role="status"
          data-testid="tpc-locked-banner"
        >
          <FontAwesomeIcon icon={faLock} />
          <span>{lockedReason}</span>
        </div>
      )}

      {commissionPreviews.map((p) => (
        <SystemCommissionRow
          key={p.key}
          supplierName={p.supplierName}
          cost={p.cost}
          productLabel={
            productOptions.length > 0
              ? (productOptions[p.position - 1]?.label || `Product ${p.position}`)
              : ''
          }
        />
      ))}

      {orderedKeys.length === 0 && commissionPreviews.length === 0 && (
        <div className="fef-tonnage-empty">
          {lockedReason ? 'No third-party cost rows.' : (
            <>
              No third-party cost rows yet. Click <em>Add row</em> below to
              capture the first one.
            </>
          )}
        </div>
      )}

      {orderedKeys.map((key) => {
        const draft = drafts[key];
        if (!draft) return null;
        const persistedRow = draft.id ? persistedRowById.get(draft.id) : null;
        const dirty = draftDiffersFromRow(draft, persistedRow);
        // A closed deal reuses the saving freeze: every field on the card is
        // read-only, and the row buttons are not drawn at all (below).
        const busy = busyKey === key || Boolean(lockedReason);
        const mode = draft.charge_mode;
        const showSupplierAndCost = mode !== CHARGE_MODE_NOT_REQUIRED;
        const showOnCharge = mode === CHARGE_MODE_ON_CHARGE;
        // ``selectedSupplier`` is rendered as a CustomerPicker chip.
        // We use the locally-cached profile blob (set when the trader
        // picks one) so non-fuels suppliers (barging ops, surveyors,
        // agency reps) render their name even though they aren't in
        // the bootstrap fuels-suppliers list.
        const selectedSupplier = draft.supplier_display || (
          draft.supplier ? { id: Number(draft.supplier) } : null
        );
        const error = rowErrors[key];
        const otherCategory = isOther(draft.category);
        const categoryName = (
          (categories || []).find((c) => c.id === Number(draft.category))?.name
          || ''
        );
        const chargeModeLabel = (
          CHARGE_MODE_OPTIONS.find((o) => o.value === draft.charge_mode)?.label
          || draft.charge_mode
        );
        const rowHeader = categoryName
          ? `${categoryName} \u2014 ${chargeModeLabel}`
          : (persistedRow ? `Row ${persistedRow.id}` : 'New row');
        // Named on the card header so a trader scanning the section can see
        // which products a cost was tagged against without opening every row.
        // Nothing is printed when the row is untagged -- "the whole deal" is the
        // normal case and does not need a caption.
        const taggedLabels = (draft.products || [])
          .map((id) => productLabelById.get(id))
          .filter(Boolean);
        // Commissions as line items (Sept 2026): the category's tick-boxes
        // decide what this card offers.
        const category = tpcCategoryById(categories, draft.category);
        const onChargeBlocked = Boolean(category) && !categoryAllowsOnCharge(category);
        const capturedOnly = Boolean(category) && !categoryCountsInTotals(category);
        const rowCurrency = draft.currency || currency;
        const hasRate = draft.rate_per_mt !== '' && draft.rate_per_mt != null;
        // A stored rate stays on show even if the category's rate box was later
        // switched off in the admin: a rate nobody can see would still be
        // driving the total.
        const showRate = showSupplierAndCost
          && (categoryAllowsRatePerMt(category) || hasRate);
        const preview = showRate && hasRate
          ? perMtPreview({
            enquiry,
            productIds: draft.products,
            rate: draft.rate_per_mt,
            currency: rowCurrency,
          })
          : null;
        // The Total cost box while a rate is typed: the preview, or on an
        // untouched saved row whose preview can't be drawn, the stored figure.
        // Never the stored figure beside a refusal -- that would show a total
        // the card is simultaneously saying can't be worked out.
        const workedOutTotal = preview?.error
          ? null
          : (preview?.total
            ?? (!dirty && persistedRow?.cost_amount != null
              ? String(persistedRow.cost_amount) : null));
        return (
          <div className="fef-supplier-product-block" key={key}>
            <div className="fef-supplier-product-label">
              {rowHeader}
              {capturedOnly && <span> — captured only</span>}
              {taggedLabels.length > 0 && (
                <span> — {taggedLabels.join(', ')}</span>
              )}
              {dirty && (
                <span className="fef-tpc-dirty"> — unsaved changes</span>
              )}
            </div>

            <div className="fef-grid fef-grid-3">
              <Field label="Category" required>
                <select
                  value={draft.category || ''}
                  onChange={(e) => setDraftField(
                    key, 'category',
                    e.target.value ? Number(e.target.value) : null,
                  )}
                  disabled={busy}
                >
                  <option value="">Select...</option>
                  {(categories || []).map((c) => (
                    <option key={c.id} value={c.id} disabled={isSystemOnlyCategory(c)}>
                      {isSystemOnlyCategory(c) ? `${c.name} (auto)` : c.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Charge mode"
                required
                hint={onChargeBlocked
                  ? `${categoryName} is paid by IMT, so it can't be charged on to the customer.`
                  : undefined}
              >
                <select
                  value={draft.charge_mode}
                  onChange={(e) => setDraftField(key, 'charge_mode', e.target.value)}
                  disabled={busy}
                  aria-label="Charge mode"
                >
                  {CHARGE_MODE_OPTIONS.map((opt) => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.value === CHARGE_MODE_ON_CHARGE && onChargeBlocked}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Currency">
                <select
                  value={draft.currency || currency}
                  onChange={(e) => setDraftField(key, 'currency', e.target.value)}
                  disabled={busy}
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.value}</option>
                  ))}
                </select>
              </Field>
            </div>

            {otherCategory && mode !== CHARGE_MODE_NOT_REQUIRED && (
              <Field
                label="Other label"
                required
                hint='Short description (e.g. "Tug fees", "Cargo hold cleaning") so multiple Other rows can be told apart.'
              >
                <input
                  type="text"
                  value={draft.other_label}
                  onChange={(e) => setDraftField(key, 'other_label', e.target.value)}
                  disabled={busy}
                  maxLength={120}
                  placeholder="Tug fees, Cleaning, ..."
                />
              </Field>
            )}

            {showSupplierAndCost && (
              <div className="fef-grid fef-grid-3">
                <Field label="Supplier">
                  {/* 3rd-party suppliers are NOT the same population as
                      fuels-suppliers -- they include barge operators,
                      survey houses, agency reps that often sit on the
                      CRM creditor side without the fuels-supplier flag.
                      Pass an empty ``searchParams`` to bypass the
                      picker's default ``fuels_debtors_only`` filter and
                      search across all CRM profiles. */}
                  <CustomerPicker
                    customer={selectedSupplier}
                    disabled={busy}
                    onChange={(profile) => setDraftSupplier(key, profile)}
                    allowUnlinked={false}
                    searchParams={{}}
                    placeholder="Search CRM partners..."
                    noResultsCopy="No matching CRM partners."
                  />
                </Field>

                {/* Commissions as line items (Sept 2026). Optional: with the
                    rate empty the total is typed exactly as before; with a
                    rate, the total below is worked out and read-only. A
                    price's decimals (utils/precision.js), the scale the
                    server stores. */}
                {showRate && (
                  <Field
                    label={`Rate (${rowCurrency}/mt)`}
                    hint="Optional. Leave empty to type the total instead."
                  >
                    <AmountInput
                      value={draft.rate_per_mt}
                      onChange={(v) => setDraftField(key, 'rate_per_mt', v)}
                      disabled={busy}
                      maxDecimals={PRICE_DECIMALS}
                      placeholder="e.g. 2.50"
                      aria-label={`Rate (${rowCurrency}/mt)`}
                    />
                  </Field>
                )}

                <Field
                  label={`Total cost (${rowCurrency})`}
                  hint={hasRate && preview?.working ? `= ${preview.working}` : undefined}
                >
                  {hasRate ? (
                    // Same sizing as the typed amount box it replaces (the
                    // app-wide rule keys on inputmode), and the same
                    // two-decimal read-out as the automatic Commission Sales
                    // card above it.
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={`Total cost (${rowCurrency})`}
                      value={workedOutTotal != null
                        ? Number(workedOutTotal).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                        : '—'}
                      disabled
                      readOnly
                    />
                  ) : (
                    // Two decimals: the column holds cents, and a third decimal
                    // typed here used to come back as a refusal on save.
                    <AmountInput
                      value={draft.cost_amount}
                      onChange={(v) => setDraftField(key, 'cost_amount', v)}
                      disabled={busy}
                      maxDecimals={AMOUNT_DECIMALS}
                      placeholder="e.g. 1250.00"
                      aria-label={`Total cost (${rowCurrency})`}
                    />
                  )}
                </Field>

                {showOnCharge && (
                  <Field
                    label={`On-charge to customer (${rowCurrency})`}
                    hint="Amount billed back to the customer."
                  >
                    <AmountInput
                      value={draft.on_charge_amount}
                      onChange={(v) => setDraftField(key, 'on_charge_amount', v)}
                      disabled={busy}
                      maxDecimals={AMOUNT_DECIMALS}
                      placeholder="e.g. 1500.00"
                    />
                  </Field>
                )}
              </div>
            )}

            {/* Which products this cost belongs to.
              *
              * Hidden entirely when the deal has no product lines to offer --
              * a single-product enquiry, or a Solids deal. An empty dropdown
              * reads as broken; no dropdown reads as not applicable.
              *
              * Tick boxes rather than a multi-select list box: every option is
              * visible at once without an explanatory click-and-drag, which is
              * what a "pick several" list box demands of the trader.
              *
              * This RECORDS the answer and nothing more. It does not split the
              * cost, and no total on this screen or anywhere else changes when
              * a box is ticked -- there is no per-product margin in HMS for a
              * split to feed. Said out loud in the hint so the stillness is
              * never mistaken for a broken sum. */}
            {/* Deliberately NOT hidden on a "Not Required" row. The charge-mode
              * matrix governs the money fields; a product tag is not one of
              * them, and hiding a control whose value still gets saved is how a
              * trader ends up with a tag they cannot see or clear.
              *
              * Spacing is set inline rather than in the stylesheet: this is the
              * only place these two class names appear, and an unstyled row of
              * checkboxes runs the labels together into one unreadable line. */}
            {productOptions.length > 0 && (
              <Field
                label="Applies to product(s)"
                hint={showRate
                  // On a rate-per-MT card the tick is no longer silent: it is
                  // what the rate is multiplied by, so the hint must not
                  // promise that ticking changes nothing.
                  ? 'Tick the product(s) this cost is for. With a rate, the total is the rate times their MAX tonnage.'
                  : 'Records which products this cost relates to. Leave all unticked if it belongs to the whole deal. Ticking does not split the cost -- the totals below are unchanged either way.'}
              >
                <div
                  className="fef-tpc-product-tags"
                  role="group"
                  aria-label="Applies to product(s)"
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px' }}
                >
                  {productOptions.map((option) => {
                    const checked = (draft.products || []).includes(option.id);
                    return (
                      <label
                        key={option.id}
                        className="fef-tpc-product-tag"
                        htmlFor={`fef-tpc-${key}-product-${option.id}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <input
                          id={`fef-tpc-${key}-product-${option.id}`}
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() => toggleDraftProduct(key, option.id)}
                        />
                        <span>{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </Field>
            )}

            {/* Notes sit under the tick-boxes, not between the rate and them:
                the rate and the products it is multiplied by read as one
                block, and "tick the product" belongs beside the ticks. */}
            {preview?.error && (
              <div className="fef-credit-inline-hint" role="note">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{preview.error}</span>
              </div>
            )}

            {onChargeBlocked && mode === CHARGE_MODE_ON_CHARGE && !lockedReason && (
              <div className="fef-credit-inline-hint" role="note">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>
                  {categoryName} can no longer be charged on to the customer.
                  Switch Charge mode to No Charge and save this row.
                </span>
              </div>
            )}

            {capturedOnly && (
              <div className="fef-credit-inline-hint" role="note">
                <FontAwesomeIcon icon={faCircleInfo} />
                <span>
                  Captured only. {categoryName} costs are saved here but not
                  counted in any total, margin, letter or bill yet.
                </span>
              </div>
            )}

            {mode === CHARGE_MODE_NOT_REQUIRED && (
              <div className="fef-credit-inline-hint" role="note">
                <FontAwesomeIcon icon={faCircleInfo} />
                <span>
                  Not Required rows record that this category was
                  considered but skipped. Supplier, cost and on-charge
                  fields are hidden and stored as NULL.
                </span>
              </div>
            )}

            {error && (
              <div className="fef-credit-banner fef-credit-banner--rejected" role="alert">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
              </div>
            )}

            {!lockedReason && (
            <div className="fef-tonnage-actions fef-tpc-row-actions">
              <button
                type="button"
                className="fef-btn fef-btn-ghost fef-btn-xs"
                onClick={() => saveDraft(key)}
                disabled={busy || !dirty}
                title={!dirty ? 'No changes to save' : ''}
              >
                {busy
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving</>
                  : <><FontAwesomeIcon icon={faCheck} /> Save row</>
                }
              </button>
              {dirty && (
                <button
                  type="button"
                  className="fef-btn fef-btn-ghost fef-btn-xs"
                  onClick={() => cancelDraft(key)}
                  disabled={busy}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="fef-btn fef-btn-ghost-danger fef-btn-xs"
                onClick={() => removeRow(key)}
                disabled={busy}
              >
                <FontAwesomeIcon icon={faTrash} /> {draft.id ? 'Delete' : 'Discard'}
              </button>
            </div>
            )}
          </div>
        );
      })}

      {(rows.length > 0 || commissionPreviews.length > 0) && (
        <div className="fef-tpc-totals">
          <div className="fef-display-row">
            <div className="fef-display-row-label">Total 3rd Party Cost</div>
            <div className="fef-display-row-value">
              {totals.cost.toFixed(2)} {currency}
            </div>
          </div>
          <div className="fef-display-row">
            <div className="fef-display-row-label">
              Total 3rd Party On-Charge Cost
            </div>
            <div className="fef-display-row-value fef-credit-request">
              {totals.onCharge.toFixed(2)} {currency}
            </div>
          </div>
        </div>
      )}

      {!lockedReason && (
        <div className="fef-add-product-row">
          <button
            type="button"
            className="fef-btn fef-btn-secondary"
            onClick={addRow}
          >
            <FontAwesomeIcon icon={faPlus} /> Add row
          </button>
          <span className="fef-add-product-hint">
            Add a new third-party cost line (Barging, Survey, Agency or
            Other). Multiple Other rows are allowed -- use the
            <em> Other label</em> field to distinguish them.
          </span>
        </div>
      )}
    </Section>
  );
};

const SectionTotals = ({ creditRequest }) => (
  <section className="fef-totals">
    <div className="fef-totals-row">
      <div className="fef-totals-label">Total Revenue</div>
      <div className="fef-totals-value">
        {creditRequest != null ? formatCurrency(creditRequest) : '—'}
      </div>
    </div>
  </section>
);

const StageHistoryPanel = ({ history }) => {
  const ordered = useMemo(
    () => [...history].sort(
      (a, b) => new Date(a.changed_at).getTime() - new Date(b.changed_at).getTime(),
    ),
    [history],
  );
  return (
    <section className="fef-history-panel">
      <header className="fef-section-header">
        <h2 className="fef-section-title">
          <FontAwesomeIcon icon={faClock} /> Stage History
        </h2>
      </header>
      <div className="fef-history-table-container">
        <table className="fef-history-table">
          <thead>
            <tr>
              <th>From</th>
              <th>To</th>
              <th>Changed By</th>
              <th>Changed At</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((row) => (
              <tr key={row.id}>
                <td>{row.from_stage ? (STAGE_LABEL[row.from_stage] || row.from_stage) : '—'}</td>
                <td>{STAGE_LABEL[row.to_stage] || row.to_stage}</td>
                <td>
                  <FontAwesomeIcon icon={faUserShield} className="fef-history-icon" />
                  {row.changed_by_name || `#${row.changed_by}`}
                </td>
                <td>{formatDateTime(row.changed_at)}</td>
                <td className="fef-history-notes">{row.notes || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

// =====================================================================
// State / shape helpers
// =====================================================================

const detailToFormState = (detail) => ({
  display_name: detail.display_name || '',
  enquiry_date: detail.enquiry_date || todayISO(),
  monday_item_id: detail.monday_item_id || '',
  customer: detail.customer ?? null,
  unlinked_customer_name: detail.unlinked_customer_name || '',
  unlinked_customer_email: detail.unlinked_customer_email || '',
  unlinked_customer_contact_name: detail.unlinked_customer_contact_name || '',
  imt_branch: detail.imt_branch ?? null,
  customer_trader: detail.customer_trader ?? null,
  supplier_trader: detail.supplier_trader ?? null,
  hms_sell_company: detail.hms_sell_company ?? null,
  hms_buy_company: detail.hms_buy_company ?? null,
  vessel_name: detail.vessel_name || '',
  vessel_type: detail.vessel_type ?? null,
  imo_number: detail.imo_number || '',
  // ``port`` is the PK on both ends — the detail serializer returns it
  // as a FK PK and the create / update serializer accepts a PK. The
  // ports endpoint also exposes ``id``, so the picker can render PK-based
  // <option> values directly.
  port: detail.port ?? null,
  country: detail.country || '',
  expected_delivery_start: detail.expected_delivery_start || '',
  expected_delivery_end: detail.expected_delivery_end || '',
  shipping_agent: detail.shipping_agent || '',
  shipping_agent_profile: detail.shipping_agent_profile ?? null,
  shipping_agent_profile_name: detail.shipping_agent_profile_name || '',
  fuel_type: detail.fuel_type ?? null,
  fuel_grade: detail.fuel_grade ?? null,
  sulfur_limit: detail.sulfur_limit ?? null,
  sulfur_limit_other: detail.sulfur_limit_other || '',
  iso_8217_year: detail.iso_8217_year ?? null,
  measuring_unit: detail.measuring_unit ?? null,
  // Anything saved before the toggle existed reads as bunkers, which is what
  // it always was.
  product_kind: detail.product_kind || 'bunkers',
  pricing_type: detail.pricing_type ?? '',
  min_tonnage: detail.min_tonnage ?? '',
  max_tonnage: detail.max_tonnage ?? '',
  sell_price: detail.sell_price ?? '',
  counter_party: detail.counter_party ?? null,
  counter_party_display_name: detail.counter_party_display_name || '',
  commission_amount: detail.commission_amount ?? '',
  supplier: detail.supplier ?? null,
  supplier_display_name: detail.supplier_display_name || '',
  buy_price: detail.buy_price ?? '',
  supplier_credit_days: detail.supplier_credit_days ?? '',
  supplier_pricing_type: detail.supplier_pricing_type ?? '',
  comments: detail.comments || '',
  show_comments_on_supplier_nomination:
    !!detail.show_comments_on_supplier_nomination,
  show_comments_on_customer_confirmation:
    !!detail.show_comments_on_customer_confirmation,
  products_comments: detail.products_comments || '',
  show_products_comments_on_supplier_nomination:
    !!detail.show_products_comments_on_supplier_nomination,
  show_products_comments_on_customer_confirmation:
    !!detail.show_products_comments_on_customer_confirmation,
  shipment_comments: detail.shipment_comments || '',
  show_shipment_comments_on_supplier_nomination:
    !!detail.show_shipment_comments_on_supplier_nomination,
  show_shipment_comments_on_customer_confirmation:
    !!detail.show_shipment_comments_on_customer_confirmation,
  // Product 1's catalogue link. The parent column was added in 2026-09 so a
  // single-product lubes enquiry can record its picked product; older rows
  // only ever wrote it to the first child, so fall back to that.
  stock_item_variant: detail.stock_item_variant
    ?? _firstProductField(detail, 'stock_item_variant'),
  stock_item_variant_display: _firstProductDisplayName(detail),
  // Sprint 6: hydrate extra_products from the detail payload's
  // ``products`` array. The first child is shown via the inline scalar
  // fields above (back-compat); rows beyond the first become editable
  // extra cards. When the parent has inline scalars but no children,
  // ``extra_products`` stays empty (legacy single-product shape).
  extra_products: Array.isArray(detail.products) && detail.products.length > 1
    ? detail.products.slice(1).map((p) => ({
        id: p.id ?? null,
        product_kind: p.product_kind || 'bunkers',
        fuel_type: p.fuel_type ?? null,
        fuel_grade: p.fuel_grade ?? null,
        sulfur_limit: p.sulfur_limit ?? null,
        sulfur_limit_other: p.sulfur_limit_other || '',
        iso_8217_year: p.iso_8217_year ?? null,
        measuring_unit: p.measuring_unit ?? null,
        pricing_type: p.pricing_type ?? '',
        min_tonnage: p.min_tonnage ?? '',
        max_tonnage: p.max_tonnage ?? '',
        sell_price: p.sell_price ?? '',
        counter_party: p.counter_party ?? null,
        counter_party_display_name: p.counter_party_display_name || '',
        commission_amount: p.commission_amount ?? '',
        supplier: p.supplier ?? null,
        supplier_display_name: p.supplier_display_name || '',
        buy_price: p.buy_price ?? '',
        supplier_credit_days: p.supplier_credit_days ?? '',
        supplier_pricing_type: p.supplier_pricing_type ?? '',
        // Sprint 7A: Stock catalogue link per extra child.
        stock_item_variant: p.stock_item_variant ?? null,
        stock_item_variant_display:
          p.stock_item_variant_data?.display_name?.split(' (Sale:')[0]?.split(' (Purchase:')[0]
          || p.stock_item_variant_data?.shortname
          || '',
      }))
    : [],
});

// Sprint 7A helpers: read the FIRST product child's variant FK / display
// name when present. Returns null / '' when the enquiry is in legacy
// single-product shape (no children).
const _firstProductField = (detail, fieldName) => {
  if (!Array.isArray(detail.products) || detail.products.length === 0) return null;
  const value = detail.products[0]?.[fieldName];
  return value ?? null;
};

const _firstProductDisplayName = (detail) => {
  if (!Array.isArray(detail.products) || detail.products.length === 0) return '';
  const data = detail.products[0]?.stock_item_variant_data;
  if (!data) return '';
  // Strip the trailing pricing block from the variant's display_name
  // (matches the picker's own display-name handling at line ~181 of
  // RecapProductSearch.js so a saved selection round-trips visually
  // identical to a fresh pick).
  if (data.display_name) {
    return data.display_name.split(' (Sale:')[0].split(' (Purchase:')[0];
  }
  return data.shortname || data.full_description || '';
};

const stageActionFlash = (modalState, updated) => {
  if (modalState.action === 'archive') return 'Enquiry archived.';
  if (modalState.action === 'unarchive') return 'Enquiry restored to the live list.';
  if (modalState.action === 'rollback') {
    return `Rolled back to ${STAGE_LABEL[updated.stage] || updated.stage}.`;
  }
  if (modalState.action === 'lost') return 'Marked as Lost.';
  return `Advanced to ${STAGE_LABEL[updated.stage] || updated.stage}.`;
};

// =====================================================================
// Loading / error / 404 states
// =====================================================================

const BootstrappingState = () => (
  <div className="fef-state-card">
    <FontAwesomeIcon icon={faSpinner} spin className="fef-state-icon" />
    <div className="fef-state-text">Loading enquiry...</div>
  </div>
);

const NotFoundState = ({ onBack }) => (
  <div className="fef-state-card">
    <FontAwesomeIcon icon={faExclamationTriangle} className="fef-state-icon" />
    <div className="fef-state-title">Enquiry not found</div>
    <div className="fef-state-text">
      This enquiry number doesn&rsquo;t exist, or it belongs to a desk you
      don&rsquo;t have access to. Archived enquiries still open normally, so
      being archived is not the reason.
    </div>
    <button type="button" className="fef-btn fef-btn-secondary" onClick={onBack}>
      <FontAwesomeIcon icon={faChevronLeft} /> Back to list
    </button>
  </div>
);

const AccessDeniedState = ({ message, onBack }) => (
  <div className="fef-state-card">
    <FontAwesomeIcon icon={faExclamationTriangle} className="fef-state-icon" />
    <div className="fef-state-title">Access denied</div>
    <div className="fef-state-text">{message}</div>
    <button type="button" className="fef-btn fef-btn-secondary" onClick={onBack}>
      <FontAwesomeIcon icon={faChevronLeft} /> Back
    </button>
  </div>
);

const BootErrorState = ({ message }) => (
  <div className="fef-state-card">
    <FontAwesomeIcon icon={faExclamationTriangle} className="fef-state-icon" />
    <div className="fef-state-title">Couldn’t load this enquiry</div>
    <div className="fef-state-text">{message}</div>
  </div>
);

export default FuelEnquiryForm;
